import { useState, useEffect, useRef, useCallback } from "react";
import AuthGate from "./AuthGate";
import BookDetailEdit from "./BookDetailEdit";
import BookDetailView from "./BookDetailView";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import {
  ALL_VIBES,
  ALLOWED_GENRES,
  FICTION_GENRES,
  FMTS,
  FUN_STUFF,
  GOOGLE_GENRE_RULES,
  MONTHS_FULL,
  MONTHS_SHORT,
  NONFIC_GENRES,
  VIBES,
} from "./constants/books";
import { THEMES } from "./constants/themes";
import { DISPLAY_SERIF, SANS } from "./constants/ui";
import { BUILD_ID } from "./buildVersion";
import {
  DEFAULT_THEME_KEY,
  readStoredTheme,
  writeStoredTheme,
} from "./themePreference";
import {
  buildFinishReadingState,
  deriveDetailStatusChange,
  hasCompletedReadHistory,
  resolveDetailStatusFields,
} from "./bookStatus";

// *** PASTE YOUR GOOGLE BOOKS API KEY HERE ***
const BOOKS_API_KEY = "AIzaSyDw_BiECjibw_nMt3XX7Npw9mnLnQoial4";
const ISBNDB_PROXY_URL = process.env.REACT_APP_ISBNDB_PROXY_URL || "";
// ********************************************

const LOOKUP_CACHE_TTL_MS = {
  isbn: 1000 * 60 * 60 * 24 * 45,
  search: 1000 * 60 * 60 * 24 * 7,
  empty: 1000 * 60 * 60 * 6,
};
const ISBN_CACHE_MODE = "isbn_v3_isbndb_cover_refresh";
const SEARCH_CACHE_MODE = "search_v2_ranked_with_isbn";
const CATALOG_OVERRIDE_CACHE_TTL_MS = 1000 * 30;
const CATALOG_OVERRIDE_FIELDS = [
  "title",
  "authors",
  "desc",
  "cover",
  "seriesName",
  "seriesNum",
  "edition",
  "genres",
  "vibes",
  "publisher",
  "publishedDate",
  "year",
  "pages",
];
const METADATA_REPORT_TYPES = [
  ["wrong_cover", "Wrong cover"],
  ["wrong_title", "Wrong title"],
  ["wrong_author", "Wrong author"],
  ["other", "Other"],
];
const catalogOverrideMemory = new Map();

const mkid = () => Math.random().toString(36).slice(2);
const TODAY = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function SettingsSubpage({
  title,
  subtitle,
  children,
  T,
  SANS,
  DISPLAY_SERIF,
  settingsHintColor,
  bottomNavPad,
  onBack,
  BottomNavComponent,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
      }}
    >
      <header
        style={{
          background: T.surface,
          padding: "18px 20px 14px",
          borderBottom: "1px solid " + T.border,
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.sub,
            fontSize: 18,
            padding: 0,
            flexShrink: 0,
            fontFamily: SANS,
          }}
        >
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: DISPLAY_SERIF,
              fontSize: 22,
              color: T.text,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 13, color: settingsHintColor, marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
      </header>
      <div style={{ flex: 1, overflowY: "auto", padding: `18px 16px ${bottomNavPad}` }}>
        {children}
      </div>
      <BottomNavComponent active="settings" />
    </div>
  );
}

function parseStoredDate(value) {
  if (!value || typeof value !== "string") {
    return { year: null, month: null, day: null, precision: null };
  }
  if (/^\d{4}$/.test(value)) {
    return { year: value.slice(0, 4), month: null, day: null, precision: "year" };
  }
  if (/^\d{4}-\d{2}$/.test(value)) {
    return {
      year: value.slice(0, 4),
      month: value.slice(5, 7),
      day: null,
      precision: "month",
    };
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return {
      year: value.slice(0, 4),
      month: value.slice(5, 7),
      day: value.slice(8, 10),
      precision: "day",
    };
  }
  return { year: null, month: null, day: null, precision: null };
}

function dateYear(value) {
  return parseStoredDate(value).year;
}

function dateMonthIndex(value) {
  const month = parseStoredDate(value).month;
  if (!month) return null;
  const idx = Number(month) - 1;
  return Number.isFinite(idx) && idx >= 0 && idx <= 11 ? idx : null;
}

function dateSortKey(value) {
  const p = parseStoredDate(value);
  if (!p.year) return "";
  const month = p.month || "00";
  const day = p.day || "00";
  return `${p.year}-${month}-${day}`;
}

function inYear(value, year) {
  return dateYear(value) === String(year);
}

function formatBookDate(value) {
  const p = parseStoredDate(value);
  if (!p.year) return "--";
  if (!p.month) return p.year;
  const month = MONTHS_SHORT[Number(p.month) - 1];
  if (p.day) return `${month} ${Number(p.day)}, ${p.year}`;
  return `${month} ${p.year}`;
}

function monthNameFromToken(token) {
  const short = String(token || "").trim().slice(0, 3).toLowerCase();
  if (!short) return "";
  const idx = MONTHS_SHORT.findIndex((m) => m.toLowerCase() === short);
  return idx >= 0 ? MONTHS_FULL[idx] : "";
}

function formatPublishedReleaseDate(publishedDate, yearFallback) {
  const fallbackYear = /^\d{4}$/.test(String(yearFallback || ""))
    ? String(yearFallback)
    : "";
  const raw = String(publishedDate || "").trim();
  if (!raw) return fallbackYear;

  const iso = raw.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/);
  if (iso) {
    const year = iso[1];
    const month = iso[2] ? Number(iso[2]) : null;
    const day = iso[3] ? Number(iso[3]) : null;
    if (month && month >= 1 && month <= 12 && day && day >= 1 && day <= 31) {
      return `${MONTHS_FULL[month - 1]} ${day}, ${year}`;
    }
    if (month && month >= 1 && month <= 12) {
      return `${MONTHS_FULL[month - 1]} ${year}`;
    }
    return year;
  }

  const monthDayYear = raw.match(
    /\b([A-Za-z]{3,9})\s+(\d{1,2}),?\s+((?:18|19|20)\d{2})\b/,
  );
  if (monthDayYear) {
    const monthName = monthNameFromToken(monthDayYear[1]);
    if (monthName) {
      return `${monthName} ${Number(monthDayYear[2])}, ${monthDayYear[3]}`;
    }
  }

  const monthYear = raw.match(/\b([A-Za-z]{3,9})\s+((?:18|19|20)\d{2})\b/);
  if (monthYear) {
    const monthName = monthNameFromToken(monthYear[1]);
    if (monthName) return `${monthName} ${monthYear[2]}`;
  }

  const yearOnly = raw.match(/\b((?:18|19|20)\d{2})\b/);
  if (yearOnly) return yearOnly[1];

  return fallbackYear;
}

function readingDays(start, end) {
  const s = parseStoredDate(start);
  const e = parseStoredDate(end);
  if (s.precision !== "day" || e.precision !== "day") return null;
  const diff = Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24));
  return Math.max(1, diff);
}

function normalizeBuildId(value) {
  return String(value || "").trim();
}

function parseIsoDayToUtc(dateStr) {
  const m = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return Date.UTC(year, month - 1, day);
}

function bestStreakFromDates(dates = []) {
  const dayMs = 1000 * 60 * 60 * 24;
  const keys = [...new Set((dates || []).map(parseIsoDayToUtc).filter(Number.isFinite))].sort(
    (a, b) => a - b,
  );
  if (!keys.length) return 0;
  let best = 1;
  let run = 1;
  for (let i = 1; i < keys.length; i += 1) {
    const diffDays = Math.round((keys[i] - keys[i - 1]) / dayMs);
    if (diffDays === 1) {
      run += 1;
    } else {
      run = 1;
    }
    if (run > best) best = run;
  }
  return best;
}

function currentStreakFromDates(dates = [], todayIso = TODAY()) {
  const dayMs = 1000 * 60 * 60 * 24;
  const todayKey = parseIsoDayToUtc(todayIso);
  if (!Number.isFinite(todayKey)) return 0;
  const keySet = new Set(
    [...new Set((dates || []).map(parseIsoDayToUtc).filter(Number.isFinite))],
  );
  if (!keySet.size) return 0;

  let count = 0;
  let cursor = todayKey;

  if (keySet.has(cursor)) {
    count = 1;
    cursor -= dayMs;
  } else if (keySet.has(cursor - dayMs)) {
    count = 1;
    cursor -= dayMs * 2;
  } else {
    return 0;
  }

  while (keySet.has(cursor)) {
    count += 1;
    cursor -= dayMs;
  }

  return count;
}

function roundPagesEstimate(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 0;
  if (num < 100) return Math.ceil(num / 10) * 10;
  if (num < 1000) return Math.ceil(num / 50) * 50;
  if (num < 10000) return Math.ceil(num / 250) * 250;
  return Math.ceil(num / 1000) * 1000;
}

function formatPagesEstimate(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "--";
  if (num >= 1000) {
    const compact = num / 1000;
    const roundedCompact = Number.isInteger(compact)
      ? String(compact)
      : compact.toFixed(1).replace(/\.0$/, "");
    return `${roundedCompact}K`;
  }
  return Math.round(num).toLocaleString();
}

const CUR_YR = new Date().getFullYear();
const MIN_PICKER_YEAR = 2010;
const YEARS = Array.from(
  { length: Math.max(1, CUR_YR - MIN_PICKER_YEAR + 1) },
  (_, i) => CUR_YR - i,
);

function normalizeText(input) {
  return input
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^\w\s/+-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function scoreCategoryToLabels(category) {
  const scores = new Map();
  const normalized = normalizeText(category);
  const parts = normalized
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
  for (const rule of GOOGLE_GENRE_RULES) {
    let score = 0;
    for (const pattern of rule.patterns) {
      const p = normalizeText(pattern);
      if (normalized === p) score += 10;
      else if (parts.includes(p)) score += 7;
      else if (normalized.includes(p)) score += 4;
    }
    if (score > 0) {
      scores.set(rule.label, (scores.get(rule.label) || 0) + score);
    }
  }
  return scores;
}

function shouldAddClassic(publishYear, labels = []) {
  const year = parseInt(String(publishYear ?? ""), 10);
  if (!isFinite(year)) return false;
  const fictionish = new Set([
    "Sci-Fi",
    "Fantasy",
    "Mystery",
    "Horror",
    "Romance",
    "Historical Fiction",
    "Literary Fiction",
    "Young Adult",
  ]);
  return year < 1950 && labels.some((label) => fictionish.has(label));
}

const FICTION_GENRE_SET = new Set(FICTION_GENRES);
const NONFIC_GENRE_SET = new Set(NONFIC_GENRES);
const FICTION_SIGNAL_RE =
  /\b(fiction|novel|romance|fantasy|mystery|thriller|suspense|horror|science fiction|sci fi|young adult|graphic novel|manga|adventure|satire)\b/i;
const NONFIC_SIGNAL_RE =
  /\b(nonfiction|non fiction|biography|memoir|self help|personal development|history|science|psychology|business|technology|political|sociology|philosophy|spiritual|parenting|true crime)\b/i;

const VIBE_RULES = [
  { patterns: ["found family"], label: "Found Family" },
  { patterns: ["enemies to lovers"], label: "Enemies to Lovers" },
  { patterns: ["slow burn"], label: "Slow Burn" },
  { patterns: ["fast burn"], label: "Fast Burn" },
  { patterns: ["second chance"], label: "Second Chance" },
  { patterns: ["forced proximity"], label: "Forced Proximity" },
  { patterns: ["small town"], label: "Small Town" },
  { patterns: ["chosen one"], label: "Chosen One" },
  { patterns: ["political intrigue"], label: "Political Intrigue" },
  { patterns: ["dark romance"], label: "Dark Romance" },
  { patterns: ["sports romance"], label: "Sports Romance" },
  { patterns: ["mm romance"], label: "MM Romance" },
  { patterns: ["lgbt", "queer", "lgbtq"], label: "LGBT+" },
  { patterns: ["sapphic"], label: "Sapphic" },
  { patterns: ["morally grey", "morally gray"], label: "Morally Grey" },
  { patterns: ["vampire"], label: "Vampire" },
  { patterns: ["werewolf"], label: "Werewolf" },
  { patterns: ["witch"], label: "Witches" },
  { patterns: ["fae"], label: "Fae" },
  { patterns: ["dragon"], label: "Dragon" },
  { patterns: ["rom com", "rom-com", "romantic comedy"], label: "Rom-Com" },
  { patterns: ["humor", "comedy", "comic"], label: "Funny" },
  { patterns: ["satire"], label: "Satire" },
  { patterns: ["supernatural", "occult"], label: "Supernatural" },
  { patterns: ["paranormal"], label: "Paranormal" },
  { patterns: ["dystopian"], label: "Dystopian" },
  { patterns: ["apocalyptic", "post apocalyptic"], label: "Apocalyptic" },
  { patterns: ["psychological"], label: "Psychological" },
  { patterns: ["suspense"], label: "Suspense" },
  { patterns: ["mythology", "myth"], label: "Mythology" },
  { patterns: ["space opera"], label: "Space Opera" },
  { patterns: ["translated"], label: "Translated" },
  { patterns: ["anthology", "short stories"], label: "Anthology" },
  { patterns: ["novella", "short novel"], label: "Novella" },
  { patterns: ["cozy"], label: "Cozy" },
  { patterns: ["dark academia"], label: "Dark Academia" },
  { patterns: ["high fantasy"], label: "High Fantasy" },
  { patterns: ["epic fantasy"], label: "Epic Fantasy" },
  { patterns: ["political fiction", "political intrigue"], label: "Political Intrigue" },
  { patterns: ["horror", "terror"], label: "Scary" },
  { patterns: ["romantasy"], label: "Romantasy" },
];

function buildGenreTotals(categories) {
  const totals = new Map();
  for (const category of categories) {
    if (!category || typeof category !== "string") continue;
    const scores = scoreCategoryToLabels(category);
    for (const [label, score] of scores.entries()) {
      totals.set(label, (totals.get(label) || 0) + score);
    }
  }
  return totals;
}

function detectGenreLane(categories, totals) {
  const list = Array.isArray(categories) ? categories : [];
  let fictionSignals = 0;
  let nonficSignals = 0;

  for (const rawCategory of list) {
    const category = normalizeText(rawCategory || "");
    if (!category) continue;
    if (FICTION_SIGNAL_RE.test(category)) fictionSignals++;
    if (NONFIC_SIGNAL_RE.test(category)) nonficSignals++;
  }

  let fictionScore = 0;
  let nonficScore = 0;
  for (const [label, score] of totals.entries()) {
    if (FICTION_GENRE_SET.has(label)) fictionScore += score;
    if (NONFIC_GENRE_SET.has(label)) nonficScore += score;
  }
  const hasStrongFictionLabel = [...totals.keys()].some((label) =>
    FICTION_GENRE_SET.has(label),
  );

  if (fictionSignals > nonficSignals + 1) return "fiction";
  if (nonficSignals > fictionSignals + 1) return "nonfiction";
  if (fictionSignals > 0 && nonficSignals === 0) return "fiction";
  if (nonficSignals > 0 && fictionSignals === 0) return "nonfiction";
  if (hasStrongFictionLabel && fictionScore >= nonficScore) return "fiction";

  if (fictionScore > nonficScore * 1.35) return "fiction";
  if (nonficScore > fictionScore * 1.35) return "nonfiction";
  return "mixed";
}

function normalizeGenreLane(value) {
  return value === "nonfiction" ? "nonfiction" : "fiction";
}

function inferLaneFromSelectedGenres(genres = [], fallback = "fiction") {
  let fiction = 0;
  let nonfic = 0;
  for (const genre of Array.isArray(genres) ? genres : []) {
    if (FICTION_GENRE_SET.has(genre)) fiction++;
    if (NONFIC_GENRE_SET.has(genre)) nonfic++;
  }
  if (fiction > nonfic) return "fiction";
  if (nonfic > fiction) return "nonfiction";
  return normalizeGenreLane(fallback);
}

function restrictGenresToLane(genres = [], lane = "fiction") {
  const normalizedLane = normalizeGenreLane(lane);
  const list = Array.isArray(genres) ? genres : [];
  return list.filter((genre) =>
    normalizedLane === "fiction"
      ? FICTION_GENRE_SET.has(genre)
      : NONFIC_GENRE_SET.has(genre),
  );
}

function scoreCategoryToVibes(category) {
  const scores = new Map();
  const normalized = normalizeText(category);
  const parts = normalized
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const rule of VIBE_RULES) {
    let score = 0;
    for (const pattern of rule.patterns) {
      const p = normalizeText(pattern);
      if (normalized === p) score += 10;
      else if (parts.includes(p)) score += 7;
      else if (normalized.includes(p)) score += 4;
    }
    if (score > 0) {
      scores.set(rule.label, (scores.get(rule.label) || 0) + score);
    }
  }

  // Direct phrase matching for long-form trope strings like:
  // "Tropes: found family / enemies to lovers / slow burn"
  for (const vibe of ALL_VIBES) {
    const token = normalizeText(vibe);
    if (!token || token.length < 6) continue;
    if (normalized === token) {
      scores.set(vibe, Math.max(10, scores.get(vibe) || 0));
      continue;
    }
    if (parts.includes(token)) {
      scores.set(vibe, Math.max(8, scores.get(vibe) || 0));
      continue;
    }
    if (normalized.includes(token)) {
      scores.set(vibe, Math.max(6, scores.get(vibe) || 0));
    }
  }
  return scores;
}

function mapGenres(categories, publishYear, maxGenres = 4, options = {}) {
  if (!Array.isArray(categories) || categories.length === 0) return [];
  const totals = buildGenreTotals(categories);
  const lane = options.lane || detectGenreLane(categories, totals);
  let labels = [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label]) => label)
    .filter((label) => {
      if (!ALLOWED_GENRES.has(label)) return false;
      if (lane === "fiction") return FICTION_GENRE_SET.has(label);
      if (lane === "nonfiction") return NONFIC_GENRE_SET.has(label);
      return true;
    })
    .slice(0, maxGenres);
  if (shouldAddClassic(publishYear, labels) && !labels.includes("Classic")) {
    labels.push("Classic");
  }
  return [...new Set(labels)].slice(0, maxGenres);
}

function mapVibes(categories, maxVibes = 3, options = {}) {
  if (!Array.isArray(categories) || categories.length === 0) return [];
  const totals = new Map();
  const lane =
    options.lane || detectGenreLane(categories, buildGenreTotals(categories));

  for (const category of categories) {
    if (!category || typeof category !== "string") continue;
    const scores = scoreCategoryToVibes(category);
    for (const [label, score] of scores.entries()) {
      totals.set(label, (totals.get(label) || 0) + score);
    }
  }

  // If lane is fiction and metadata says humor/comedy, prefer vibe instead of non-fiction genre.
  if (lane === "fiction") {
    const hasHumorSignal = categories.some((c) =>
      /\bhumou?r|comedy|comic\b/i.test(String(c || "")),
    );
    if (hasHumorSignal) {
      totals.set("Funny", Math.max(6, totals.get("Funny") || 0));
    }
  }

  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label]) => label)
    .filter((label) => ALL_VIBES.includes(label))
    .slice(0, maxVibes);
}

function getEncouragement(pct) {
  if (pct === 0) return { emoji: "📖", msg: "Your story starts now" };
  if (pct < 25) return { emoji: "🌱", msg: "Just getting started!" };
  if (pct < 50) return { emoji: "🔥", msg: "Great momentum!" };
  if (pct < 75) return { emoji: "⭐", msg: "More than halfway!" };
  if (pct < 100) return { emoji: "🎯", msg: "So close, keep going!" };
  return { emoji: "🏆", msg: "Goal crushed!" };
}

const EDITION_LABEL_RE =
  /\b((?:\d{1,2}(?:st|nd|rd|th)\s+edition)|(?:anniversary|collectors?|collector'?s|deluxe|special|illustrated|limited|revised|updated|expanded|annotated|international|signed|exclusive|definitive|author'?s preferred)\s+edition)\b/i;
const EDITION_SHORT_RE = /\b(\d{1,2}(?:st|nd|rd|th)?\s+ed(?:ition)?\.?)\b/i;

function extractEditionFromText(...parts) {
  for (const part of parts) {
    if (!part || typeof part !== "string") continue;
    const text = part.replace(/\s+/g, " ").trim();
    if (!text) continue;
    const match = text.match(EDITION_LABEL_RE) || text.match(EDITION_SHORT_RE);
    if (match?.[1]) return match[1].replace(/\.$/, "").trim();
  }
  return "";
}

function normalizeTitleArticles(rawTitle) {
  const title = String(rawTitle || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!title) return "";
  const trailingArticle = title.match(/^(.*?),\s*(the|a|an)$/i);
  if (!trailingArticle) return title;
  const article =
    trailingArticle[2].charAt(0).toUpperCase() +
    trailingArticle[2].slice(1).toLowerCase();
  return `${article} ${trailingArticle[1].trim()}`.replace(/\s+/g, " ").trim();
}

function restoreLeadingArticleFromQuery(title, query) {
  const normalizedTitle = normalizeTitleArticles(title);
  if (/^(the|a|an)\s+/i.test(normalizedTitle)) return normalizedTitle;
  const rawQuery = String(query || "").trim();
  if (!rawQuery) return normalizedTitle;
  const match = rawQuery.match(/^(the|a|an)\s+(.+)$/i);
  if (!match) return normalizedTitle;
  const article =
    match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
  const remainder = normalizeSearchText(match[2]);
  const titleNorm = normalizeSearchText(normalizedTitle);
  if (!remainder || !titleNorm) return normalizedTitle;
  if (
    titleNorm === remainder ||
    titleNorm.startsWith(remainder + " ") ||
    remainder.startsWith(titleNorm + " ")
  ) {
    return `${article} ${normalizedTitle}`;
  }
  return normalizedTitle;
}

const TITLE_MARKETING_PATTERNS = [
  /\s*[•·\-–—:|]\s*(?:a|an|the)?\s*#?\d+\s+new york times bestseller.*$/i,
  /\s*[•·\-–—:|]?\s*(?:instant\s+)?new york times bestseller.*$/i,
  /\s*[•·\-–—:|]?\s*usa today bestseller.*$/i,
  /\s*[•·\-–—:|]?\s*(?:now|soon)\s+(?:a|an)\s+(?:major\s+)?motion picture.*$/i,
  /\s*[•·\-–—:|]?\s*reese(?:'s)?\s+book club.*$/i,
  /\s*[•·\-–—:|]?\s*oprah(?:'s)?\s+book club.*$/i,
];

function cleanBookTitle(rawTitle) {
  let title = normalizeTitleArticles(rawTitle);
  if (!title) return "";
  for (const pattern of TITLE_MARKETING_PATTERNS) {
    const next = title.replace(pattern, "").trim();
    if (next && next.length >= 3 && next !== title) {
      title = next;
    }
  }
  return title.replace(/\s+/g, " ").trim();
}

function normalizeEditableBookTitle(rawTitle) {
  return String(rawTitle || "").replace(/\s+/g, " ").trim();
}

function titleWithoutLeadingArticle(rawTitle) {
  const title = normalizeTitleArticles(rawTitle);
  return normalizeSearchText(title.replace(/^(the|a|an)\s+/i, ""));
}

function choosePreferredArticleTitle(primaryTitle, secondaryTitle) {
  const primary = normalizeTitleArticles(primaryTitle);
  const secondary = normalizeTitleArticles(secondaryTitle);
  if (primary && !secondary) return primary;
  if (!primary && secondary) return secondary;
  if (!primary && !secondary) return "";

  const primaryHasLeading = /^(the|a|an)\s+/i.test(primary);
  const secondaryHasLeading = /^(the|a|an)\s+/i.test(secondary);
  if (primaryHasLeading && !secondaryHasLeading) return primary;
  if (!primaryHasLeading && secondaryHasLeading) {
    const primaryCore = titleWithoutLeadingArticle(primary);
    const secondaryCore = titleWithoutLeadingArticle(secondary);
    if (primaryCore && secondaryCore && primaryCore === secondaryCore) {
      return secondary;
    }
  }
  return primary;
}

function parseSeriesFromTitle(rawTitle) {
  if (!rawTitle)
    return { title: rawTitle, seriesName: "", seriesNum: "", edition: "" };
  const normalizedRawTitle = cleanBookTitle(rawTitle);
  const match = normalizedRawTitle.match(/^(.*?)\s*\(([^,]+),?\s*#?(\d+\.?\d*)?\)$/);
  if (match) {
    const edition = extractEditionFromText(match[2] || "");
    if (edition) {
      return {
        title: normalizeTitleArticles(match[1].trim()),
        seriesName: "",
        seriesNum: "",
        edition,
      };
    }
    return {
      title: normalizeTitleArticles(match[1].trim()),
      seriesName: match[2].trim(),
      seriesNum: match[3] || "",
      edition: "",
    };
  }
  return {
    title: normalizedRawTitle,
    seriesName: "",
    seriesNum: "",
    edition: "",
  };
}

const SERIES_NAME_RE = "(?:series|saga|chronicles|duology|trilogy|quartet|cycle)";
const SERIES_PATTERN_1 = new RegExp(
  `\\b(?:book|volume|vol\\.?)\\s*(\\d+(?:\\.\\d+)?)\\s*(?:of|in)\\s+(?:the\\s+)?([A-Za-z0-9'’&: \\-]{2,90}?)(?:\\s+${SERIES_NAME_RE})?\\b`,
  "i",
);
const SERIES_PATTERN_2 = new RegExp(
  `\\b([A-Za-z0-9'’&: \\-]{2,90}?)\\s+${SERIES_NAME_RE}\\s*#?\\s*(\\d+(?:\\.\\d+)?)?\\b`,
  "i",
);
const SERIES_PATTERN_3 = /\b([A-Za-z0-9'’&: -]{2,90}?)\s*#\s*(\d+(?:\.\d+)?)\b/i;

function sanitizeSeriesName(name) {
  const clean = String(name || "")
    .replace(/\s+/g, " ")
    .replace(/^[\s:,\-–—]+|[\s:,\-–—]+$/g, "")
    .trim();
  if (!clean) return "";
  if (extractEditionFromText(clean)) return "";
  if (/^(book|volume|vol)\s+\d+/i.test(clean)) return "";
  return clean;
}

function extractSeriesFromHints(...parts) {
  for (const part of parts) {
    if (!part || typeof part !== "string") continue;
    const text = part.replace(/\s+/g, " ").trim();
    if (!text) continue;

    const m1 = text.match(SERIES_PATTERN_1);
    if (m1?.[2]) {
      const seriesName = sanitizeSeriesName(m1[2]);
      if (seriesName) return { seriesName, seriesNum: m1[1] || "" };
    }

    const m2 = text.match(SERIES_PATTERN_2);
    if (m2?.[1]) {
      const seriesName = sanitizeSeriesName(m2[1]);
      if (seriesName) return { seriesName, seriesNum: m2[2] || "" };
    }

    const m3 = text.match(SERIES_PATTERN_3);
    if (m3?.[1]) {
      const seriesName = sanitizeSeriesName(m3[1]);
      if (seriesName) return { seriesName, seriesNum: m3[2] || "" };
    }
  }
  return { seriesName: "", seriesNum: "" };
}

function withSeriesHints(parsed, ...parts) {
  if (parsed?.seriesName) return parsed;
  const hinted = extractSeriesFromHints(...parts);
  if (!hinted.seriesName) return parsed;
  return {
    ...(parsed || {}),
    seriesName: hinted.seriesName,
    seriesNum: hinted.seriesNum || parsed?.seriesNum || "",
  };
}
const SEARCH_STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "of",
  "and",
  "to",
  "for",
  "in",
  "on",
  "at",
  "by",
  "with",
]);

function normalizeSearchText(input) {
  return String(input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const LEADING_MARKETING_BLOCK_RE =
  /^([A-Z0-9&'"’“”.,:;!?()[\]/\-–—●•\s]{90,}?)(?=[A-Z][a-z])/;
const MARKETING_BLURB_HINT_RE =
  /\b(top ten|best book|book of the year|notable book|books we love|named a best|winner|shortlist|finalist|award|prize)\b/i;

function stripDescriptionHtml(input) {
  return String(input || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanBookDescription(input) {
  let text = stripDescriptionHtml(input);
  if (!text) return "";

  for (let i = 0; i < 2; i++) {
    const match = text.match(LEADING_MARKETING_BLOCK_RE);
    if (!match) break;
    const lead = match[1];
    if (!MARKETING_BLURB_HINT_RE.test(lead)) break;
    const next = text
      .slice(lead.length)
      .replace(/^[\s●•\-–—,:;.!?]+/, "")
      .trim();
    if (next.length < 80) break;
    text = next;
  }

  return text;
}

const SHELF_ICEBREAKER_PROMPTS = [
  "If you could live in any fictional world, where are you moving?",
  "Which book universe would ruin your life in the best way?",
  "Last book that made you cry?",
  "Guilty pleasure read?",
  "A book you wish you could unread so you could read it again?",
  "A trope you'll never get tired of?",
  "A book you defend like it's family?",
  "Your ideal reading setting?",
  "A book you didn't expect to love?",
  "An underrated gem?",
  "A character you would fight in a parking lot?",
  "Coffee shop read or rainy night at home?",
  "A book you wish more people understood?",
  "Hardcovers or paperbacks?",
  "Currently obsessed with",
  "A trope you're officially banning?",
  "A character you'd trust with your life?",
  "A book that changed your brain chemistry?",
  "Favorite comfort reread?",
  "Best villain you've ever read?",
  "A book that made you want to annotate everything?",
  "A setting you still think about?",
  "A fictional place you'd never survive?",
];

const INVITE_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function normalizeInviteCode(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function formatInviteCode(value) {
  const normalized = normalizeInviteCode(value);
  if (!normalized) return "";
  if (normalized.length <= 4) return normalized;
  return `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}`;
}

function makeInviteCode() {
  let out = "";
  for (let i = 0; i < 8; i++) {
    const idx = Math.floor(Math.random() * INVITE_CODE_ALPHABET.length);
    out += INVITE_CODE_ALPHABET[idx];
  }
  return formatInviteCode(out);
}

function defaultShelfPromptAnswers() {
  return SHELF_ICEBREAKER_PROMPTS.slice(0, 5).map((prompt) => ({
    prompt,
    answer: "",
  }));
}

function defaultShelfSharingConfig() {
  return {
    enabled: false,
    mode: "private",
    inviteCode: "",
    bio: "",
    topGenres: [],
    featuredCurrentReads: [],
    promptAnswers: defaultShelfPromptAnswers(),
    recommendedBookIds: [],
    showFullShelf: true,
    fullShelfLayout: "sectioned",
    showReadCount: true,
    showRatings: true,
    showReading: true,
    showRead: true,
    showUnread: false,
    showDnf: false,
  };
}

function normalizeShelfPromptAnswers(value) {
  const fallback = defaultShelfPromptAnswers();
  const incoming = Array.isArray(value) ? value : [];
  const normalized = incoming
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const prompt = String(item.prompt || "").trim();
      const answer = String(item.answer || "");
      return prompt ? { prompt, answer } : null;
    })
    .filter(Boolean)
    .slice(0, 5);

  while (normalized.length < 5) {
    normalized.push(fallback[normalized.length]);
  }
  return normalized.slice(0, 5);
}

function normalizeShelfSharingConfig(value) {
  const defaults = defaultShelfSharingConfig();
  const source = value && typeof value === "object" ? value : {};
  const mode =
    source.mode === "invite-only" || source.mode === "private"
      ? source.mode
      : source.enabled
        ? "invite-only"
        : "private";
  const uniqueTrimmed = (items, max) =>
    [...new Set((Array.isArray(items) ? items : []).map((x) => String(x || "").trim()))]
      .filter(Boolean)
      .slice(0, max);

  return {
    ...defaults,
    enabled: mode === "invite-only",
    mode,
    inviteCode: formatInviteCode(source.inviteCode),
    bio: String(source.bio || "").slice(0, 200),
    topGenres: uniqueTrimmed(source.topGenres, 5),
    featuredCurrentReads: uniqueTrimmed(source.featuredCurrentReads, 3),
    promptAnswers: normalizeShelfPromptAnswers(source.promptAnswers),
    recommendedBookIds: uniqueTrimmed(source.recommendedBookIds, 6),
    showFullShelf: source.showFullShelf !== false,
    fullShelfLayout: source.fullShelfLayout === "continuous" ? "continuous" : "sectioned",
    showReadCount: source.showReadCount !== false,
    showRatings: source.showRatings !== false,
    showReading: source.showReading !== false,
    showRead: source.showRead !== false,
    showUnread: !!source.showUnread,
    showDnf: !!source.showDnf,
  };
}

function isDnfStatus(status, dnfLabel = "DNF") {
  return status === dnfLabel || status === "DNF";
}

function isSharedShelfStatusVisible(status, cfg, dnfLabel = "DNF") {
  if (status === "Read") return cfg.showRead !== false;
  if (status === "In Progress") return cfg.showReading !== false;
  if (status === "Unstarted") return !!cfg.showUnread;
  if (isDnfStatus(status, dnfLabel)) return !!cfg.showDnf;
  return true;
}

function computeReadingSinceYear(books) {
  const yearCandidates = [];
  for (const book of books || []) {
    const startedYear = dateYear(book?.dateStarted);
    const readYear = dateYear(book?.dateRead);
    if (startedYear) yearCandidates.push(Number(startedYear));
    if (readYear) yearCandidates.push(Number(readYear));
    const createdAt = createdAtOrderValue(book);
    if (Number.isFinite(createdAt) && createdAt > 0) {
      yearCandidates.push(new Date(createdAt).getFullYear());
    }
  }
  const valid = yearCandidates.filter((y) => Number.isFinite(y) && y >= 1900);
  return valid.length ? String(Math.min(...valid)) : String(CUR_YR);
}

function computeMemberSinceYear(user) {
  const raw = String(user?.metadata?.creationTime || "").trim();
  const match = raw.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : String(CUR_YR);
}

function createdAtOrderValue(book) {
  const createdAt = book?.createdAt;
  if (typeof createdAt === "number" && Number.isFinite(createdAt)) {
    return createdAt;
  }
  if (createdAt && typeof createdAt.toMillis === "function") {
    try {
      const millis = createdAt.toMillis();
      if (Number.isFinite(millis)) return millis;
    } catch {
      // Fall through to the serializable Timestamp shape below.
    }
  }
  if (
    createdAt &&
    typeof createdAt === "object" &&
    typeof createdAt.seconds === "number"
  ) {
    const nanos = Number(createdAt.nanoseconds || 0);
    return createdAt.seconds * 1000 + (Number.isFinite(nanos) ? nanos / 1e6 : 0);
  }
  if (typeof createdAt === "string") {
    const parsed = Date.parse(createdAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function nextUpOrderValue(book) {
  const explicit = Number(book?.queueOrder);
  if (Number.isFinite(explicit)) return explicit;
  return createdAtOrderValue(book);
}

function sortNextUpBooks(list = []) {
  return [...list].sort((a, b) => nextUpOrderValue(b) - nextUpOrderValue(a));
}

function nextUpHighestOrder(list = []) {
  return list.reduce((max, book) => Math.max(max, nextUpOrderValue(book)), 0);
}

function normalizeNextUpQueueOrder(list = []) {
  const total = list.length;
  return list.map((book, idx) => ({
    ...book,
    queueOrder: total - idx,
  }));
}

function normalizeIsbn(input) {
  const cleaned = String(input || "")
    .replace(/[^0-9Xx]/g, "")
    .toUpperCase();
  return cleaned.length === 10 || cleaned.length === 13 ? cleaned : null;
}

function extractIsbnFromText(input) {
  const raw = String(input || "");
  const direct = normalizeIsbn(raw);
  if (direct) return direct;

  const matches =
    raw.match(/(?:97[89][0-9Xx\-\s]{10,20}|[0-9Xx][0-9Xx\-\s]{9,16})/g) || [];
  for (const candidate of matches) {
    const normalized = normalizeIsbn(candidate);
    if (normalized) return normalized;
  }
  return null;
}

function detectSearchType(input) {
  const raw = String(input || "").trim();
  const isbn = extractIsbnFromText(raw);
  if (isbn) return { type: "isbn", value: isbn };
  const authorPrefix = raw.match(/^(?:author:|by\s+)(.+)$/i);
  if (authorPrefix) return { type: "author", value: authorPrefix[1].trim() };
  const byMatch = raw.match(/^(.+?)\s+by\s+(.+)$/i);
  if (byMatch) {
    return {
      type: "title-author",
      title: byMatch[1].trim(),
      author: byMatch[2].trim(),
    };
  }
  const words = normalizeSearchText(raw).split(" ").filter(Boolean);
  const hasStop = words.some((w) => SEARCH_STOP_WORDS.has(w));
  const allAlpha = words.every((w) => /^[a-z]+$/i.test(w));
  if (!hasStop && allAlpha && words.length >= 2 && words.length <= 4) {
    return { type: "ambiguous", value: raw };
  }
  return { type: "text", value: raw };
}

function meaningfulWords(input) {
  return normalizeSearchText(input)
    .split(" ")
    .filter((w) => w && !SEARCH_STOP_WORDS.has(w));
}

function shouldRunSearch(input) {
  const search = detectSearchType(input);
  if (
    search.type === "isbn" ||
    search.type === "author" ||
    search.type === "title-author"
  )
    return true;

  const words = meaningfulWords(input);
  const normalized = normalizeSearchText(input);

  if (words.length >= 2) return true;
  return words.length === 1 && normalized === words[0] && words[0].length >= 4;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hasWholeWord(text, word) {
  return new RegExp(`(^|\\s)${escapeRegExp(word)}(\\s|$)`, "i").test(text);
}

function normalizeLookupCacheKey(raw) {
  return normalizeSearchText(String(raw || "")).slice(0, 220);
}

function normalizeClaimForCache(claim) {
  if (!claim || typeof claim !== "object") return null;
  const source = String(claim.source || "").trim();
  const field = String(claim.field || "").trim();
  const entityType = String(claim.entityType || "edition").trim();
  const value = claim.value;
  if (!source || !field) return null;
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && !value.trim()) return null;
  return {
    source,
    field,
    entityType,
    value,
  };
}

function serializeBookForLookupCache(book) {
  if (!book || typeof book !== "object") return null;
  const claims = Array.isArray(book._sourceClaims)
    ? book._sourceClaims
        .map(normalizeClaimForCache)
        .filter(Boolean)
        .slice(0, 40)
    : [];
  return {
    id: String(book.id || "").trim() || mkid(),
    title: String(book.title || "Unknown Title"),
    authors: String(book.authors || "Unknown Author"),
    year: String(book.year || "--"),
    publishedDate: String(book.publishedDate || ""),
    publisher: String(book.publisher || "--"),
    pages:
      book.pages === "--" || book.pages === null || book.pages === undefined
        ? "--"
        : Number(book.pages) || String(book.pages),
    desc: typeof book.desc === "string" ? book.desc : "",
    cover: normalizeCoverUrl(book.cover) || null,
    genres: Array.isArray(book.genres) ? book.genres.slice(0, 6) : [],
    vibes: Array.isArray(book.vibes) ? book.vibes.slice(0, 8) : [],
    seriesName: String(book.seriesName || ""),
    seriesNum: String(book.seriesNum || ""),
    edition: String(book.edition || ""),
    isbn: normalizeIsbn(book.isbn || "") || "",
    isbn10: normalizeIsbn(book.isbn10 || book._isbn10 || "") || "",
    isbn13: normalizeIsbn(book.isbn13 || book._isbn13 || "") || "",
    metaSource: String(book.metaSource || ""),
    _sourceClaims: claims,
  };
}

function deserializeCachedBook(rawBook) {
  const normalized = serializeBookForLookupCache(rawBook);
  return normalized || null;
}

async function readLookupCache(kind, key, mode = "auto") {
  const normalizedKey = normalizeLookupCacheKey(key);
  if (!normalizedKey) return null;
  try {
    const docId = canonicalDocId("lkp", `${kind}::${mode}::${normalizedKey}`);
    const snap = await getDoc(doc(db, "catalogLookupCache", docId));
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    const updatedAtMs = Number(data.updatedAtMs || 0);
    const isEmpty = !!data.isEmpty;
    const ttl = isEmpty ? LOOKUP_CACHE_TTL_MS.empty : LOOKUP_CACHE_TTL_MS[kind] || LOOKUP_CACHE_TTL_MS.search;
    if (!updatedAtMs || Date.now() - updatedAtMs > ttl) return null;
    const cachedResults = Array.isArray(data.results) ? data.results : [];
    return {
      hit: true,
      results: cachedResults.map(deserializeCachedBook).filter(Boolean),
      isEmpty,
    };
  } catch {
    return null;
  }
}

async function writeLookupCache(kind, key, results, mode = "auto") {
  const normalizedKey = normalizeLookupCacheKey(key);
  if (!normalizedKey) return;
  try {
    const sanitized = Array.isArray(results)
      ? results.map(deserializeCachedBook).filter(Boolean).slice(0, 20)
      : [];
    const docId = canonicalDocId("lkp", `${kind}::${mode}::${normalizedKey}`);
    await setDoc(
      doc(db, "catalogLookupCache", docId),
      {
        docId,
        kind,
        mode,
        key: normalizedKey,
        isEmpty: sanitized.length === 0,
        results: sanitized,
        updatedAtMs: Date.now(),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null,
      },
      { merge: true },
    );
  } catch {
    // cache failures should never block search
  }
}

async function fetchGoogleItems(query, maxResults = 20) {
  const url =
    "https://www.googleapis.com/books/v1/volumes?q=" +
    encodeURIComponent(query) +
    "&maxResults=" +
    maxResults +
    "&orderBy=relevance&printType=books&key=" +
    BOOKS_API_KEY;

  const res = await fetch(url);
  const data = await res.json();
  return data.items || [];
}

function buildIsbnDbProxyUrl(isbn) {
  const base = String(ISBNDB_PROXY_URL || "").trim();
  if (!base || !isbn) return "";
  const encoded = encodeURIComponent(isbn);
  if (base.includes("{isbn}")) return base.replace("{isbn}", encoded);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}isbn=${encoded}`;
}

function normalizeCoverUrl(rawUrl) {
  let url = String(rawUrl || "").trim();
  if (!url) return "";
  url = url.replace(/^http:\/\//i, "https://");
  // Keep Google covers conservative; aggressive zooming caused broken/blank covers
  // for some results, so cap back to default zoom.
  if (/books\.google\./i.test(url) && /([?&])zoom=\d+/i.test(url)) {
    url = url.replace(/([?&])zoom=\d+/i, "$1zoom=1");
  }
  return url;
}

function normalizeUserCoverUrl(rawUrl) {
  const normalized = normalizeCoverUrl(rawUrl);
  if (!normalized) return "";
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? normalized
      : "";
  } catch {
    return "";
  }
}

function isPlaceholderCoverUrl(rawUrl) {
  const url = String(rawUrl || "").toLowerCase();
  if (!url) return true;
  return (
    /image[\s_-]*not[\s_-]*available/.test(url) ||
    /not[\s_-]*available/.test(url) ||
    /no[\s_-]*cover/.test(url) ||
    /placeholder/.test(url) ||
    /default[\s_-]*cover/.test(url)
  );
}

function isWeakGenericCoverUrl(rawUrl) {
  const url = String(rawUrl || "").toLowerCase();
  if (!url) return true;
  return (
    isPlaceholderCoverUrl(url) ||
    /coming[\s_-]*soon/.test(url) ||
    /pre[\s_-]*order/.test(url) ||
    /generic/.test(url) ||
    /stock[\s_-]*image/.test(url) ||
    /not[\s_-]*yet/.test(url) ||
    /unavailable/.test(url)
  );
}

function isGoogleHostedCoverUrl(rawUrl) {
  const url = String(rawUrl || "").toLowerCase();
  return (
    url.includes("books.google.") ||
    url.includes("googleusercontent.com/books/")
  );
}

function coverProviderLabel(rawUrl) {
  const url = String(rawUrl || "").toLowerCase();
  if (url.includes("isbndb.com")) return "ISBNdb";
  if (url.includes("openlibrary.org")) return "Open Library";
  if (isGoogleHostedCoverUrl(url)) return "Google Books";
  return "Cover image";
}

function bookNeedsCoverRepair(book) {
  const cover = normalizeCoverUrl(book?.cover || "");
  return !cover || isPlaceholderCoverUrl(cover) || isGoogleHostedCoverUrl(cover);
}

function preferredBookIsbn(book) {
  return (
    normalizeIsbn(book?.isbn13 || book?._isbn13 || "") ||
    normalizeIsbn(book?.isbn10 || book?._isbn10 || book?.isbn || "") ||
    null
  );
}

function mergeSelectedBookMetadata(original, enriched) {
  if (!enriched) return original;
  const sourceClaims = [
    ...(Array.isArray(original?._sourceClaims) ? original._sourceClaims : []),
    ...(Array.isArray(enriched?._sourceClaims) ? enriched._sourceClaims : []),
  ];
  return {
    ...original,
    ...enriched,
    id: enriched.id || original.id,
    cover: enriched.cover || original.cover || null,
    isbn10:
      normalizeIsbn(enriched.isbn10 || original.isbn10 || original._isbn10 || "") ||
      "",
    isbn13:
      normalizeIsbn(enriched.isbn13 || original.isbn13 || original._isbn13 || "") ||
      "",
    _sourceClaims: sourceClaims.slice(0, 60),
  };
}

function upgradeGoogleCoverUrl(rawUrl) {
  return normalizeCoverUrl(rawUrl);
}

function pickGoogleCover(imageLinks) {
  if (!imageLinks || typeof imageLinks !== "object") return "";
  const candidates = [
    imageLinks.thumbnail,
    imageLinks.smallThumbnail,
    imageLinks.small,
    imageLinks.medium,
    imageLinks.large,
    imageLinks.extraLarge,
  ];
  for (const candidate of candidates) {
    const upgraded = upgradeGoogleCoverUrl(candidate);
    if (upgraded && !isPlaceholderCoverUrl(upgraded)) return upgraded;
  }
  return "";
}

function pickBestCover({
  openLibraryCover,
  isbndbCover,
  googleImageLinks,
  preferIsbnDb = false,
}) {
  const openLibrary = normalizeCoverUrl(
    openLibraryCover?.large || openLibraryCover?.medium || openLibraryCover?.small || "",
  );
  const isbndb = normalizeCoverUrl(isbndbCover);
  const google = pickGoogleCover(googleImageLinks);
  const hasOpenLibrary = openLibrary && !isPlaceholderCoverUrl(openLibrary);
  const hasIsbndb = isbndb && !isPlaceholderCoverUrl(isbndb);
  const hasGoogle = !!google;
  const weakIsbnDb = hasIsbndb && isWeakGenericCoverUrl(isbndb);

  if (preferIsbnDb && hasIsbndb && !weakIsbnDb) return isbndb;
  if (hasOpenLibrary) return openLibrary;
  if (hasGoogle) return google;
  if (hasIsbndb) return isbndb;
  return null;
}

function mapGoogleItemForApp(item) {
  const v = item.volumeInfo || {};
  const rawYear = (v.publishedDate || "").slice(0, 4);
  const year = /^\d{4}$/.test(rawYear) ? rawYear : "--";
  const rawDesc = typeof v.description === "string" ? v.description : "";
  const cleanedDesc = cleanBookDescription(rawDesc);
  const parsed = withSeriesHints(
    parseSeriesFromTitle(cleanBookTitle(v.title || "Unknown Title")),
    v.subtitle || "",
    rawDesc,
  );
  const edition =
    parsed.edition ||
    extractEditionFromText(
      v.subtitle || "",
      v.title || "",
      rawDesc,
    );
  const categories = v.categories || [];
  const lane = detectGenreLane(categories, buildGenreTotals(categories));
  const genres = mapGenres(categories, year, 4, { lane });
  const vibes = mapVibes(categories, 8, { lane });

  const mapped = {
    id: item.id,
    ...parsed,
    edition,
    authors: (v.authors || []).join(", ") || "Unknown Author",
    publishedDate: v.publishedDate || "",
    year,
    publisher: v.publisher || "--",
    pages: v.pageCount || "--",
    desc: cleanedDesc,
    cover: pickGoogleCover(v.imageLinks) || null,
    genres,
    vibes,
    _isbn10:
      v.industryIdentifiers?.find((x) => x.type === "ISBN_10")?.identifier ||
      "",
    _isbn13:
      v.industryIdentifiers?.find((x) => x.type === "ISBN_13")?.identifier ||
      "",
    _subtitle: v.subtitle || "",
    _categoriesRaw: (v.categories || []).join(" "),
  };
  mapped.isbn10 = normalizeIsbn(mapped._isbn10) || "";
  mapped.isbn13 = normalizeIsbn(mapped._isbn13) || "";
  return {
    ...mapped,
    metaSource: "google_books_search",
    _sourceClaims: buildDefaultSourceClaimsFromBook(mapped, "google_books"),
  };
}

function dedupeMappedBooks(books) {
  const out = new Map();

  for (const book of books) {
    const isbnKey = normalizeIsbn(book._isbn13) || normalizeIsbn(book._isbn10);
    const fallbackKey =
      normalizeSearchText(book.title) +
      "::" +
      normalizeSearchText(book.authors);
    const key = isbnKey || fallbackKey;

    const prev = out.get(key);
    if (!prev) {
      out.set(key, book);
      continue;
    }

    const prevQ =
      (prev.cover ? 1 : 0) +
      (prev.pages !== "--" ? 1 : 0) +
      (prev.year !== "--" ? 1 : 0);
    const nextQ =
      (book.cover ? 1 : 0) +
      (book.pages !== "--" ? 1 : 0) +
      (book.year !== "--" ? 1 : 0);

    if (nextQ > prevQ) out.set(key, book);
  }

  return [...out.values()];
}

function buildCatalogOverrideDocIds(book) {
  const ids = [];
  const rawIsbn13 = normalizeIsbn(
    book?.isbn13 || book?._isbn13 || book?.isbn || book?.id || "",
  );
  const rawIsbn10 = normalizeIsbn(
    book?.isbn10 || book?._isbn10 || book?.isbn || book?.id || "",
  );
  if (rawIsbn13 && rawIsbn13.length === 13) ids.push(`isbn13_${rawIsbn13}`);
  if (rawIsbn10 && rawIsbn10.length === 10) ids.push(`isbn10_${rawIsbn10}`);
  const titleKey = normalizeSearchText(cleanBookTitle(book?.title || ""));
  const authorKey = normalizeSearchText(firstAuthorFromText(book?.authors || ""));
  if (titleKey && authorKey) {
    ids.push(`title_author_${stableHash(`${titleKey}::${authorKey}`).slice(0, 18)}`);
  }
  return [...new Set(ids)];
}

function normalizeCatalogOverride(raw, docId = "") {
  const source = raw && typeof raw === "object" ? raw : {};
  const out = {
    overrideId: docId || String(source.overrideId || "").trim(),
    active: source.active !== false,
  };
  if (typeof source.title === "string" && source.title.trim()) {
    out.title = cleanBookTitle(source.title);
  }
  if (typeof source.authors === "string" && source.authors.trim()) {
    out.authors = String(source.authors).trim();
  }
  if (typeof source.desc === "string" && source.desc.trim()) {
    out.desc = cleanBookDescription(source.desc);
  }
  if (typeof source.cover === "string" && source.cover.trim()) {
    out.cover = normalizeCoverUrl(source.cover);
  }
  if (Object.prototype.hasOwnProperty.call(source, "seriesName")) {
    out.seriesName =
      source.seriesName === undefined || source.seriesName === null
        ? ""
        : String(source.seriesName).trim();
  }
  if (source.seriesNum !== undefined && source.seriesNum !== null) {
    out.seriesNum = String(source.seriesNum).trim();
  }
  if (typeof source.edition === "string" && source.edition.trim()) {
    out.edition = String(source.edition).trim();
  }
  if (Array.isArray(source.genres)) {
    out.genres = source.genres.map((x) => String(x || "").trim()).filter(Boolean).slice(0, 6);
  }
  if (Array.isArray(source.vibes)) {
    out.vibes = source.vibes.map((x) => String(x || "").trim()).filter(Boolean).slice(0, 8);
  }
  if (typeof source.publisher === "string" && source.publisher.trim()) {
    out.publisher = String(source.publisher).trim();
  }
  if (typeof source.publishedDate === "string" && source.publishedDate.trim()) {
    out.publishedDate = String(source.publishedDate).trim();
  }
  if (source.year !== undefined && source.year !== null && String(source.year).trim()) {
    out.year = String(source.year).trim();
  }
  if (source.pages !== undefined && source.pages !== null && String(source.pages).trim()) {
    out.pages = Number(source.pages) || String(source.pages).trim();
  }
  return out;
}

async function readCatalogOverrideDoc(docId) {
  if (!docId) return null;
  const cached = catalogOverrideMemory.get(docId);
  if (cached && Date.now() - cached.at < CATALOG_OVERRIDE_CACHE_TTL_MS) {
    return cached.value;
  }
  try {
    const snap = await getDoc(doc(db, "catalogOverrides", docId));
    const value = snap.exists() ? normalizeCatalogOverride(snap.data(), docId) : null;
    if (snap.exists()) {
      catalogOverrideMemory.set(docId, { at: Date.now(), value });
    } else {
      catalogOverrideMemory.delete(docId);
    }
    return value;
  } catch {
    return null;
  }
}

function mergeCatalogOverrides(overrides) {
  const list = (Array.isArray(overrides) ? overrides : [])
    .filter((item) => item && item.active !== false)
    .sort((a, b) => {
      const priority = (id) => {
        if (String(id || "").startsWith("isbn13_")) return 3;
        if (String(id || "").startsWith("isbn10_")) return 2;
        if (String(id || "").startsWith("title_author_")) return 1;
        return 0;
      };
      return priority(b.overrideId) - priority(a.overrideId);
    });
  if (!list.length) return null;
  const merged = { active: true };
  for (const item of list) {
    for (const field of CATALOG_OVERRIDE_FIELDS) {
      if (item[field] !== undefined) merged[field] = item[field];
    }
  }
  return merged;
}

async function readCatalogOverrideForBook(book) {
  const docIds = buildCatalogOverrideDocIds(book);
  if (!docIds.length) return null;
  const docs = await Promise.all(docIds.map((id) => readCatalogOverrideDoc(id)));
  return mergeCatalogOverrides(docs);
}

function buildBookFromCatalogOverride(rawIsbn, override) {
  const normalized = normalizeIsbn(rawIsbn);
  const isbn13 = normalized && normalized.length === 13 ? normalized : "";
  const isbn10 = normalized && normalized.length === 10 ? normalized : "";
  const book = {
    id: normalized || mkid(),
    title: cleanBookTitle(override?.title || "Unknown Title"),
    authors: String(override?.authors || "Unknown Author").trim(),
    publishedDate: String(override?.publishedDate || "").trim(),
    year: String(override?.year || "--").trim() || "--",
    publisher: String(override?.publisher || "--").trim() || "--",
    pages:
      override?.pages === undefined || override?.pages === null || override?.pages === ""
        ? "--"
        : Number(override.pages) || String(override.pages),
    desc: cleanBookDescription(override?.desc || ""),
    cover: normalizeCoverUrl(override?.cover || "") || null,
    genres: Array.isArray(override?.genres) ? override.genres.slice(0, 6) : [],
    vibes: Array.isArray(override?.vibes) ? override.vibes.slice(0, 8) : [],
    seriesName: String(override?.seriesName || "").trim(),
    seriesNum: String(override?.seriesNum || "").trim(),
    edition: String(override?.edition || "").trim(),
    isbn10,
    isbn13,
  };
  return {
    ...book,
    metaSource: "catalog_override",
    _sourceClaims: buildDefaultSourceClaimsFromBook(book, "catalog_override"),
  };
}

function applyCatalogOverrideToBook(book, override) {
  if (!book || !override || override.active === false) return book;
  const next = { ...book };
  if (override.title) next.title = cleanBookTitle(override.title);
  if (override.authors) next.authors = String(override.authors).trim();
  if (override.desc) next.desc = cleanBookDescription(override.desc);
  if (override.cover) next.cover = normalizeCoverUrl(override.cover) || next.cover || null;
  if (override.seriesName !== undefined) next.seriesName = String(override.seriesName || "").trim();
  if (override.seriesNum !== undefined) next.seriesNum = String(override.seriesNum || "").trim();
  if (override.edition !== undefined) next.edition = String(override.edition || "").trim();
  if (Array.isArray(override.genres)) next.genres = override.genres.slice(0, 6);
  if (Array.isArray(override.vibes)) next.vibes = override.vibes.slice(0, 8);
  if (override.publisher) next.publisher = String(override.publisher).trim();
  if (override.publishedDate) next.publishedDate = String(override.publishedDate).trim();
  if (override.year) next.year = String(override.year).trim();
  if (override.pages !== undefined && override.pages !== null && override.pages !== "") {
    next.pages = Number(override.pages) || String(override.pages);
  }
  next.metaSource = next.metaSource
    ? `${next.metaSource}+override`
    : "catalog_override";
  next._sourceClaims = [
    ...(Array.isArray(next._sourceClaims) ? next._sourceClaims : []),
    ...buildDefaultSourceClaimsFromBook(next, "catalog_override"),
  ];
  return next;
}

function buildCatalogOverridePatchData(currentBook, override) {
  if (!override || override.active === false) return {};
  const current = currentBook && typeof currentBook === "object" ? currentBook : {};
  const patch = {};

  if (override.title && cleanBookTitle(override.title) !== String(current.title || "")) {
    patch.title = cleanBookTitle(override.title);
  }
  if (override.authors && String(override.authors).trim() !== String(current.authors || "").trim()) {
    patch.authors = String(override.authors).trim();
  }
  if (override.desc && cleanBookDescription(override.desc) !== cleanBookDescription(current.desc || "")) {
    patch.desc = cleanBookDescription(override.desc);
  }
  if (override.cover !== undefined) {
    const nextCover = normalizeCoverUrl(override.cover || "") || null;
    const currentCover = normalizeCoverUrl(current.cover || "") || null;
    if (nextCover !== currentCover) patch.cover = nextCover;
  }
  if (
    override.seriesName !== undefined &&
    String(override.seriesName || "").trim() !== String(current.seriesName || "").trim()
  ) {
    patch.seriesName = String(override.seriesName || "").trim();
  }
  if (
    override.seriesNum !== undefined &&
    String(override.seriesNum || "").trim() !== String(current.seriesNum || "").trim()
  ) {
    patch.seriesNum = String(override.seriesNum || "").trim();
  }
  if (
    override.edition !== undefined &&
    String(override.edition || "").trim() !== String(current.edition || "").trim()
  ) {
    patch.edition = String(override.edition || "").trim();
  }
  if (Array.isArray(override.genres)) {
    const nextGenres = override.genres.slice(0, 6);
    const currentGenres = Array.isArray(current.genres) ? current.genres : [];
    if (
      normalizeSearchText(nextGenres.join("|")) !==
      normalizeSearchText(currentGenres.join("|"))
    ) {
      patch.genres = nextGenres;
    }
  }
  if (Array.isArray(override.vibes)) {
    const nextVibes = override.vibes.slice(0, 8);
    const currentVibes = Array.isArray(current.vibes) ? current.vibes : [];
    if (
      normalizeSearchText(nextVibes.join("|")) !==
      normalizeSearchText(currentVibes.join("|"))
    ) {
      patch.vibes = nextVibes;
    }
  }
  if (
    override.publisher !== undefined &&
    String(override.publisher || "").trim() !== String(current.publisher || "").trim()
  ) {
    patch.publisher = String(override.publisher || "").trim() || "--";
  }
  if (
    override.publishedDate !== undefined &&
    String(override.publishedDate || "").trim() !==
      String(current.publishedDate || "").trim()
  ) {
    patch.publishedDate = String(override.publishedDate || "").trim();
  }
  if (
    override.year !== undefined &&
    String(override.year || "").trim() !== String(current.year || "").trim()
  ) {
    patch.year = String(override.year || "").trim() || "--";
  }
  if (override.pages !== undefined && override.pages !== null && override.pages !== "") {
    const nextPages = Number(override.pages) || String(override.pages);
    if (String(nextPages) !== String(current.pages || "")) {
      patch.pages = nextPages;
    }
  }

  return patch;
}

function buildTitleAuthorOverrideDocIdFromOverride(override) {
  const titleKey = normalizeSearchText(cleanBookTitle(override?.title || ""));
  const authorKey = normalizeSearchText(firstAuthorFromText(override?.authors || ""));
  if (!titleKey || !authorKey) return "";
  return `title_author_${stableHash(`${titleKey}::${authorKey}`).slice(0, 18)}`;
}

function buildMirroredOverridePayload(override, docId) {
  if (!override || override.active === false) return null;
  const mirrored = { active: true, overrideId: docId };
  for (const field of CATALOG_OVERRIDE_FIELDS) {
    if (override[field] !== undefined) mirrored[field] = override[field];
  }
  return mirrored;
}

async function applyCatalogOverridesToBooks(books) {
  const list = Array.isArray(books) ? books : [];
  if (!list.length) return [];
  const pairs = await Promise.all(
    list.map(async (book) => [book, await readCatalogOverrideForBook(book)]),
  );
  return pairs.map(([book, override]) => applyCatalogOverrideToBook(book, override));
}

function strongTitleFilter(book, query) {
  const title = normalizeSearchText(book.title || "");
  const words = meaningfulWords(query);
  if (!words.length) return false;

  const phrase = words.join(" ");
  if (title === phrase) return true;
  if (title.startsWith(phrase + " ")) return true;

  const hits = words.filter((w) => hasWholeWord(title, w)).length;
  return hits >= Math.min(2, words.length);
}

function scoreBookForSearch(book, search) {
  const title = normalizeSearchText(book.title || "");
  const subtitle = normalizeSearchText(book._subtitle || "");
  const authors = normalizeSearchText(book.authors || "");
  const categories = normalizeSearchText(book._categoriesRaw || "");
  const titleBlob = (title + " " + subtitle).trim();
  let score = 0;
  if (search.type === "isbn") {
    const i10 = normalizeIsbn(book._isbn10);
    const i13 = normalizeIsbn(book._isbn13);
    if (i10 === search.value || i13 === search.value) score += 1500;
    else if (i10 || i13) score -= 120;
  }
  const titleQuery =
    search.type === "title-author" ? search.title : search.value || "";
  const authorQuery =
    search.type === "title-author"
      ? search.author
      : search.type === "author" || search.type === "ambiguous"
        ? search.value
        : "";
  const titleWords = splitSearchWords(titleQuery, true);
  const authorWords = splitSearchWords(authorQuery, true);
  const titleHits = countWordHits(titleBlob, titleWords, true);
  const authorHits = countWordHits(authors, authorWords, true);
  const titleNorm = normalizeSearchText(titleQuery);
  if (titleNorm) {
    if (title === titleNorm) score += 240;
    else if (title.startsWith(titleNorm + " ")) score += 140;
    else if (title.includes(titleNorm)) score += 70;
  }
  if (search.type === "author") {
    score += authorHits * 95;
    if (authorWords.length && authorHits === authorWords.length) score += 120;
    if (authorHits === 0) score -= 120;
  } else if (search.type === "title-author") {
    score += titleHits * 45 + authorHits * 70;
  } else if (search.type === "ambiguous") {
    score += titleHits * 42 + authorHits * 42;
  } else {
    score += titleHits * 45 + authorHits * 20;
  }
  if (book.cover) score += 10;
  if (book.pages !== "--") score += 7;
  const y = Number(book.year);
  if (Number.isFinite(y)) {
    if (y < 1900) score -= 60;
    else if (y < 1950) score -= 26;
    else if (y >= 1990) score += 8;
  }
  if (SEARCH_NOISE_TITLE_RE.test(titleBlob)) score -= 90;
  if (SEARCH_BOXSET_RE.test(titleBlob)) score -= 55;
  if (
    (search.type === "author" || search.type === "ambiguous") &&
    SEARCH_ABOUT_AUTHOR_RE.test(titleBlob)
  )
    score -= 110;
  if (SEARCH_NOISE_CATEGORY_RE.test(categories)) score -= 75;
  return score;
}

function keepBookForSearch(book, search, score) {
  const title = normalizeSearchText(
    (book.title || "") + " " + (book._subtitle || ""),
  );
  const authors = normalizeSearchText(book.authors || "");
  if (search.type === "isbn") {
    const i10 = normalizeIsbn(book._isbn10);
    const i13 = normalizeIsbn(book._isbn13);
    if (i10 === search.value || i13 === search.value) return true;
    const hasCoreMeta = !!book.cover || book.pages !== "--" || !!book.publisher;
    return hasCoreMeta && score >= -80;
  }
  if (search.type === "author") {
    const aw = splitSearchWords(search.value, true);
    const aHits = countWordHits(authors, aw, true);
    return aHits >= Math.min(2, aw.length) && score >= 110;
  }
  if (search.type === "title-author") {
    const tw = splitSearchWords(search.title, true);
    const aw = splitSearchWords(search.author, true);
    const tHits = countWordHits(title, tw, true);
    const aHits = countWordHits(authors, aw, true);
    return (
      tHits >= Math.min(2, tw.length) &&
      aHits >= Math.min(1, aw.length) &&
      score >= 120
    );
  }
  if (search.type === "ambiguous") {
    const qw = splitSearchWords(search.value, true);
    const tHits = countWordHits(title, qw, true);
    const aHits = countWordHits(authors, qw, true);
    if (qw.length >= 3) {
      return (
        (tHits >= 3 || (tHits >= 2 && aHits >= 1) || aHits >= 2) && score >= 85
      );
    }
    return (
      (tHits >= 2 || aHits >= 2 || (tHits >= 1 && aHits >= 1)) && score >= 80
    );
  }
  return strongTitleFilter(book, search.value) && score >= 75;
}
const SEARCH_NOISE_TITLE_RE =
  /\b(study guide|analysis|critical|criticism|essay|essays|paper|papers|workbook|summary|companion|review)\b/i;
const SEARCH_BOXSET_RE =
  /\b(box set|boxed set|collection|collected works|complete works|omnibus|anthology)\b/i;
const SEARCH_ABOUT_AUTHOR_RE =
  /\b(biography|life of|about|on the works of|critical essays)\b/i;
const SEARCH_NOISE_CATEGORY_RE =
  /\b(literary criticism|study aids|education|reference|social science)\b/i;

function splitSearchWords(input, allowShortLast = false) {
  const raw = normalizeSearchText(input).split(" ").filter(Boolean);
  return raw.filter((w, i) => {
    if (SEARCH_STOP_WORDS.has(w)) return false;
    if (w.length >= 2) return true;
    return allowShortLast && i === raw.length - 1;
  });
}

function hasWordPrefix(text, word) {
  return new RegExp(`(^|\\s)${escapeRegExp(word)}\\w*`, "i").test(text);
}

function countWordHits(text, words, allowPrefix = false) {
  const t = normalizeSearchText(text);
  let hits = 0;
  for (const w of words) {
    if (hasWholeWord(t, w)) {
      hits++;
      continue;
    }
    if (allowPrefix && w.length >= 3 && hasWordPrefix(t, w)) hits++;
  }
  return hits;
}

function volumeInfoHasIsbn(v, isbn) {
  const wanted = normalizeIsbn(isbn);
  if (!wanted || !v) return false;
  const ids = Array.isArray(v.industryIdentifiers) ? v.industryIdentifiers : [];
  return ids.some((id) => normalizeIsbn(id?.identifier) === wanted);
}

function scoreGoogleIsbnVolume(v, isbn) {
  if (!v) return -Infinity;
  let score = 0;
  if (volumeInfoHasIsbn(v, isbn)) score += 250;
  if (Number(v.pageCount) > 0) score += 90;
  if (v.imageLinks?.thumbnail) score += 25;
  if (v.publisher) score += 15;
  if (v.publishedDate) score += 15;
  if (typeof v.description === "string" && v.description.trim().length > 80) score += 20;
  return score;
}

function pickBestGoogleIsbnVolume(items, isbn) {
  const volumes = (items || [])
    .map((item) => item?.volumeInfo || null)
    .filter(Boolean);
  if (!volumes.length) return null;
  return [...volumes].sort((a, b) => {
    const aExact = volumeInfoHasIsbn(a, isbn) ? 1 : 0;
    const bExact = volumeInfoHasIsbn(b, isbn) ? 1 : 0;
    if (aExact !== bExact) return bExact - aExact;

    const aHasPages = Number(a.pageCount) > 0 ? 1 : 0;
    const bHasPages = Number(b.pageCount) > 0 ? 1 : 0;
    if (aHasPages !== bHasPages) return bHasPages - aHasPages;

    return scoreGoogleIsbnVolume(b, isbn) - scoreGoogleIsbnVolume(a, isbn);
  })[0];
}

function collectGoogleIndustryIsbns(volumeInfo) {
  if (!volumeInfo || typeof volumeInfo !== "object") return [];
  const ids = Array.isArray(volumeInfo.industryIdentifiers)
    ? volumeInfo.industryIdentifiers
    : [];
  const out = [];
  for (const id of ids) {
    const normalized = normalizeIsbn(id?.identifier || "");
    if (normalized) out.push(normalized);
  }
  return [...new Set(out)];
}

function collectOpenLibraryIsbns(olBook) {
  if (!olBook || typeof olBook !== "object") return [];
  const out = [];
  const idObj = olBook.identifiers || {};
  const rawGroups = [idObj.isbn_10, idObj.isbn_13, idObj.isbn];
  for (const group of rawGroups) {
    if (Array.isArray(group)) {
      for (const value of group) {
        const normalized = normalizeIsbn(value);
        if (normalized) out.push(normalized);
      }
    } else {
      const normalized = normalizeIsbn(group);
      if (normalized) out.push(normalized);
    }
  }
  return [...new Set(out)];
}

function collectIsbnDbRelatedIsbns(rawBook) {
  if (!rawBook || typeof rawBook !== "object") return [];
  const out = [];
  const direct = [rawBook.isbn13, rawBook.isbn10, rawBook.isbn];
  for (const value of direct) {
    const normalized = normalizeIsbn(value);
    if (normalized) out.push(normalized);
  }
  const related = Array.isArray(rawBook.other_isbns) ? rawBook.other_isbns : [];
  for (const entry of related) {
    if (typeof entry === "string") {
      const normalized = normalizeIsbn(entry);
      if (normalized) out.push(normalized);
      continue;
    }
    const normalized = normalizeIsbn(
      entry?.isbn13 || entry?.isbn10 || entry?.isbn || entry?.identifier || "",
    );
    if (normalized) out.push(normalized);
  }
  return [...new Set(out)];
}

function buildIsbnCluster(seedIsbn, googleVolumeInfo, isbnDbBook, olBook) {
  const set = new Set();
  const addIsbn = (value) => {
    const normalized = normalizeIsbn(value);
    if (normalized) set.add(normalized);
  };
  addIsbn(seedIsbn);
  for (const value of collectGoogleIndustryIsbns(googleVolumeInfo)) addIsbn(value);
  for (const value of collectOpenLibraryIsbns(olBook)) addIsbn(value);
  for (const value of collectIsbnDbRelatedIsbns(isbnDbBook)) addIsbn(value);
  return [...set];
}

function extractOpenLibraryWorkKey(editionData) {
  const works = Array.isArray(editionData?.works) ? editionData.works : [];
  for (const work of works) {
    const key = String(work?.key || "").trim();
    if (key.startsWith("/works/")) return key;
  }
  return "";
}

function normalizeOpenLibraryDescription(raw) {
  if (!raw) return "";
  if (typeof raw === "string") return cleanBookDescription(raw);
  if (typeof raw === "object") {
    if (typeof raw.value === "string") return cleanBookDescription(raw.value);
    if (typeof raw.description === "string")
      return cleanBookDescription(raw.description);
  }
  return "";
}

function collectOpenLibraryWorkSubjects(workData) {
  const subjects = Array.isArray(workData?.subjects) ? workData.subjects : [];
  return subjects
    .map((entry) => {
      if (typeof entry === "string") return entry.trim();
      if (typeof entry === "object") {
        const v = String(entry?.name || entry?.title || entry?.value || "").trim();
        return v;
      }
      return "";
    })
    .filter(Boolean);
}

function extractOpenLibrarySeries(workData) {
  if (!workData || typeof workData !== "object") {
    return { seriesName: "", seriesNum: "" };
  }
  const candidates = [];
  for (const field of ["series", "series_name", "series_names"]) {
    const raw = workData[field];
    if (Array.isArray(raw)) candidates.push(...raw);
    else if (raw) candidates.push(raw);
  }
  for (const candidate of candidates) {
    const parsed = extractIsbnDbSeries(candidate);
    if (parsed.seriesName) return parsed;
  }
  return { seriesName: "", seriesNum: "" };
}

function pickOpenLibraryEditionPublisher(editionData) {
  const publishers = Array.isArray(editionData?.publishers)
    ? editionData.publishers
    : [];
  for (const entry of publishers) {
    if (typeof entry === "string" && entry.trim()) return entry.trim();
    const name = String(entry?.name || "").trim();
    if (name) return name;
  }
  return "";
}

async function fetchOpenLibraryWorkByIsbn(isbn) {
  const normalized = normalizeIsbn(isbn);
  if (!normalized) return null;
  try {
    const editionRes = await fetch(
      "https://openlibrary.org/isbn/" + normalized + ".json",
    );
    if (!editionRes.ok) return null;
    const editionData = await editionRes.json();
    const workKey = extractOpenLibraryWorkKey(editionData);
    if (!workKey) {
      return { isbn: normalized, editionData, workData: null, workKey: "" };
    }
    const workRes = await fetch("https://openlibrary.org" + workKey + ".json");
    if (!workRes.ok) {
      return { isbn: normalized, editionData, workData: null, workKey };
    }
    const workData = await workRes.json();
    return { isbn: normalized, editionData, workData, workKey };
  } catch {
    return null;
  }
}

async function fetchOpenLibraryWorkForIsbnCluster(isbns, maxLookups = 4) {
  const candidates = Array.isArray(isbns) ? isbns.slice(0, maxLookups) : [];
  for (const candidate of candidates) {
    const found = await fetchOpenLibraryWorkByIsbn(candidate);
    if (found && (found.workData || found.editionData)) return found;
  }
  return null;
}

function normalizeIsbnDbDate(value) {
  const parsed = parseImportDate(value);
  if (!parsed) return "";
  return parsed;
}

function extractTaxonomyHintsFromText(rawText) {
  const text = String(rawText || "");
  if (!text.trim()) return [];
  const out = [];
  const lineRx =
    /^\s*(?:tropes?|themes?|tags?|keywords?|subjects?)\s*:\s*([^\n\r]+)/gim;
  let m;
  while ((m = lineRx.exec(text))) {
    const chunk = String(m[1] || "");
    chunk
      .split(/[|/;,•·]+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((p) => out.push(p));
  }
  return [...new Set(out)];
}

function collectIsbnDbTaxonomyTokens(rawBook) {
  const list = [
    ...(Array.isArray(rawBook?.subjects) ? rawBook.subjects : []),
    ...(Array.isArray(rawBook?.categories) ? rawBook.categories : []),
    ...(typeof rawBook?.subject === "string" && rawBook.subject.trim()
      ? [rawBook.subject.trim()]
      : []),
    ...extractTaxonomyHintsFromText(rawBook?.synopsis || rawBook?.overview || ""),
  ];
  return list.filter((s) => typeof s === "string" && s.trim());
}

function extractIsbnDbSeries(rawSeries) {
  if (!rawSeries) return { seriesName: "", seriesNum: "" };
  if (typeof rawSeries === "string") {
    const cleaned = rawSeries.trim();
    const match = cleaned.match(/^(.*?)(?:\s+#\s?(\d+(?:\.\d+)?))?$/);
    return {
      seriesName: match ? match[1].trim() : cleaned,
      seriesNum: match?.[2] || "",
    };
  }
  if (Array.isArray(rawSeries)) {
    for (const entry of rawSeries) {
      const parsed = extractIsbnDbSeries(entry);
      if (parsed.seriesName) return parsed;
    }
    return { seriesName: "", seriesNum: "" };
  }
  if (typeof rawSeries === "object") {
    const name = String(rawSeries.name || rawSeries.title || "").trim();
    const num = String(
      rawSeries.position || rawSeries.sequence || rawSeries.number || "",
    ).trim();
    return { seriesName: name, seriesNum: num };
  }
  return { seriesName: "", seriesNum: "" };
}

async function fetchIsbnDbByIsbn(isbn) {
  const proxyUrl = buildIsbnDbProxyUrl(isbn);
  if (!proxyUrl) return null;
  try {
    const res = await fetch(proxyUrl, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.book) return data.book;
    if (data?.data?.book) return data.data.book;
    if (data?.result?.book) return data.result.book;
    if (data?.title || data?.isbn13 || data?.isbn10 || data?.isbn) return data;
    return null;
  } catch {
    return null;
  }
}

function mapIsbnDbBookForApp(rawBook, isbnFallback) {
  if (!rawBook) return null;
  const rawTitle =
    cleanBookTitle(choosePreferredArticleTitle(rawBook.title, rawBook.title_long)) ||
    "Unknown Title";
  const parsedTitle = withSeriesHints(
    parseSeriesFromTitle(rawTitle),
    rawBook.subtitle || "",
    rawBook.title_long || "",
    rawBook.series_name || "",
    rawBook.series || "",
  );
  const parsedSeries = extractIsbnDbSeries(rawBook.series || rawBook.series_name);
  const publishedDate = normalizeIsbnDbDate(
    rawBook.date_published || rawBook.date_released || "",
  );
  const year =
    parseStoredDate(publishedDate).year ||
    String(rawBook.year || "").match(/\b(18|19|20)\d{2}\b/)?.[0] ||
    "--";
  const subjects = collectIsbnDbTaxonomyTokens(rawBook);
  const lane = detectGenreLane(subjects, buildGenreTotals(subjects));
  const mapped = {
    id:
      normalizeIsbn(
        isbnFallback || rawBook.isbn13 || rawBook.isbn || rawBook.isbn10 || "",
      ) || mkid(),
    title: parsedTitle.title || rawTitle,
    seriesName: parsedSeries.seriesName || parsedTitle.seriesName || "",
    seriesNum: parsedSeries.seriesNum || parsedTitle.seriesNum || "",
    edition:
      parsedTitle.edition ||
      extractEditionFromText(
        rawBook.edition || "",
        rawBook.binding || "",
        rawBook.title_long || "",
      ),
    authors:
      (Array.isArray(rawBook.authors) ? rawBook.authors.join(", ") : "") ||
      String(rawBook.author || "").trim() ||
      "Unknown Author",
    publishedDate,
    year: /^\d{4}$/.test(String(year || "")) ? String(year) : "--",
    publisher: String(rawBook.publisher || "").trim() || "--",
    pages: Number(rawBook.pages || rawBook.page_count) || "--",
    desc: cleanBookDescription(String(rawBook.synopsis || rawBook.overview || "")),
    cover:
      normalizeCoverUrl(rawBook.image || rawBook.image_url || rawBook.cover) ||
      null,
    genres: mapGenres(subjects, year, 4, { lane }),
    vibes: mapVibes(subjects, 8, { lane }),
    isbn10: normalizeIsbn(rawBook.isbn10 || rawBook.isbn || "") || "",
    isbn13: normalizeIsbn(rawBook.isbn13 || isbnFallback || "") || "",
  };
  return {
    ...mapped,
    metaSource: "isbndb_isbn",
    _sourceClaims: buildDefaultSourceClaimsFromBook(mapped, "isbndb"),
  };
}

async function searchByIsbn(rawIsbn, options = {}) {
  const isbn = extractIsbnFromText(rawIsbn);
  if (!isbn) return null;
  const directOverride = await readCatalogOverrideForBook({
    id: isbn,
    isbn13: isbn.length === 13 ? isbn : "",
    isbn10: isbn.length === 10 ? isbn : "",
  });
  if (!options.refresh) {
    const cached = await readLookupCache("isbn", isbn, ISBN_CACHE_MODE);
    if (cached && !cached.isEmpty) {
      return applyCatalogOverrideToBook(cached.results[0] || null, directOverride);
    }
  }
  try {
    const [olRes, googleRes, isbnDbResult] = await Promise.allSettled([
      fetch(
        "https://openlibrary.org/api/books?bibkeys=ISBN:" +
          isbn +
          "&format=json&jscmd=data",
      ),
      fetch(
        "https://www.googleapis.com/books/v1/volumes?q=isbn:" +
          isbn +
          "&key=" +
          BOOKS_API_KEY,
      ),
      fetchIsbnDbByIsbn(isbn),
    ]);
    let olBook = null;
    let gBook = null;
    let isbnDbBook = null;
    if (olRes.status === "fulfilled" && olRes.value.ok) {
      const olData = await olRes.value.json();
      olBook = olData["ISBN:" + isbn] || null;
    }
    if (googleRes.status === "fulfilled" && googleRes.value.ok) {
      const googleData = await googleRes.value.json();
      gBook = pickBestGoogleIsbnVolume(googleData.items || [], isbn);
    }
    if (isbnDbResult.status === "fulfilled") {
      isbnDbBook = isbnDbResult.value || null;
    }
    if (!olBook && !gBook && !isbnDbBook) {
      if (directOverride) {
        const overrideOnly = buildBookFromCatalogOverride(isbn, directOverride);
        await writeLookupCache("isbn", isbn, [overrideOnly], ISBN_CACHE_MODE);
        return overrideOnly;
      }
      await writeLookupCache("isbn", isbn, [], ISBN_CACHE_MODE);
      return null;
    }

    const isbnCluster = buildIsbnCluster(isbn, gBook, isbnDbBook, olBook);

    let bestIsbnDbBook = isbnDbBook;
    let isbnDbMapped = mapIsbnDbBookForApp(bestIsbnDbBook, isbn);
    const shouldExpandIsbnDb =
      isbnCluster.length > 1 &&
      (!isbnDbMapped?.seriesName ||
        !normalizeCoverUrl(isbnDbMapped?.cover || "") ||
        String(isbnDbMapped?.desc || "").length < 120);
    if (shouldExpandIsbnDb) {
      const relatedCandidates = isbnCluster.filter((v) => v !== isbn).slice(0, 4);
      for (const candidateIsbn of relatedCandidates) {
        const altBook = await fetchIsbnDbByIsbn(candidateIsbn);
        if (!altBook) continue;
        const altMapped = mapIsbnDbBookForApp(altBook, candidateIsbn);
        if (!altMapped) continue;
        const currentHasSeries = !!String(isbnDbMapped?.seriesName || "").trim();
        const altHasSeries = !!String(altMapped.seriesName || "").trim();
        const currentHasCover = !!normalizeCoverUrl(isbnDbMapped?.cover || "");
        const altHasCover = !!normalizeCoverUrl(altMapped.cover || "");
        const currentDescLen = String(isbnDbMapped?.desc || "").length;
        const altDescLen = String(altMapped.desc || "").length;
        const shouldReplace =
          (altHasSeries && !currentHasSeries) ||
          (altHasCover && !currentHasCover) ||
          (!currentHasSeries &&
            !currentHasCover &&
            altDescLen > currentDescLen + 80);
        if (shouldReplace) {
          bestIsbnDbBook = altBook;
          isbnDbMapped = altMapped;
        }
        if (altHasSeries && altHasCover) break;
      }
    }

    const openLibraryWorkInfo = await fetchOpenLibraryWorkForIsbnCluster(
      isbnCluster,
      4,
    );
    const openLibraryWorkData = openLibraryWorkInfo?.workData || null;
    const workSeries = extractOpenLibrarySeries(openLibraryWorkData);
    const workSubjects = collectOpenLibraryWorkSubjects(openLibraryWorkData);
    const workDesc =
      normalizeOpenLibraryDescription(openLibraryWorkData?.description) ||
      normalizeOpenLibraryDescription(openLibraryWorkInfo?.editionData?.description);
    const olEditionPublisher = pickOpenLibraryEditionPublisher(
      openLibraryWorkInfo?.editionData,
    );
    const olEditionPublishDate =
      normalizeIsbnDbDate(openLibraryWorkInfo?.editionData?.publish_date || "") ||
      "";

    const isbnDbClaims = Array.isArray(isbnDbMapped?._sourceClaims)
      ? isbnDbMapped._sourceClaims
      : [];

    const rawYear =
      isbnDbMapped?.year ||
      olEditionPublishDate?.match(/\b(18|19|20)\d{2}\b/)?.[0] ||
      olBook?.publish_date?.match(/\b(18|19|20)\d{2}\b/)?.[0] ||
      gBook?.publishedDate?.slice(0, 4) ||
      "--";
    const year = /^\d{4}$/.test(String(rawYear)) ? String(rawYear) : "--";
    const parsed = withSeriesHints(
      parseSeriesFromTitle(
        cleanBookTitle(
          choosePreferredArticleTitle(
          isbnDbMapped?.title,
          choosePreferredArticleTitle(olBook?.title, gBook?.title),
          ),
        ) || "Unknown Title",
      ),
      gBook?.subtitle || "",
      typeof gBook?.description === "string" ? gBook.description : "",
      bestIsbnDbBook?.series_name || "",
      bestIsbnDbBook?.series || "",
      workSeries.seriesName || "",
    );
    const edition =
      parsed.edition ||
      isbnDbMapped?.edition ||
      extractEditionFromText(
        gBook?.subtitle || "",
        olBook?.title || gBook?.title || "",
        typeof gBook?.description === "string" ? gBook.description : "",
      );
    const cleanedDesc = cleanBookDescription(
      isbnDbMapped?.desc ||
        (typeof gBook?.description === "string" ? gBook.description : "") ||
        workDesc,
    );
    const openSubjects = (olBook?.subjects || [])
      .map((s) => (typeof s === "string" ? s : s?.name))
      .filter(Boolean);
    const combinedCategories = [
      ...(gBook?.categories || []),
      ...((bestIsbnDbBook?.subjects || []).filter((s) => typeof s === "string")),
      ...((bestIsbnDbBook?.categories || []).filter((s) => typeof s === "string")),
      ...openSubjects,
      ...workSubjects,
    ];
    const lane = detectGenreLane(
      combinedCategories,
      buildGenreTotals(combinedCategories),
    );
    const mapped = {
      id: isbn,
      ...parsed,
      edition,
      authors:
        isbnDbMapped?.authors ||
        olBook?.authors?.map((a) => a.name).join(", ") ||
        gBook?.authors?.join(", ") ||
        "Unknown Author",
      publishedDate:
        isbnDbMapped?.publishedDate ||
        gBook?.publishedDate ||
        olEditionPublishDate ||
        olBook?.publish_date ||
        "",
      year,
      publisher:
        isbnDbMapped?.publisher ||
        olBook?.publishers?.[0]?.name ||
        olEditionPublisher ||
        gBook?.publisher ||
        "--",
      pages:
        isbnDbMapped?.pages !== "--"
          ? isbnDbMapped.pages
          : olBook?.number_of_pages || gBook?.pageCount || "--",
      desc: cleanedDesc,
      cover: pickBestCover({
        openLibraryCover: olBook?.cover,
        isbndbCover: isbnDbMapped?.cover,
        googleImageLinks: gBook?.imageLinks,
        preferIsbnDb: true,
      }),
      genres:
        Array.isArray(isbnDbMapped?.genres) && isbnDbMapped.genres.length
          ? isbnDbMapped.genres.slice(0, 4)
          : mapGenres(combinedCategories, year, 4, { lane }),
      vibes:
        Array.isArray(isbnDbMapped?.vibes) && isbnDbMapped.vibes.length
          ? isbnDbMapped.vibes.slice(0, 8)
          : mapVibes(combinedCategories, 8, { lane }),
      seriesName:
        isbnDbMapped?.seriesName || parsed.seriesName || workSeries.seriesName || "",
      seriesNum:
        isbnDbMapped?.seriesNum || parsed.seriesNum || workSeries.seriesNum || "",
      isbn10: normalizeIsbn(
        gBook?.industryIdentifiers?.find((x) => x.type === "ISBN_10")?.identifier ||
          isbnDbMapped?.isbn10 ||
          "",
      ) || "",
      isbn13: normalizeIsbn(
        gBook?.industryIdentifiers?.find((x) => x.type === "ISBN_13")?.identifier ||
          isbnDbMapped?.isbn13 ||
          isbn,
      ) || "",
    };
    const googleClaims = gBook
      ? buildDefaultSourceClaimsFromBook(
          {
            ...mapped,
            title: choosePreferredArticleTitle(gBook?.title, mapped.title) || mapped.title,
            authors: gBook?.authors?.join(", ") || mapped.authors,
            publisher: gBook?.publisher || mapped.publisher,
            pages: gBook?.pageCount || mapped.pages,
            publishedDate: gBook?.publishedDate || mapped.publishedDate,
            year,
            genres: mapGenres(gBook?.categories || [], year, 4, {
              lane: detectGenreLane(
                gBook?.categories || [],
                buildGenreTotals(gBook?.categories || []),
              ),
            }),
            vibes: mapVibes(gBook?.categories || [], 8, {
              lane: detectGenreLane(
                gBook?.categories || [],
                buildGenreTotals(gBook?.categories || []),
              ),
            }),
          },
          "google_books",
        )
      : [];
    const openClaims = olBook
      ? buildDefaultSourceClaimsFromBook(
          {
            ...mapped,
            title: choosePreferredArticleTitle(olBook?.title, mapped.title) || mapped.title,
            authors: olBook?.authors?.map((a) => a.name).join(", ") || mapped.authors,
            publisher: olBook?.publishers?.[0]?.name || mapped.publisher,
            pages: olBook?.number_of_pages || mapped.pages,
            publishedDate: olBook?.publish_date || mapped.publishedDate,
            year,
          },
          "open_library",
        )
      : [];
    const openWorkClaims = openLibraryWorkData
      ? buildDefaultSourceClaimsFromBook(
          {
            ...mapped,
            title:
              choosePreferredArticleTitle(openLibraryWorkData?.title, mapped.title) ||
              mapped.title,
            genres: mapGenres(workSubjects, year, 4, {
              lane: detectGenreLane(workSubjects, buildGenreTotals(workSubjects)),
            }),
            vibes: mapVibes(workSubjects, 8, {
              lane: detectGenreLane(workSubjects, buildGenreTotals(workSubjects)),
            }),
            publishedDate: olEditionPublishDate || mapped.publishedDate,
          },
          "open_library_work",
        )
      : [];
    const finalMapped = {
      ...mapped,
      metaSource: bestIsbnDbBook
        ? openLibraryWorkData
          ? "google_openlibrary_isbndb_cluster_work_isbn"
          : "google_openlibrary_isbndb_cluster_isbn"
        : openLibraryWorkData
        ? "google_openlibrary_work_isbn"
          : "google_openlibrary_isbn",
      _sourceClaims: [...googleClaims, ...openClaims, ...openWorkClaims, ...isbnDbClaims],
    };
    const override = directOverride || (await readCatalogOverrideForBook(finalMapped));
    const resolved = applyCatalogOverrideToBook(finalMapped, override);
    await writeLookupCache("isbn", isbn, [resolved], ISBN_CACHE_MODE);
    return resolved;
  } catch {
    if (directOverride) {
      const overrideOnly = buildBookFromCatalogOverride(isbn, directOverride);
      await writeLookupCache("isbn", isbn, [overrideOnly], ISBN_CACHE_MODE);
      return overrideOnly;
    }
    await writeLookupCache("isbn", isbn, [], ISBN_CACHE_MODE);
    return null;
  }
}

async function searchBooks(input, options = {}) {
  const rawInput = String(input || "").trim();
  const forceIsbn = options.mode === "isbn";
  const forcedIsbn = forceIsbn ? extractIsbnFromText(rawInput) : null;
  const search = forceIsbn
    ? forcedIsbn
      ? { type: "isbn", value: forcedIsbn }
      : null
    : detectSearchType(rawInput);
  if (!search) return [];
  if (!forceIsbn && !shouldRunSearch(rawInput)) return [];

  const cacheKind = search.type === "isbn" ? "isbn" : "search";
  const cacheKey = search.type === "isbn" ? search.value : rawInput;
  const cacheMode =
    search.type === "isbn"
      ? ISBN_CACHE_MODE
      : `${SEARCH_CACHE_MODE}_${search.type}`;
  const cached = await readLookupCache(cacheKind, cacheKey, cacheMode);
  if (cached && !(search.type === "isbn" && cached.isEmpty)) {
    return await applyCatalogOverridesToBooks(cached.results);
  }

  try {
    let items = [];
    if (search.type === "isbn") {
      const exact = await searchByIsbn(search.value);
      if (exact) return [exact];
      items = await fetchGoogleItems("isbn:" + search.value, 10);
    } else if (search.type === "title-author") {
      const [exact, combined, fallback] = await Promise.all([
        fetchGoogleItems(
          `intitle:"${search.title}" inauthor:"${search.author}"`,
          10,
        ),
        fetchGoogleItems(
          `intitle:${search.title} inauthor:${search.author}`,
          16,
        ),
        fetchGoogleItems(`"${search.title}" "${search.author}"`, 10),
      ]);
      items = [...exact, ...combined, ...fallback];
    } else if (search.type === "author") {
      const [exact, loose] = await Promise.all([
        fetchGoogleItems(`inauthor:"${search.value}"`, 12),
        fetchGoogleItems(`inauthor:${search.value}`, 20),
      ]);
      items = [...exact, ...loose];
    } else if (search.type === "ambiguous") {
      const [titleExact, titleLoose, authorExact, authorLoose] =
        await Promise.all([
          fetchGoogleItems(`intitle:"${search.value}"`, 10),
          fetchGoogleItems(`intitle:${search.value}`, 14),
          fetchGoogleItems(`inauthor:"${search.value}"`, 10),
          fetchGoogleItems(`inauthor:${search.value}`, 14),
        ]);
      items = [...titleExact, ...titleLoose, ...authorExact, ...authorLoose];
    } else {
      const [exactTitle, titleOnly, broad] = await Promise.all([
        fetchGoogleItems(`intitle:"${search.value}"`, 10),
        fetchGoogleItems("intitle:" + search.value, 16),
        fetchGoogleItems(search.value, 8),
      ]);
      items = [...exactTitle, ...titleOnly, ...broad];
    }
    const titleSourceForArticleRestore =
      search.type === "title-author" ? search.title : search.value || "";
    const mapped = dedupeMappedBooks(
      items.map(mapGoogleItemForApp).map((book) => ({
        ...book,
        title:
          search.type === "isbn"
            ? cleanBookTitle(normalizeTitleArticles(book.title))
            : cleanBookTitle(
                restoreLeadingArticleFromQuery(book.title, titleSourceForArticleRestore),
              ),
      })),
    );
    const overrideMapped = await applyCatalogOverridesToBooks(mapped);
    const scored = overrideMapped
      .map((book) => ({
        ...book,
        _score: scoreBookForSearch(book, search),
      }))
      .sort((a, b) => b._score - a._score);
    const kept = scored.filter((book) =>
      keepBookForSearch(book, search, book._score),
    );
    const finalBooks =
      search.type === "isbn" && kept.length === 0 ? scored.slice(0, 5) : kept;
    const out = finalBooks.slice(0, 20).map(
      ({ _score, _isbn10, _isbn13, _subtitle, _categoriesRaw, ...book }) =>
        book,
    );
    await writeLookupCache(cacheKind, cacheKey, out, cacheMode);
    return out;
  } catch {
    await writeLookupCache(cacheKind, cacheKey, [], cacheMode);
    return [];
  }
}

function stableHash(input) {
  const str = String(input || "");
  let h1 = 0xdeadbeef ^ str.length;
  let h2 = 0x41c6ce57 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function canonicalDocId(prefix, seed) {
  return `${prefix}_${stableHash(seed).slice(0, 18)}`;
}

function firstAuthorFromText(authors) {
  return String(authors || "")
    .split(",")[0]
    .trim();
}

function parsePageCount(value) {
  const num = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(num) && num > 0 ? num : null;
}

function stripUndefinedFields(input) {
  return Object.fromEntries(
    Object.entries(input || {}).filter(([, value]) => value !== undefined),
  );
}

function sanitizeBookForStorage(book) {
  const blocked = new Set(["metaSource", "sourceClaims"]);
  return Object.fromEntries(
    Object.entries(book || {}).filter(
      ([key, value]) => !key.startsWith("_") && !blocked.has(key) && value !== undefined,
    ),
  );
}

function buildCanonicalRefsFromBook(book) {
  const titleKey = normalizeSearchText(normalizeTitleArticles(book?.title || ""));
  const firstAuthor = firstAuthorFromText(book?.authors || "");
  const authorKey = normalizeSearchText(firstAuthor);
  const workSeed = `${titleKey || "untitled"}::${authorKey || "unknown-author"}`;
  const workId = canonicalDocId("wrk", workSeed);

  const isbn13 = normalizeIsbn(book?.isbn13 || book?._isbn13 || book?.isbn || "");
  const isbn10 = normalizeIsbn(book?.isbn10 || book?._isbn10 || "");
  const editionSeed =
    isbn13 ||
    isbn10 ||
    `${workSeed}::${normalizeSearchText(book?.publisher || "")}::${normalizeSearchText(
      book?.edition || "",
    )}::${book?.year || ""}::${parsePageCount(book?.pages) || ""}`;
  const editionId = canonicalDocId("edt", editionSeed);

  const seriesName = String(book?.seriesName || "").trim();
  const seriesId = seriesName
    ? canonicalDocId("ser", `${normalizeSearchText(seriesName)}::${authorKey || ""}`)
    : null;

  return {
    workId,
    editionId,
    seriesId,
    titleKey,
    firstAuthor,
    isbn10,
    isbn13,
    seriesName,
  };
}

function buildDefaultSourceClaimsFromBook(book, sourceTag) {
  const claims = [];
  const push = (field, value, entityType) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && !value.trim()) return;
    claims.push({
      source: sourceTag || "app",
      field,
      value,
      entityType,
    });
  };

  push("title", normalizeTitleArticles(book?.title || ""), "work");
  push("authors", String(book?.authors || "").trim(), "work");
  push("series_name", String(book?.seriesName || "").trim(), "series");
  push("series_num", String(book?.seriesNum || "").trim(), "series");
  push("edition", String(book?.edition || "").trim(), "edition");
  push("publisher", String(book?.publisher || "").trim(), "edition");
  push("published_date", String(book?.publishedDate || "").trim(), "edition");
  push("year", String(book?.year || "").trim(), "edition");
  push("pages", parsePageCount(book?.pages), "edition");

  const genres = Array.isArray(book?.genres) ? book.genres : [];
  genres.slice(0, 6).forEach((genre) => push("genre", String(genre || "").trim(), "work"));
  return claims;
}

function computeClaimConfidence({ field, value, source, support }) {
  let confidence = 0.58;
  const sourceLabel = normalizeSearchText(source || "");
  if (sourceLabel.includes("google") && sourceLabel.includes("open")) confidence += 0.2;
  else if (sourceLabel.includes("google") || sourceLabel.includes("open")) confidence += 0.12;
  else if (sourceLabel.includes("import")) confidence += 0.08;

  if (field === "title" || field === "authors") confidence += 0.14;
  if (field === "pages" && Number.isFinite(Number(value))) confidence += 0.1;
  if (field === "publisher" && String(value || "").length >= 4) confidence += 0.06;
  if (field === "genre") confidence -= 0.04;
  if (field === "series_name" || field === "series_num") confidence -= 0.02;
  if ((support || 1) > 1) confidence += Math.min(0.2, (support - 1) * 0.08);

  return Math.max(0.35, Math.min(0.98, Number(confidence.toFixed(2))));
}

const IMPORT_FIELD_ALIASES = {
  title: ["title", "book title", "title without series"],
  authors: ["author", "authors", "author l f", "author lf", "primary author"],
  additionalAuthors: ["additional authors", "co authors", "co-authors"],
  isbn: ["isbn", "isbn10", "isbn 10"],
  isbn13: ["isbn13", "isbn 13"],
  status: ["status", "reading status", "exclusive shelf", "shelf"],
  shelves: ["bookshelves", "shelves", "bookshelves with positions", "shelf tags"],
  rating: ["my rating", "rating", "star rating"],
  pages: ["number of pages", "pages", "page count"],
  publisher: ["publisher", "imprint"],
  year: [
    "year published",
    "published year",
    "original publication year",
    "publication year",
    "year",
  ],
  publishedDate: ["published date", "publication date", "date published", "published"],
  dateStarted: ["date started", "started", "start date"],
  dateRead: ["date read", "finished date", "date finished", "read date"],
  genres: ["genre", "genres", "category", "categories"],
  vibes: ["vibe", "vibes", "trope", "tropes"],
  tags: ["tags", "tag"],
  format: ["format", "binding", "book format"],
  notes: ["my review", "private notes", "notes", "review"],
  edition: ["edition"],
  seriesName: ["series", "series name"],
  seriesNum: ["series number", "series #", "series num"],
  description: ["description", "synopsis", "summary"],
};

const IMPORT_IGNORED_TOKENS = new Set([
  "read",
  "to read",
  "to-read",
  "want to read",
  "want-to-read",
  "currently reading",
  "currently-reading",
  "in progress",
  "in-progress",
  "dnf",
  "did not finish",
  "did-not-finish",
  "owned",
  "default",
  "kindle",
  "audible",
  "ebook",
  "e-book",
  "audiobook",
  "physical",
]);

const IMPORT_GENRE_LOOKUP = new Map(
  [...FICTION_GENRES, ...NONFIC_GENRES].map((label) => [normalizeText(label), label]),
);
const IMPORT_VIBE_LOOKUP = new Map(
  ALL_VIBES.map((label) => [normalizeText(label), label]),
);

function normalizeImportHeader(value) {
  return normalizeSearchText(value).replace(/\s+/g, "");
}

function parseCsvText(csvText) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const text = String(csvText || "").replace(/^\uFEFF/, "");

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        field += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(field);
      field = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i++;
      row.push(field);
      const hasValue = row.some((cell) => String(cell || "").trim() !== "");
      if (hasValue) rows.push(row);
      row = [];
      field = "";
      continue;
    }

    field += char;
  }

  row.push(field);
  if (row.some((cell) => String(cell || "").trim() !== "")) rows.push(row);

  if (!rows.length) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => String(h || "").trim());
  const dataRows = rows.slice(1);
  return { headers, rows: dataRows };
}

function buildImportFieldIndexes(headers) {
  const normalizedHeaders = headers.map((h) => normalizeImportHeader(h));
  const map = {};
  for (const [field, aliases] of Object.entries(IMPORT_FIELD_ALIASES)) {
    let idx = -1;
    for (const alias of aliases) {
      const normalizedAlias = normalizeImportHeader(alias);
      idx = normalizedHeaders.findIndex(
        (header) =>
          header === normalizedAlias || header.includes(normalizedAlias),
      );
      if (idx >= 0) break;
    }
    if (idx >= 0) map[field] = idx;
  }
  return map;
}

function getImportValue(row, fieldMap, fieldName) {
  const idx = fieldMap[fieldName];
  if (idx === undefined || idx < 0) return "";
  return String(row[idx] || "").trim();
}

function splitImportTokens(raw) {
  return String(raw || "")
    .split(/[;,|]/g)
    .map((token) =>
      token
        .replace(/\s*\(#\d+\)\s*/g, " ")
        .replace(/[_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean);
}

function parseImportDate(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^\d{4}$/.test(value)) return value;

  const isoLike = value.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?$/);
  if (isoLike) {
    const year = isoLike[1];
    const month = String(Math.min(12, Math.max(1, Number(isoLike[2])))).padStart(2, "0");
    if (!isoLike[3]) return `${year}-${month}`;
    const day = String(Math.min(31, Math.max(1, Number(isoLike[3])))).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const usLike = value.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (usLike) {
    const month = String(Math.min(12, Math.max(1, Number(usLike[1])))).padStart(2, "0");
    const day = String(Math.min(31, Math.max(1, Number(usLike[2])))).padStart(2, "0");
    const yr = Number(usLike[3]);
    const year = String(yr < 100 ? (yr >= 70 ? 1900 + yr : 2000 + yr) : yr);
    return `${year}-${month}-${day}`;
  }

  const dateObj = new Date(value);
  if (!Number.isNaN(dateObj.getTime())) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const extractedYear = value.match(/\b(18|19|20)\d{2}\b/)?.[0] || "";
  return extractedYear;
}

function parseImportRating(raw) {
  const value = Number(String(raw || "").replace(/[^\d.]/g, ""));
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(5, Math.max(0, Math.round(value * 2) / 2));
}

function parseImportPages(raw) {
  const value = Number(String(raw || "").replace(/[^\d]/g, ""));
  if (!Number.isFinite(value) || value <= 0) return "--";
  return value;
}

function parseImportYear(rawYear, parsedDate) {
  const fromYearField = String(rawYear || "").match(/\b(18|19|20)\d{2}\b/)?.[0];
  if (fromYearField) return fromYearField;
  return parseStoredDate(parsedDate).year || "--";
}

function mapImportFormats(rawFormat) {
  const text = normalizeSearchText(rawFormat);
  if (!text) return ["Physical"];
  const formats = [];
  if (/audio|audible|audiobook/.test(text)) formats.push("Audiobook");
  if (/ebook|e book|e-book|kindle|digital/.test(text)) formats.push("E-Book");
  if (!formats.length) formats.push("Physical");
  return [...new Set(formats)];
}

function classifyImportStatus(rawStatus, shelvesBlob, dnfLabel) {
  const statusNorm = normalizeSearchText(rawStatus);
  const blob = normalizeSearchText([rawStatus, shelvesBlob].filter(Boolean).join(" "));
  const isQueueLike =
    /\bto[-\s]?read\b|want[-\s]?to[-\s]?read|\btbr\b|queue|next[-\s]?up|wishlist|pre[-\s]?order/.test(
      statusNorm,
    ) ||
    /\bto[-\s]?read\b|want[-\s]?to[-\s]?read|\btbr\b|queue|next[-\s]?up|wishlist|pre[-\s]?order/.test(
      blob,
    );
  const isCurrentlyReading =
    /\bcurrently[-\s]?reading\b|in[-\s]?progress\b/.test(statusNorm) ||
    /\bcurrently[-\s]?reading\b|in[-\s]?progress\b/.test(blob);
  const isDnfLike =
    /did[-\s]?not[-\s]?finish|\bdnf\b|abandoned|paused|not for me/.test(statusNorm) ||
    /did[-\s]?not[-\s]?finish|\bdnf\b|abandoned|paused|not for me/.test(blob);
  const isReadLike =
    /\bread\b|finished|completed|complete/.test(statusNorm) ||
    (/\bread\b|finished|completed|complete/.test(blob) &&
      !/\bto[-\s]?read\b|want[-\s]?to[-\s]?read|\bcurrently[-\s]?reading\b/.test(blob));

  if (isQueueLike) return { status: "Unstarted", isQueueCandidate: true };
  if (isCurrentlyReading) return { status: "In Progress", isQueueCandidate: false };
  if (isDnfLike) return { status: dnfLabel, isQueueCandidate: false };
  if (isReadLike) return { status: "Read", isQueueCandidate: false };

  return { status: "Unstarted", isQueueCandidate: false };
}

function buildImportEnrichmentPatch(book, found, options = {}) {
  if (!book || !found) return {};
  const force = !!options.force;
  const patch = {};
  const existingTitleNorm = normalizeSearchText(book.title || "");
  const foundTitleNorm = normalizeSearchText(found.title || "");
  const existingAuthorsNorm = normalizeSearchText(book.authors || "");
  const foundAuthorsNorm = normalizeSearchText(found.authors || "");
  const hasSparseDesc = !book.desc || String(book.desc).trim().length < 40;
  const hasSparsePages = !book.pages || book.pages === "--";
  const hasSparsePublisher = !book.publisher || book.publisher === "--";
  const hasSparsePublishedDate =
    !book.publishedDate ||
    book.publishedDate === "--" ||
    (String(book.publishedDate).length <= 4 && String(found.publishedDate || "").length > 4);
  const hasSparseYear = !book.year || book.year === "--";
  const hasSparseGenres = !Array.isArray(book.genres) || book.genres.length === 0;
  const hasSparseVibes = !Array.isArray(book.vibes) || book.vibes.length === 0;
  const existingPublisherNorm = normalizeSearchText(book.publisher || "");
  const foundPublisherNorm = normalizeSearchText(found.publisher || "");
  const looksGenericPublisher = (value) =>
    /\b(penguin random house|harpercollins|hachette|macmillan|simon and schuster|publishing group|media group|book group)\b/.test(
      value,
    );
  const shouldReplacePublisher =
    found.publisher &&
    found.publisher !== "--" &&
    (hasSparsePublisher ||
      (force &&
        existingPublisherNorm &&
        foundPublisherNorm &&
        existingPublisherNorm !== foundPublisherNorm &&
        ((looksGenericPublisher(existingPublisherNorm) &&
          !looksGenericPublisher(foundPublisherNorm)) ||
          (foundPublisherNorm.includes(existingPublisherNorm) &&
            foundPublisherNorm.length - existingPublisherNorm.length >= 5))));

  if (
    found.cover &&
    ((!book.cover && !force) || (force && found.cover !== book.cover))
  ) {
    patch.cover = found.cover;
  }
  if (
    found.title &&
    ((existingTitleNorm === "" && !force) ||
      (force && foundTitleNorm && foundTitleNorm !== existingTitleNorm))
  ) {
    patch.title = cleanBookTitle(found.title);
  }
  if (
    found.authors &&
    ((existingAuthorsNorm === "" && !force) ||
      (force && foundAuthorsNorm && foundAuthorsNorm !== existingAuthorsNorm))
  ) {
    patch.authors = String(found.authors).trim();
  }
  if (
    found.desc &&
    ((hasSparseDesc && !force) ||
      (force &&
        normalizeSearchText(found.desc).slice(0, 220) !==
          normalizeSearchText(book.desc || "").slice(0, 220)))
  ) {
    patch.desc = found.desc;
  }
  if (
    found.pages !== "--" &&
    ((hasSparsePages && !force) ||
      (force && String(found.pages) !== String(book.pages || "")))
  ) {
    patch.pages = found.pages;
  }
  if (
    shouldReplacePublisher &&
    (hasSparsePublisher ||
      (force &&
        existingPublisherNorm &&
        foundPublisherNorm &&
        foundPublisherNorm !== existingPublisherNorm))
  ) {
    patch.publisher = found.publisher;
  }
  if (
    found.publishedDate &&
    ((hasSparsePublishedDate && !force) ||
      (force &&
        normalizeSearchText(found.publishedDate) !==
          normalizeSearchText(book.publishedDate || "")))
  ) {
    patch.publishedDate = found.publishedDate;
  }
  if (
    found.year &&
    found.year !== "--" &&
    ((hasSparseYear && !force) || (force && String(found.year) !== String(book.year || "")))
  ) {
    patch.year = found.year;
  }
  if (Array.isArray(found.genres) && found.genres.length) {
    const nextGenres = found.genres.slice(0, 4);
    const sameGenres =
      normalizeSearchText(nextGenres.join("|")) ===
      normalizeSearchText((book.genres || []).join("|"));
    if (hasSparseGenres || (force && !sameGenres)) {
      patch.genres = nextGenres;
    }
  }
  if (Array.isArray(found.vibes) && found.vibes.length) {
    const nextVibes = found.vibes.slice(0, 8);
    const sameVibes =
      normalizeSearchText(nextVibes.join("|")) ===
      normalizeSearchText((book.vibes || []).join("|"));
    if (hasSparseVibes || (force && !sameVibes)) {
      patch.vibes = nextVibes;
    }
  }
  if (found.edition && (!book.edition || (force && found.edition !== book.edition))) {
    patch.edition = found.edition;
  }
  if (
    found.seriesName !== undefined &&
    ((String(book.seriesName || "").trim() === "" && String(found.seriesName || "").trim() !== "") ||
      (force && String(found.seriesName || "").trim() !== String(book.seriesName || "").trim()))
  ) {
    patch.seriesName = String(found.seriesName || "").trim();
  }
  if (
    found.seriesNum !== undefined &&
    ((String(book.seriesNum || "").trim() === "" && String(found.seriesNum || "").trim() !== "") ||
      (force && String(found.seriesNum || "").trim() !== String(book.seriesNum || "").trim()))
  ) {
    patch.seriesNum = String(found.seriesNum || "").trim();
  }
  return patch;
}

function needsImportEnrichment(book) {
  if (!book) return false;
  if (!book.cover) return true;
  if (!book.desc || String(book.desc).trim().length < 40) return true;
  if (!book.pages || book.pages === "--") return true;
  if (!book.publisher || book.publisher === "--") return true;
  if (!book.publishedDate || book.publishedDate === "--") return true;
  if (!Array.isArray(book.genres) || book.genres.length === 0) return true;
  return false;
}

function prettifyImportTag(token) {
  const clean = String(token || "")
    .replace(/\s*\(#\d+\)\s*/g, " ")
    .replace(/[_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";
  return clean.length > 30 ? clean.slice(0, 30).trim() : clean;
}

function mapImportTaxonomy({
  rawGenres,
  rawVibes,
  rawTags,
  rawShelves,
  year,
}) {
  const genreSet = new Set();
  const vibeSet = new Set();
  const tagSet = new Set();
  const combined = [
    ...splitImportTokens(rawGenres),
    ...splitImportTokens(rawVibes),
    ...splitImportTokens(rawTags),
    ...splitImportTokens(rawShelves),
  ];

  for (const token of combined) {
    const normalized = normalizeText(token);
    if (!normalized || IMPORT_IGNORED_TOKENS.has(normalized)) continue;
    if (/favo?urite/.test(normalized)) continue;

    const directGenre = IMPORT_GENRE_LOOKUP.get(normalized);
    if (directGenre) {
      genreSet.add(directGenre);
      continue;
    }

    const mappedGenre = mapGenres([token], year, 1)[0];
    if (mappedGenre) {
      genreSet.add(mappedGenre);
      continue;
    }

    const directVibe = IMPORT_VIBE_LOOKUP.get(normalized);
    if (directVibe) {
      vibeSet.add(directVibe);
      continue;
    }

    const fuzzyVibe = ALL_VIBES.find((v) => {
      const n = normalizeText(v);
      return (
        (normalized.length >= 4 && n.includes(normalized)) ||
        (n.length >= 4 && normalized.includes(n))
      );
    });
    if (fuzzyVibe) {
      vibeSet.add(fuzzyVibe);
      continue;
    }

    const pretty = prettifyImportTag(token);
    if (pretty) tagSet.add(pretty);
  }

  return {
    genres: [...genreSet].slice(0, 4),
    vibes: [...vibeSet].slice(0, 8),
    tags: [...tagSet].slice(0, 5),
  };
}

function buildImportDraft(csvText, options = {}) {
  const { dnfLabel = "DNF", routeQueueToNextUp = true } = options;
  const { headers, rows } = parseCsvText(csvText);
  if (!headers.length) {
    return { error: "No CSV columns found. Please export again and retry." };
  }
  if (!rows.length) {
    return { error: "This CSV has no book rows to import." };
  }

  const fieldMap = buildImportFieldIndexes(headers);
  const seenInFile = new Set();
  const candidates = [];
  let skippedMissingTitle = 0;
  let skippedDuplicateRows = 0;
  let queueCandidateCount = 0;

  for (const row of rows) {
    const rawTitle = getImportValue(row, fieldMap, "title");
    if (!rawTitle) {
      skippedMissingTitle++;
      continue;
    }

    const parsedTitle = parseSeriesFromTitle(rawTitle);
    const authorPrimary = getImportValue(row, fieldMap, "authors");
    const authorExtra = getImportValue(row, fieldMap, "additionalAuthors");
    const authorParts = [authorPrimary, authorExtra]
      .map((a) => String(a || "").trim())
      .filter(Boolean);
    const authors = [...new Set(authorParts)].join(", ") || "Unknown Author";

    const rawShelves = [
      getImportValue(row, fieldMap, "status"),
      getImportValue(row, fieldMap, "shelves"),
    ]
      .filter(Boolean)
      .join(", ");
    const statusMeta = classifyImportStatus(
      getImportValue(row, fieldMap, "status"),
      rawShelves,
      dnfLabel,
    );
    const queueToNextUp = statusMeta.isQueueCandidate && routeQueueToNextUp;
    if (queueToNextUp) queueCandidateCount++;
    const target = queueToNextUp ? "nextup" : "books";
    const status = queueToNextUp ? "Unstarted" : statusMeta.status;

    const parsedPublishedDate = parseImportDate(
      getImportValue(row, fieldMap, "publishedDate"),
    );
    const year = parseImportYear(
      getImportValue(row, fieldMap, "year"),
      parsedPublishedDate,
    );
    const dateReadRaw = parseImportDate(getImportValue(row, fieldMap, "dateRead"));
    const dateStartedRaw = parseImportDate(
      getImportValue(row, fieldMap, "dateStarted"),
    );

    const isbn13 = normalizeIsbn(getImportValue(row, fieldMap, "isbn13"));
    const isbn10 = normalizeIsbn(getImportValue(row, fieldMap, "isbn"));
    const identityKeys = [];
    if (isbn13) identityKeys.push("isbn:" + isbn13);
    if (isbn10 && isbn10 !== isbn13) identityKeys.push("isbn:" + isbn10);
    const metaKey =
      "meta:" +
      normalizeText((parsedTitle.title || rawTitle) + "::" + (authors || ""));
    identityKeys.push(metaKey);

    const rowDedupeKey = target + "::" + (identityKeys[0] || metaKey);
    if (seenInFile.has(rowDedupeKey)) {
      skippedDuplicateRows++;
      continue;
    }
    seenInFile.add(rowDedupeKey);

    const edition =
      getImportValue(row, fieldMap, "edition") ||
      parsedTitle.edition ||
      extractEditionFromText(
        getImportValue(row, fieldMap, "description"),
        rawTitle,
      );
    const seriesName =
      getImportValue(row, fieldMap, "seriesName") || parsedTitle.seriesName || "";
    const seriesNum =
      getImportValue(row, fieldMap, "seriesNum") || parsedTitle.seriesNum || "";
    const rating = parseImportRating(getImportValue(row, fieldMap, "rating"));
    const pages = parseImportPages(getImportValue(row, fieldMap, "pages"));
    const formats = mapImportFormats(getImportValue(row, fieldMap, "format"));
    const notes = getImportValue(row, fieldMap, "notes");
    const desc = getImportValue(row, fieldMap, "description");
    const taxonomy = mapImportTaxonomy({
      rawGenres: getImportValue(row, fieldMap, "genres"),
      rawVibes: getImportValue(row, fieldMap, "vibes"),
      rawTags: getImportValue(row, fieldMap, "tags"),
      rawShelves,
      year,
    });
    const favFromShelves = /\bfavo?urites?\b/.test(normalizeSearchText(rawShelves));
    const isReadLike = status === "Read" || status === dnfLabel;
    const book = {
      title: parsedTitle.title || rawTitle,
      authors,
      publisher: getImportValue(row, fieldMap, "publisher") || "--",
      year,
      publishedDate: parsedPublishedDate || (year !== "--" ? year : ""),
      pages,
      desc: desc || "",
      cover: null,
      genres: taxonomy.genres,
      vibes: taxonomy.vibes,
      tags: taxonomy.tags,
      status,
      formats,
      rating,
      fav: favFromShelves || rating >= 5,
      notes: notes || "",
      seriesName,
      seriesNum,
      edition,
      dateStarted: dateStartedRaw || null,
      dateRead: isReadLike ? dateReadRaw || null : null,
      reads: [],
      isbn: isbn13 || isbn10 || "",
      isbn10: isbn10 || "",
      isbn13: isbn13 || "",
      createdAt: Date.now(),
    };

    if (target === "nextup") {
      book.status = "Unstarted";
      book.dateStarted = null;
      book.dateRead = null;
    }

    candidates.push({
      target,
      identityKeys,
      book,
    });
  }

  return {
    headers,
    totalRows: rows.length,
    candidates,
    stats: {
      parsedRows: rows.length,
      preparedRows: candidates.length,
      preparedBooks: candidates.filter((c) => c.target === "books").length,
      preparedNextUp: candidates.filter((c) => c.target === "nextup").length,
      skippedMissingTitle,
      skippedDuplicateRows,
      queueCandidateCount,
    },
  };
}

function getBookIdentityKeys(book) {
  const keys = [];
  const isbn13 = normalizeIsbn(book?.isbn13 || book?.isbn || "");
  const isbn10 = normalizeIsbn(book?.isbn10 || "");
  if (isbn13) keys.push("isbn:" + isbn13);
  if (isbn10 && isbn10 !== isbn13) keys.push("isbn:" + isbn10);
  const meta = normalizeText((book?.title || "") + "::" + (book?.authors || ""));
  if (meta) keys.push("meta:" + meta);
  return keys;
}

function MonthYearPicker({ value, onChange, label, T }) {
  const parsed = parseStoredDate(value);
  const [selMo, setSelMo] = useState(parsed.month || "");
  const [selYr, setSelYr] = useState(parsed.year || "");
  const [selDay, setSelDay] = useState(parsed.day || "");
  useEffect(() => {
    const next = parseStoredDate(value);
    setSelMo(next.month || "");
    setSelYr(next.year || "");
    setSelDay(next.day || "");
  }, [value]);
  const sel = {
    padding: "9px 10px",
    borderRadius: 9,
    border: "1px solid " + T.border,
    background: T.s2,
    color: T.text,
    fontFamily: SANS,
    fontSize: 13,
    outline: "none",
    cursor: "pointer",
  };
  const daysInMonth =
    selMo && selYr ? new Date(Number(selYr), Number(selMo), 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );

  function emit(mo, yr, day) {
    if (!yr) {
      onChange("");
      return;
    }
    if (!mo) {
      onChange(yr);
      return;
    }
    if (!day) {
      onChange(yr + "-" + mo);
      return;
    }
    onChange(yr + "-" + mo + "-" + day);
  }
  function handleMo(m) {
    const safeDay =
      m && selYr && selDay
        ? Number(selDay) <= new Date(Number(selYr), Number(m), 0).getDate()
          ? selDay
          : ""
        : "";
    setSelMo(m);
    setSelDay(safeDay);
    emit(m, selYr, safeDay);
  }
  function handleYr(y) {
    const safeDay =
      y && selMo && selDay
        ? Number(selDay) <= new Date(Number(y), Number(selMo), 0).getDate()
          ? selDay
          : ""
        : "";
    setSelYr(y);
    setSelDay(safeDay);
    emit(selMo, y, safeDay);
  }
  function handleDay(d) {
    setSelDay(d);
    emit(selMo, selYr, d);
  }

  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: T.mute, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <select
          value={selMo}
          onChange={(e) => handleMo(e.target.value)}
          style={{ ...sel, flex: 2 }}
        >
          <option value="">Month (optional)</option>
          {MONTHS_FULL.map((m, i) => (
            <option key={i} value={String(i + 1).padStart(2, "0")}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={selYr}
          onChange={(e) => handleYr(e.target.value)}
          style={{ ...sel, flex: 1 }}
        >
          <option value="">Year</option>
          {YEARS.map((y) => (
            <option key={y} value={String(y)}>
              {y}
            </option>
          ))}
        </select>
      </div>
      {selMo && selYr && (
        <div style={{ marginTop: 6 }}>
          <select
            value={selDay}
            onChange={(e) => handleDay(e.target.value)}
            style={{ ...sel, width: "100%" }}
          >
            <option value="">Day (optional)</option>
            {days.map((d) => (
              <option key={d} value={d}>
                {Number(d)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function HalfStars({ val, onChange, size }) {
  const [hovered, setHovered] = useState(null);
  const display = hovered !== null ? hovered : val;
  const gold = "#C4884A",
    empty = "#E4DED6";
  const sz = size || 26;
  function starType(n) {
    if (display >= n) return "full";
    if (display >= n - 0.5) return "half";
    return "empty";
  }
  function Star({ type }) {
    const pts =
      "12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26";
    if (type === "full")
      return (
        <svg width={sz} height={sz} viewBox="0 0 24 24">
          <polygon
            points={pts}
            fill={gold}
            stroke={gold}
            strokeWidth="0.5"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (type === "half")
      return (
        <svg width={sz} height={sz} viewBox="0 0 24 24">
          <defs>
            <linearGradient id="hg">
              <stop offset="50%" stopColor={gold} />
              <stop offset="50%" stopColor={empty} />
            </linearGradient>
          </defs>
          <polygon
            points={pts}
            fill="url(#hg)"
            stroke={empty}
            strokeWidth="0.5"
            strokeLinejoin="round"
          />
        </svg>
      );
    return (
      <svg width={sz} height={sz} viewBox="0 0 24 24">
        <polygon
          points={pts}
          fill="none"
          stroke={empty}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <div
      style={{ display: "flex", gap: 2, alignItems: "center" }}
      onMouseLeave={() => setHovered(null)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          style={{
            position: "relative",
            width: sz + 4,
            height: sz + 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: onChange ? "pointer" : "default",
          }}
        >
          {onChange && (
            <>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "50%",
                  height: "100%",
                  zIndex: 2,
                }}
                onMouseEnter={() => setHovered(n - 0.5)}
                onClick={() => onChange(val === n - 0.5 ? 0 : n - 0.5)}
              />
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: "50%",
                  height: "100%",
                  zIndex: 2,
                }}
                onMouseEnter={() => setHovered(n)}
                onClick={() => onChange(val === n ? 0 : n)}
              />
            </>
          )}
          <Star type={starType(n)} />
        </div>
      ))}
      {val > 0 && (
        <span
          style={{ fontSize: 12, fontWeight: 600, color: gold, marginLeft: 4 }}
        >
          {val}
        </span>
      )}
    </div>
  );
}

function GenrePicker({
  selectedGenres,
  onGenres,
  lane = "fiction",
  onLaneChange,
  selectedVibes,
  onVibes,
  customTags,
  onTags,
  T,
  openState,
  setOpenState,
}) {
  const [localOpen, setLocalOpen] = useState({
    fiction: false,
    nonfic: false,
    vibes: false,
    funStuff: false,
  });
  const open = openState || localOpen;
  const setOpen = setOpenState || setLocalOpen;
  const activeLane = normalizeGenreLane(lane);
  const fictionOpen = !!open.fiction;
  const nonficOpen = !!open.nonfic;
  const vibesOpen = !!open.vibes;
  const funStuffOpen = !!open.funStuff;
  const [newTag, setNewTag] = useState("");
  const inputRef = useRef(null);
  const GENRE_LIMIT = 4,
    VIBE_LIMIT = 8,
    TAG_LIMIT = 5;
  const genreFull = selectedGenres.length >= GENRE_LIMIT,
    vibeFull = selectedVibes.length >= VIBE_LIMIT,
    tagFull = customTags.length >= TAG_LIMIT;
  const selectedMoodVibes = selectedVibes.filter((v) => VIBES.includes(v));
  const selectedFunStuff = selectedVibes.filter((v) => FUN_STUFF.includes(v));
  function toggleSection(section) {
    setOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  }
  function toggleGenre(g) {
    onGenres(
      selectedGenres.includes(g)
        ? selectedGenres.filter((x) => x !== g)
        : genreFull
          ? selectedGenres
          : [...selectedGenres, g],
    );
  }
  function toggleVibe(v) {
    onVibes(
      selectedVibes.includes(v)
        ? selectedVibes.filter((x) => x !== v)
        : vibeFull
          ? selectedVibes
          : [...selectedVibes, v],
    );
  }
  function addTag() {
    const t = newTag.trim();
    if (
      !t ||
      customTags.map((x) => x.toLowerCase()).includes(t.toLowerCase()) ||
      tagFull
    )
      return;
    onTags([...customTags, t]);
    setNewTag("");
    inputRef.current?.focus();
  }
  function ExpandRow({ label, sublabel, open, onToggle, color }) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 0",
          cursor: "pointer",
          userSelect: "none",
          background: "transparent",
          border: "none",
          textAlign: "left",
          touchAction: "manipulation",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: open ? color : T.text,
            }}
          >
            {label}
          </span>
          {sublabel && !open && (
            <span
              style={{
                fontSize: 11,
                color: T.mute,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: 180,
              }}
            >
              {sublabel}
            </span>
          )}
        </div>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke={open ? color : T.mute}
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease",
            flexShrink: 0,
          }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    );
  }
  function ChipRow({ items, selected, onToggle, isFull, color, bgColor }) {
    return (
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 7, paddingBottom: 12 }}
      >
        {items.map((g) => {
          const on = selected.includes(g),
            disabled = isFull && !on;
          return (
            <button
              key={g}
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) onToggle(g);
              }}
              style={{
                padding: "7px 14px",
                borderRadius: 20,
                cursor: disabled ? "default" : "pointer",
                fontSize: 12,
                fontFamily: SANS,
                whiteSpace: "nowrap",
                transition: "all 0.13s",
                border: "1px solid " + (on ? color : T.border),
                background: on ? bgColor : T.surface,
                color: on ? color : disabled ? T.mute : T.sub,
                fontWeight: on ? 600 : 400,
                opacity: disabled ? 0.3 : 1,
              }}
            >
              {g}
            </button>
          );
        })}
      </div>
    );
  }
  const divider = <div style={{ height: 1, background: T.border }} />;
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: T.mute,
          marginBottom: 4,
        }}
      >
        Genre{" "}
        {selectedGenres.length > 0 && (
          <span style={{ color: T.accent }}>
            ({selectedGenres.length}/{GENRE_LIMIT})
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => onLaneChange && onLaneChange("fiction")}
          style={{
            padding: "6px 12px",
            borderRadius: 18,
            border:
              "1px solid " + (activeLane === "fiction" ? T.accent : T.border),
            background: activeLane === "fiction" ? T.accentBg : T.surface,
            color: activeLane === "fiction" ? T.accent : T.sub,
            fontSize: 12,
            fontWeight: 600,
            cursor: onLaneChange ? "pointer" : "default",
            fontFamily: SANS,
          }}
        >
          Fiction
        </button>
        <button
          type="button"
          onClick={() => onLaneChange && onLaneChange("nonfiction")}
          style={{
            padding: "6px 12px",
            borderRadius: 18,
            border:
              "1px solid " + (activeLane === "nonfiction" ? T.accent : T.border),
            background: activeLane === "nonfiction" ? T.accentBg : T.surface,
            color: activeLane === "nonfiction" ? T.accent : T.sub,
            fontSize: 12,
            fontWeight: 600,
            cursor: onLaneChange ? "pointer" : "default",
            fontFamily: SANS,
          }}
        >
          Non-Fiction
        </button>
      </div>
      {activeLane === "fiction" && (
        <>
          <ExpandRow
            label="Fiction"
            sublabel={
              selectedGenres.filter((g) => FICTION_GENRES.includes(g)).join(", ") ||
              null
            }
            open={fictionOpen}
            onToggle={() => toggleSection("fiction")}
            color={T.accent}
          />
          {fictionOpen && (
            <ChipRow
              items={FICTION_GENRES}
              selected={selectedGenres}
              onToggle={toggleGenre}
              isFull={genreFull}
              color={T.accent}
              bgColor={T.accentBg}
            />
          )}
        </>
      )}
      {activeLane === "nonfiction" && (
        <>
          <ExpandRow
            label="Non-Fiction"
            sublabel={
              selectedGenres.filter((g) => NONFIC_GENRES.includes(g)).join(", ") ||
              null
            }
            open={nonficOpen}
            onToggle={() => toggleSection("nonfic")}
            color={T.accent}
          />
          {nonficOpen && (
            <ChipRow
              items={NONFIC_GENRES}
              selected={selectedGenres}
              onToggle={toggleGenre}
              isFull={genreFull}
              color={T.accent}
              bgColor={T.accentBg}
            />
          )}
        </>
      )}
      {divider}
      <ExpandRow
        label={
          <>
            The Vibes{" "}
            {selectedMoodVibes.length > 0 && (
              <span style={{ color: "#C4607A" }}>
                ({selectedMoodVibes.length})
              </span>
            )}
          </>
        }
        sublabel={selectedMoodVibes.join(", ") || "dark, cozy, psychological, witches..."}
        open={vibesOpen}
        onToggle={() => toggleSection("vibes")}
        color="#C4607A"
      />
      {vibesOpen && (
        <ChipRow
          items={VIBES}
          selected={selectedVibes}
          onToggle={toggleVibe}
          isFull={vibeFull}
          color="#C4607A"
          bgColor="rgba(196,96,122,0.1)"
        />
      )}
      {divider}
      <ExpandRow
        label={
          <>
            The Fun Stuff{" "}
            {selectedFunStuff.length > 0 && (
              <span style={{ color: "#B26A2F" }}>
                ({selectedFunStuff.length}/{VIBE_LIMIT})
              </span>
            )}
          </>
        }
        sublabel={
          selectedFunStuff.join(", ") || "enemies to lovers, found family, fake dating..."
        }
        open={funStuffOpen}
        onToggle={() => toggleSection("funStuff")}
        color="#B26A2F"
      />
      {funStuffOpen && (
        <ChipRow
          items={FUN_STUFF}
          selected={selectedVibes}
          onToggle={toggleVibe}
          isFull={vibeFull}
          color="#B26A2F"
          bgColor="rgba(178,106,47,0.12)"
        />
      )}
      {divider}
      <div style={{ paddingTop: 10 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: T.mute,
            marginBottom: 8,
          }}
        >
          My Tags{" "}
          {customTags.length > 0 && (
            <span style={{ color: "#2A8C7A" }}>
              ({customTags.length}/{TAG_LIMIT})
            </span>
          )}
        </div>
        {customTags.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 8,
            }}
          >
            {customTags.map((t) => (
              <span
                key={t}
                style={{
                  background: "rgba(42,140,122,0.1)",
                  color: "#2A8C7A",
                  borderRadius: 20,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                #{t}
                <button
                  type="button"
                  onClick={() => onTags(customTags.filter((x) => x !== t))}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#2A8C7A",
                    fontSize: 12,
                    padding: 0,
                    opacity: 0.6,
                  }}
                >
                  &#x2715;
                </button>
              </span>
            ))}
          </div>
        )}
        {!tagFull && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              borderBottom: "1.5px solid " + T.border,
            }}
          >
            <span
              style={{
                fontSize: 13,
                color: T.mute,
                marginRight: 4,
                paddingBottom: 8,
              }}
            >
              #
            </span>
            <input
              ref={inputRef}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder="add a tag, hit enter..."
              maxLength={30}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 13,
                color: T.text,
                paddingBottom: 8,
                fontFamily: SANS,
              }}
            />
            {newTag.trim() && (
              <button
                type="button"
                onClick={addTag}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#2A8C7A",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "0 0 8px 8px",
                  fontFamily: SANS,
                }}
              >
                Add
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AppInner() {
  const [addGenreOpen, setAddGenreOpen] = useState({
    fiction: false,
    nonfic: false,
    vibes: false,
    funStuff: false,
  });
  const [detailGenreOpen, setDetaiGenreOpen] = useState({
    fiction: false,
    nonfic: false,
    vibes: false,
    funStuff: false,
  });
  const [theme, setTheme] = useState(() =>
    readStoredTheme(THEMES, DEFAULT_THEME_KEY),
  );
  const [name, setName] = useState("My Library");
  const [avatar, setAvatar] = useState("📚");
  const [tmpName, setTmpName] = useState("My Library");
  const [books, setBooks] = useState([]);
  const [nextUp, setNextUp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [view, setView] = useState("mybooks");
  const [yearlyGoals, setYearlyGoals] = useState({ [CUR_YR]: 24 });
  const [tmpGoal, setTmpGoal] = useState(24);
  const [checkIns, setCheckIns] = useState([]);
  const [showStreak, setShowStreak] = useState(true);
  const [showBookRatings, setShowBookRatings] = useState(true);
  const [dnfLabel, setDnfLabel] = useState("DNF");
  const [tmpDnf, setTmpDnf] = useState("DNF");
  const [addSheet, setAddSheet] = useState(false);
  const [filterSheet, setFilterSheet] = useState(false);
  const [collectionSq, setCollectionSq] = useState("");
  const [collectionFocused, setCollectionFocused] = useState(false);
  const [addTarget, setAddTarget] = useState("library");
  const [nextUpFromLibrarySq, setNextUpFromLibrarySq] = useState("");
  const [nextUpDragUid, setNextUpDragUid] = useState("");
  const [nextUpDropUid, setNextUpDropUid] = useState("");
  const [importRouteQueueToNextUp, setImportRouteQueueToNextUp] = useState(true);
  const [importSkipDuplicates, setImportSkipDuplicates] = useState(true);
  const [importSource, setImportSource] = useState(null);
  const [importDraft, setImportDraft] = useState(null);
  const [importErr, setImportErr] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importEnrichBusy, setImportEnrichBusy] = useState(false);
  const [importEnrichProgress, setImportEnrichProgress] = useState({
    done: 0,
    total: 0,
    updated: 0,
  });
  const [importReport, setImportReport] = useState(null);
  const [importReturnView, setImportReturnView] = useState("settings");
  const [shelfSharing, setShelfSharing] = useState(defaultShelfSharingConfig());
  const [shelfPromptDrafts, setShelfPromptDrafts] = useState(defaultShelfPromptAnswers());
  const [shareRecommendedQuery, setShareRecommendedQuery] = useState("");
  const [shareSyncBusy, setShareSyncBusy] = useState(false);
  const [shareCodeBusy, setShareCodeBusy] = useState(false);
  const [shareRevokeBusy, setShareRevokeBusy] = useState(false);
  const [sharedRoute, setSharedRoute] = useState({
    ownerId: "",
    inviteCode: "",
    source: "",
  });
  const [sharedProfileRemote, setSharedProfileRemote] = useState(null);
  const [sharedBooksRemote, setSharedBooksRemote] = useState([]);
  const [sharedRemoteLoading, setSharedRemoteLoading] = useState(false);
  const [sharedRemoteError, setSharedRemoteError] = useState("");
  const [sharedUnlockCode, setSharedUnlockCode] = useState("");
  const [sharedShelfGridMode, setSharedShelfGridMode] = useState(false);
  const [sharedUnlockBusy, setSharedUnlockBusy] = useState(false);
  const [sharedUnlockErr, setSharedUnlockErr] = useState("");
  const [metadataReportOpen, setMetadataReportOpen] = useState(false);
  const [metadataReportBusy, setMetadataReportBusy] = useState(false);
  const [metadataReportIssues, setMetadataReportIssues] = useState([]);
  const [metadataReportNote, setMetadataReportNote] = useState("");
  const [metadataReportBook, setMetadataReportBook] = useState(null);
  const [metadataReportSentOpen, setMetadataReportSentOpen] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [adminFixBusy, setAdminFixBusy] = useState(false);
  const [adminFixReport, setAdminFixReport] = useState(null);
  const [coverRepairBusy, setCoverRepairBusy] = useState(false);
  const [coverRepairProgress, setCoverRepairProgress] = useState({
    done: 0,
    total: 0,
    updated: 0,
  });
  const [coverRepairReport, setCoverRepairReport] = useState(null);
  const [sharedAccessNonce, setSharedAccessNonce] = useState(0);
  const [sharedShelfGenre, setSharedShelfGenre] = useState("All");
  const [sharedShelfQuery, setSharedShelfQuery] = useState("");
  const [smartNextPick, setSmartNextPick] = useState(null);
  const [sq, setSq] = useState("");
  const [searchMode, setSearchMode] = useState("auto");
  const [results, setResults] = useState([]);
  const [srching, setSrching] = useState(false);
  const [searchHasRun, setSearchHasRun] = useState(false);
  const [pending, setPending] = useState(null);
  const [aTitle, setATitle] = useState("");
  const [aFmts, setAFmts] = useState(["Physical"]);
  const [aStat, setAStat] = useState("Unstarted");
  const [aGenreLane, setAGenreLane] = useState("fiction");
  const [aGenres, setAGenres] = useState([]);
  const [aVibes, setAVibes] = useState([]);
  const [aTags, setATags] = useState([]);
  const [aRating, setARating] = useState(0);
  const [aNotes, setANotes] = useState("");
  const [aFav, setAFav] = useState(false);
  const [aSeriesName, setASeriesName] = useState("");
  const [aSeriesNum, setASeriesNum] = useState("");
  const [aEdition, setAEdition] = useState("");
  const [aStartDate, setAStartDate] = useState("");
  const [aEndDate, setAEndDate] = useState("");
  const [fStatus, setFStatus] = useState("All");
  const [fFav, setFFav] = useState(false);
  const [fGenre, setFGenre] = useState("All");
  const [fFmt, setFFmt] = useState("All");
  const [sortBy, setSortBy] = useState("smart");
  const [gridMode, setGridMode] = useState(false);
  const [active, setActive] = useState(null);
  const [dTitle, setDTitle] = useState("");
  const [dNotes, setDNotes] = useState("");
  const [dDesc, setDDesc] = useState("");
  const [dRating, setDRating] = useState(0);
  const [dStatus, setDStatus] = useState("Unstarted");
  const [dGenreLane, setDGenreLane] = useState("fiction");
  const [dFmts, setDFmts] = useState([]);
  const [dFav, setDFav] = useState(false);
  const [dGenres, setDGenres] = useState([]);
  const [dVibes, setDVibes] = useState([]);
  const [dTags, setDTags] = useState([]);
  const [dSeriesName, setDSeriesName] = useState("");
  const [dSeriesNum, setDSeriesNum] = useState("");
  const [dEdition, setDEdition] = useState("");
  const [dCover, setDCover] = useState("");
  const [dIsbn, setDIsbn] = useState("");
  const [dPublishedDate, setDPublishedDate] = useState("");
  const [dPublisher, setDPublisher] = useState("");
  const [dPages, setDPages] = useState("");
  const [dStartDate, setDStartDate] = useState("");
  const [dEndDate, setDEndDate] = useState("");
  const [dReads, setDReads] = useState([]);
  const [dRereading, setDRereading] = useState(false);
  const [dRereadStartDate, setDRereadStartDate] = useState("");
  const [fromNextUp, setFromNextUp] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [yr, setYr] = useState(CUR_YR);
  const [showRecap, setShowRecap] = useState(false);
  const [monthPeek, setMonthPeek] = useState(null);
  const [detailTab, setDetailTab] = useState("view");
  const [detailEditSection, setDetailEditSection] = useState("");
  const [finishPromptOpen, setFinishPromptOpen] = useState(false);
  const [finishPromptBusy, setFinishPromptBusy] = useState(false);
  const [finishPromptRating, setFinishPromptRating] = useState(0);
  const [finishPromptFav, setFinishPromptFav] = useState(false);
  const [finishPromptNotes, setFinishPromptNotes] = useState("");
  const [scanOn, setScanOn] = useState(false);
  const [scanErr, setScanErr] = useState("");
  const [toast, setToast] = useState(null);
  const vidRef = useRef(null);
  const streamRef = useRef(null);
  const toastTmr = useRef(null);
  const importFileRef = useRef(null);
  const myBooksScrollRef = useRef(null);
  const nextUpScrollRef = useRef(null);
  const searchScrollRef = useRef(null);
  const detailViewScrollRef = useRef(null);
  const detailEditScrollRef = useRef(null);
  const privateShelfScrollRef = useRef(null);
  const sharedProfileScrollRef = useRef(null);
  const sharedBookshelfScrollRef = useRef(null);
  const searchReqRef = useRef(0);
  const sharedAutoUnlockKeyRef = useRef("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);
  const [resetScope, setResetScope] = useState(null);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [viewportW, setViewportW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 430,
  );
  const T = THEMES[theme] || THEMES[DEFAULT_THEME_KEY];
  const shellMaxWidth =
    viewportW >= 1280 ? 980 : viewportW >= 768 ? 760 : 430;
  const isTabletViewport = viewportW >= 768;
  const myBooksGridColumns = isTabletViewport
    ? "repeat(3,minmax(0,1fr))"
    : "repeat(2,minmax(0,1fr))";

  const STATUS = {
    Unstarted: {
      color: "#8B7FA8",
      bg: "rgba(139,127,168,0.12)",
      label: "Unread",
    },
    "In Progress": {
      color: "#B8823C",
      bg: "rgba(184,130,60,0.12)",
      label: "Reading",
    },
    Read: { color: "#4E8060", bg: "rgba(78,128,96,0.12)", label: "Finished" },
    [dnfLabel]: {
      color: "#B84C4C",
      bg: "rgba(184,76,76,0.12)",
      label: dnfLabel,
    },
  };

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
      else setUserId(null);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setIsAdminUser(false);
      return undefined;
    }
    getDoc(doc(db, "admins", userId))
      .then((snap) => {
        if (!cancelled) setIsAdminUser(snap.exists());
      })
      .catch(() => {
        if (!cancelled) setIsAdminUser(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    function handleResize() {
      setViewportW(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    const currentBuildId = normalizeBuildId(BUILD_ID);
    if (!currentBuildId) return undefined;

    async function checkForAppUpdate() {
      try {
        const res = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (!res.ok) return;
        const payload = await res.json();
        const latestBuildId = normalizeBuildId(payload?.buildId);
        if (
          !cancelled &&
          latestBuildId &&
          latestBuildId !== currentBuildId
        ) {
          setUpdateReady(true);
          setUpdateDismissed(false);
        }
      } catch {
        // Ignore silent update check errors.
      }
    }

    const onFocus = () => {
      checkForAppUpdate();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") checkForAppUpdate();
    };
    const onPageShow = () => {
      checkForAppUpdate();
    };
    const delayedCheck = window.setTimeout(checkForAppUpdate, 2500);
    const intervalId = window.setInterval(checkForAppUpdate, 30000);

    checkForAppUpdate();
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearTimeout(delayedCheck);
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    setShelfPromptDrafts(normalizeShelfPromptAnswers(shelfSharing.promptAnswers));
  }, [shelfSharing.promptAnswers]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const ownerId = String(params.get("shelf") || "").trim();
    const inviteCode = String(
      params.get("code") || params.get("invite") || "",
    ).trim();
    if (!ownerId) return;
    setSharedRoute({ ownerId, inviteCode, source: "link" });
    setSharedUnlockCode(formatInviteCode(inviteCode));
    setSharedUnlockErr("");
    sharedAutoUnlockKeyRef.current = "";
    setView("sharedprofile");
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.background = T.bg;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', T.bg);
  }, [T.bg]);

  useEffect(() => {
    writeStoredTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!userId) return;
    const booksCol = collection(db, "users", userId, "books");
    const nextCol = collection(db, "users", userId, "nextup");
    const settCol = collection(db, "users", userId, "settings");
    const unsubB = onSnapshot(booksCol, (snap) => {
      setBooks(
        snap.docs
          .map((d) => ({ ...d.data(), uid: d.id }))
          .sort((a, b) => createdAtOrderValue(b) - createdAtOrderValue(a)),
      );
      setLoading(false);
    });
    const unsubN = onSnapshot(nextCol, (snap) => {
      setNextUp(sortNextUpBooks(snap.docs.map((d) => ({ ...d.data(), uid: d.id }))));
    });
    const unsubS = onSnapshot(settCol, (snap) => {
      snap.docs.forEach((d) => {
        const s = d.data();
        if (s.name) setName(s.name);
        if (s.avatar) setAvatar(s.avatar);
        if (s.yearlyGoals) setYearlyGoals(s.yearlyGoals);
        if (s.checkIns) setCheckIns(s.checkIns);
        if (s.showStreak !== undefined) setShowStreak(s.showStreak);
        if (s.showBookRatings !== undefined) {
          setShowBookRatings(Boolean(s.showBookRatings));
        } else if (s.minimalCards !== undefined) {
          setShowBookRatings(!s.minimalCards);
        }
        if (s.theme && THEMES[s.theme]) setTheme(s.theme);
        if (s.dnfLabel) setDnfLabel(s.dnfLabel);
        if (s.shelfSharing) {
          setShelfSharing(normalizeShelfSharingConfig(s.shelfSharing));
        }
      });
    });
    return () => {
      unsubB();
      unsubN();
      unsubS();
    };
  }, [userId]);

  const saveSettings = useCallback(async (patch) => {
    if (!userId) return;
    await setDoc(doc(db, "users", userId, "settings", "prefs"), patch, {
      merge: true,
    });
  }, [userId]);

  function openSettingsHome() {
    setTmpName(name);
    setTmpGoal(yearlyGoals[CUR_YR] || 24);
    setTmpDnf(dnfLabel);
    setView("settings");
  }

  async function upsertCatalogMetadata(bookInput, options = {}) {
    if (!userId || !bookInput) return;

    try {
      const book = sanitizeBookForStorage(bookInput);
      const refs = buildCanonicalRefsFromBook(book);
      if (!refs.workId || !refs.editionId) return;

      const sourceTag = String(options.sourceTag || "app").trim() || "app";
      const providedClaims = Array.isArray(options.sourceClaims)
        ? options.sourceClaims
        : [];
      const claims = providedClaims.length
        ? providedClaims
        : buildDefaultSourceClaimsFromBook(book, sourceTag);

      const valueSupport = new Map();
      for (const claim of claims) {
        const norm = normalizeSearchText(String(claim?.value ?? ""));
        if (!norm) continue;
        const supportKey = `${claim.field || ""}::${norm}`;
        valueSupport.set(supportKey, (valueSupport.get(supportKey) || 0) + 1);
      }

      const workPayload = stripUndefinedFields({
        workId: refs.workId,
        canonicalTitle: book.title || "Unknown Title",
        canonicalTitleNorm: refs.titleKey || null,
        primaryAuthor: refs.firstAuthor || null,
        primaryAuthorNorm: normalizeSearchText(refs.firstAuthor || "") || null,
        latestSeriesId: refs.seriesId || null,
        latestSeriesName: refs.seriesName || null,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });

      const editionPayload = stripUndefinedFields({
        editionId: refs.editionId,
        workId: refs.workId,
        seriesId: refs.seriesId || null,
        isbn10: refs.isbn10 || null,
        isbn13: refs.isbn13 || null,
        title: book.title || "Unknown Title",
        authors: book.authors || "Unknown Author",
        publisher: book.publisher && book.publisher !== "--" ? book.publisher : null,
        year: /^\d{4}$/.test(String(book.year || "")) ? String(book.year) : null,
        publishedDate: book.publishedDate || null,
        pageCount: parsePageCount(book.pages),
        cover: book.cover || null,
        edition: book.edition || null,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });

      const writes = [
        setDoc(doc(db, "catalogWorks", refs.workId), workPayload, { merge: true }),
        setDoc(doc(db, "catalogEditions", refs.editionId), editionPayload, {
          merge: true,
        }),
      ];

      if (refs.seriesId && refs.seriesName) {
        const seriesPayload = stripUndefinedFields({
          seriesId: refs.seriesId,
          seriesName: refs.seriesName,
          seriesNameNorm: normalizeSearchText(refs.seriesName) || null,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        });
        writes.push(
          setDoc(doc(db, "catalogSeries", refs.seriesId), seriesPayload, {
            merge: true,
          }),
        );
      }

      const sourceSnapshot = stripUndefinedFields({
        title: book.title || null,
        authors: book.authors || null,
        seriesName: book.seriesName || null,
        seriesNum: book.seriesNum || null,
        edition: book.edition || null,
        publisher: book.publisher && book.publisher !== "--" ? book.publisher : null,
        publishedDate: book.publishedDate || null,
        year: book.year && book.year !== "--" ? String(book.year) : null,
        pages: parsePageCount(book.pages),
        genres: Array.isArray(book.genres) ? book.genres.slice(0, 8) : [],
      });
      const sourceRecordId = canonicalDocId(
        "src",
        `${refs.editionId}::${sourceTag}::${JSON.stringify(sourceSnapshot)}`,
      );
      writes.push(
        setDoc(
          doc(db, "catalogSourceRecords", sourceRecordId),
          {
            sourceRecordId,
            source: sourceTag,
            workId: refs.workId,
            editionId: refs.editionId,
            seriesId: refs.seriesId || null,
            context: options.context || null,
            snapshot: sourceSnapshot,
            capturedAt: serverTimestamp(),
            capturedBy: userId,
          },
          { merge: true },
        ),
      );

      claims.forEach((claim) => {
        const field = String(claim?.field || "").trim();
        if (!field) return;
        const rawValue = claim?.value;
        if (rawValue === undefined || rawValue === null) return;
        if (typeof rawValue === "string" && !rawValue.trim()) return;

        const entityType =
          claim?.entityType === "series" || claim?.entityType === "work"
            ? claim.entityType
            : "edition";
        const entityId =
          entityType === "work"
            ? refs.workId
            : entityType === "series"
              ? refs.seriesId
              : refs.editionId;
        if (!entityId) return;

        const normalizedValue = normalizeSearchText(String(rawValue));
        const supportKey = `${field}::${normalizedValue}`;
        const support = valueSupport.get(supportKey) || 1;
        const claimSource = String(claim?.source || sourceTag).trim() || sourceTag;
        const confidence = computeClaimConfidence({
          field,
          value: rawValue,
          source: claimSource,
          support,
        });
        const claimId = canonicalDocId(
          "clm",
          `${entityType}::${entityId}::${field}::${claimSource}::${normalizedValue || String(rawValue)}`,
        );

        writes.push(
          setDoc(
            doc(db, "catalogClaims", claimId),
            {
              claimId,
              source: claimSource,
              entityType,
              entityId,
              workId: refs.workId,
              editionId: refs.editionId,
              seriesId: refs.seriesId || null,
              field,
              value: rawValue,
              normalizedValue,
              support,
              confidence,
              observedAt: serverTimestamp(),
              observedBy: userId,
            },
            { merge: true },
          ),
        );
      });

      await Promise.all(writes);
    } catch (err) {
      console.error("catalog upsert failed", err);
    }
  }

  async function persistNextUpOrder(orderedBooks) {
    if (!userId || !orderedBooks.length) return;
    await Promise.all(
      orderedBooks.map((book) =>
        setDoc(
          doc(collection(db, "users", userId, "nextup"), book.uid),
          { queueOrder: Number(book.queueOrder) || 0 },
          { merge: true },
        ),
      ),
    );
  }

  function clearNextUpDragState() {
    setNextUpDragUid("");
    setNextUpDropUid("");
  }

  function reorderNextUpByUid(sourceUid, targetUid) {
    if (!sourceUid || !targetUid || sourceUid === targetUid) return;
    setNextUp((prev) => {
      const from = prev.findIndex((b) => b.uid === sourceUid);
      const to = prev.findIndex((b) => b.uid === targetUid);
      if (from < 0 || to < 0 || from === to) return prev;
      const moved = [...prev];
      const [item] = moved.splice(from, 1);
      moved.splice(to, 0, item);
      const normalized = normalizeNextUpQueueOrder(moved);
      void persistNextUpOrder(normalized);
      return normalized;
    });
  }

  function nextUpUidFromPoint(clientX, clientY) {
    if (typeof document === "undefined") return "";
    const el = document.elementFromPoint(clientX, clientY);
    const row =
      el && typeof el.closest === "function"
        ? el.closest("[data-nextup-uid]")
        : null;
    return row?.getAttribute?.("data-nextup-uid") || "";
  }

  const showToast = useCallback((msg, warn) => {
    setToast({ msg, warn });
    clearTimeout(toastTmr.current);
    toastTmr.current = setTimeout(() => setToast(null), 2500);
  }, []);

  function findMatchingNextUpEntries(book) {
    if (!book) return [];
    const targetKey = normalizeText((book.title || "") + "::" + (book.authors || ""));
    return nextUp.filter((queued) => {
      const queuedKey = normalizeText((queued.title || "") + "::" + (queued.authors || ""));
      return queued.uid === book.uid || (!!targetKey && queuedKey === targetKey);
    });
  }

  async function removeMatchingNextUpEntries(book) {
    const matches = findMatchingNextUpEntries(book);
    if (!matches.length) return;

    const removeIds = new Set(matches.map((item) => item.uid));
    setNextUp((prev) => prev.filter((item) => !removeIds.has(item.uid)));

    if (userId) {
      await Promise.all(
        matches.map((item) =>
          deleteDoc(doc(collection(db, "users", userId, "nextup"), item.uid)).catch(() => null),
        ),
      );
    }
  }

  function openFinishPrompt(book) {
    if (!book) return;
    setFinishPromptRating(Number(book.rating || 0));
    setFinishPromptFav(!!book.fav);
    setFinishPromptNotes(String(book.notes || ""));
    setFinishPromptOpen(true);
  }

  function closeFinishPrompt(force = false) {
    if (finishPromptBusy && !force) return;
    setFinishPromptOpen(false);
  }

  function refreshToLatestVersion() {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("appv", String(Date.now()));
    window.location.replace(url.toString());
  }

  function buildSharedShelfProfilePayload(configOverride) {
    const cfg = normalizeShelfSharingConfig(configOverride || shelfSharing);
    const readCount = books.filter(hasCompletedReadHistory).length;
    return {
      ownerId: userId,
      displayName: (name || "").trim() || "Reader",
      avatar: avatar || "📚",
      theme,
      enabled: cfg.mode === "invite-only",
      mode: cfg.mode,
      bio: cfg.bio || "",
      topGenres: cfg.topGenres || [],
      featuredCurrentReads: cfg.featuredCurrentReads || [],
      promptAnswers: cfg.promptAnswers || [],
      recommendedBookIds: cfg.recommendedBookIds || [],
      showFullShelf: cfg.showFullShelf !== false,
      fullShelfLayout: cfg.fullShelfLayout === "continuous" ? "continuous" : "sectioned",
      showReadCount: cfg.showReadCount !== false,
      showRatings: cfg.showRatings !== false,
      showReading: cfg.showReading !== false,
      showRead: cfg.showRead !== false,
      showUnread: !!cfg.showUnread,
      showDnf: !!cfg.showDnf,
      totalBooks: books.length,
      readCount,
      dnfLabel: dnfLabel || "DNF",
      memberSinceYear: computeMemberSinceYear(auth.currentUser),
      readingSinceYear: computeReadingSinceYear(books),
      updatedAt: serverTimestamp(),
    };
  }

  function updateShelfSharing(mutator, persist = true) {
    setShelfSharing((prev) => {
      const draft =
        typeof mutator === "function" ? mutator(prev) : mutator || prev;
      const next = normalizeShelfSharingConfig(draft);
      if (persist) {
        void saveSettings({ shelfSharing: next });
        if (userId) {
          void setDoc(
            doc(db, "sharedShelves", userId),
            buildSharedShelfProfilePayload(next),
            { merge: true },
          );
        }
      }
      return next;
    });
  }

  function buildShelfInviteLink(ownerIdValue, inviteCodeValue) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!origin || !ownerIdValue) return "";
    const code = formatInviteCode(inviteCodeValue);
    const base = `${origin}?shelf=${encodeURIComponent(ownerIdValue)}`;
    return code ? `${base}&code=${encodeURIComponent(code)}` : base;
  }

  const syncShelfInviteCodeSecret = useCallback(async (codeValue) => {
    if (!userId) return;
    const normalizedCode = normalizeInviteCode(codeValue);
    if (!normalizedCode) return;
    await setDoc(
      doc(db, "sharedShelves", userId, "private", "invite"),
      {
        code: normalizedCode,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }, [userId]);

  async function regenerateShelfInviteCode() {
    if (!userId) return;
    setShareCodeBusy(true);
    try {
      const inviteCode = makeInviteCode();
      updateShelfSharing((prev) => ({ ...prev, inviteCode }));
      await syncShelfInviteCodeSecret(inviteCode);
      showToast("Invite code refreshed");
    } catch (e) {
      showToast("Could not refresh invite code", true);
    } finally {
      setShareCodeBusy(false);
    }
  }

  async function copyShelfInviteCode() {
    const inviteCode = formatInviteCode(shelfSharing.inviteCode);
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      showToast("Invite code copied");
    } catch (e) {
      showToast("Could not copy invite code", true);
    }
  }

  async function copyShelfInviteLink() {
    if (!userId) return;
    const link = buildShelfInviteLink(userId, shelfSharing.inviteCode);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Invite link copied");
    } catch (e) {
      showToast("Could not copy invite link", true);
    }
  }

  async function shareShelfInviteLink() {
    if (!userId) return;
    const inviteCode = formatInviteCode(shelfSharing.inviteCode);
    const link = buildShelfInviteLink(userId, inviteCode);
    if (!link) return;
    const displayName = (name || "").trim() || "Reader";
    const shareText =
      `${displayName} invited you to view a private bookshelf.\n\n` +
      `Open this link:\n${link}\n\n` +
      (inviteCode
        ? `Invite code: ${inviteCode}`
        : "If prompted, enter the invite code provided by the owner.");
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${displayName}'s Private Shelf`,
          text: shareText,
          url: link,
        });
        showToast("Share sheet opened");
        return;
      }
      await navigator.clipboard.writeText(shareText);
      showToast("Invite text copied");
    } catch (e) {
      if (e?.name === "AbortError") return;
      showToast("Could not share invite", true);
    }
  }

  function openSharedProfilePreview(returnView = "settings-private") {
    if (!userId) return;
    setSharedRoute({
      ownerId: userId,
      inviteCode: "",
      source: "preview",
      returnView,
    });
    setSharedUnlockCode("");
    setSharedUnlockErr("");
    sharedAutoUnlockKeyRef.current = "";
    setView("sharedprofile");
  }

  const unlockSharedShelfWithCode = useCallback(async (codeValue, opts = {}) => {
    const ownerId = sharedRoute.ownerId;
    if (!userId || !ownerId || ownerId === userId) return false;
    const normalizedCode = normalizeInviteCode(codeValue || sharedUnlockCode);
    if (normalizedCode.length < 4) {
      setSharedUnlockErr("Enter a valid invite code.");
      return false;
    }

    setSharedUnlockBusy(true);
    if (!opts.keepError) setSharedUnlockErr("");
    try {
      await setDoc(
        doc(db, "sharedShelves", ownerId, "access", userId),
        {
          viewerUid: userId,
          via: "invite-code",
          inviteCode: normalizedCode,
          grantedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setSharedUnlockErr("");
      setSharedRemoteError("");
      setSharedRemoteLoading(true);
      setSharedAccessNonce((n) => n + 1);
      if (!opts.silentSuccess) showToast("Bookshelf unlocked");
      return true;
    } catch (e) {
      if (e?.code === "permission-denied") {
        setSharedUnlockErr("That invite code is not valid for this shelf.");
      } else {
        setSharedUnlockErr("Could not unlock right now. Try again.");
      }
      if (!opts.silentError) showToast("Could not unlock shelf", true);
      return false;
    } finally {
      setSharedUnlockBusy(false);
    }
  }, [sharedRoute.ownerId, userId, sharedUnlockCode, showToast]);

  async function revokeAllSharedShelfAccess() {
    if (!userId) return;
    setShareRevokeBusy(true);
    try {
      const accessCol = collection(db, "sharedShelves", userId, "access");
      const snap = await getDocs(accessCol);
      for (const d of snap.docs) {
        await deleteDoc(d.ref);
      }
      showToast("All viewer access revoked");
    } catch (e) {
      showToast("Could not revoke viewer access", true);
    } finally {
      setShareRevokeBusy(false);
    }
  }

  useEffect(() => {
    if (!userId) return;
    if (shelfSharing.mode !== "invite-only") return;
    if (formatInviteCode(shelfSharing.inviteCode)) return;

    const inviteCode = makeInviteCode();
    setShelfSharing((prev) => {
      const next = normalizeShelfSharingConfig({ ...prev, inviteCode });
      void saveSettings({ shelfSharing: next });
      return next;
    });
    void syncShelfInviteCodeSecret(inviteCode);
  }, [userId, shelfSharing.mode, shelfSharing.inviteCode, saveSettings, syncShelfInviteCodeSecret]);

  async function syncSharedShelfMirror(configOverride) {
    if (!userId) return;
    const cfg = normalizeShelfSharingConfig(configOverride || shelfSharing);
    setShareSyncBusy(true);
    try {
      await setDoc(
        doc(db, "sharedShelves", userId),
        buildSharedShelfProfilePayload(cfg),
        { merge: true },
      );

      const sharedBooksCol = collection(db, "sharedShelves", userId, "books");
      const desiredIds = new Set();

      for (const b of books) {
        if (!b?.uid) continue;
        if (!isSharedShelfStatusVisible(b.status || "Unstarted", cfg, dnfLabel)) continue;
        desiredIds.add(b.uid);
        const payload = {
          uid: b.uid,
          title: b.title || "",
          authors: b.authors || "",
          cover: b.cover || null,
          status: b.status || "Unstarted",
          formats: b.formats || [],
          genres: b.genres || [],
          vibes: b.vibes || [],
          tags: b.tags || [],
          rating: Number(b.rating) || 0,
          pages: b.pages ?? "--",
          publisher: b.publisher || "--",
          publishedDate: b.publishedDate || "",
          year: b.year || "",
          edition: b.edition || "",
          seriesName: b.seriesName || "",
          seriesNum: b.seriesNum || "",
          desc: b.desc || "",
          createdAt: b.createdAt || Date.now(),
          updatedAt: serverTimestamp(),
        };
        await setDoc(doc(sharedBooksCol, b.uid), payload);
      }

      const existingSharedBooks = await getDocs(sharedBooksCol);
      for (const d of existingSharedBooks.docs) {
        if (!desiredIds.has(d.id)) {
          await deleteDoc(d.ref);
        }
      }

      showToast("Shared shelf refreshed");
    } catch (e) {
      showToast("Could not refresh shared shelf", true);
    } finally {
      setShareSyncBusy(false);
    }
  }

  useEffect(() => {
    if (!sharedRoute.ownerId || !userId || sharedRoute.ownerId === userId) {
      setSharedProfileRemote(null);
      setSharedBooksRemote([]);
      setSharedRemoteLoading(false);
      setSharedRemoteError("");
      return;
    }

    setSharedRemoteLoading(true);
    setSharedRemoteError("");

    const profileRef = doc(db, "sharedShelves", sharedRoute.ownerId);
    const booksCol = collection(db, "sharedShelves", sharedRoute.ownerId, "books");

    const unsubProfile = onSnapshot(
      profileRef,
      (snap) => {
        setSharedProfileRemote(
          snap.exists() ? { ...snap.data(), ownerId: sharedRoute.ownerId } : null,
        );
        setSharedRemoteLoading(false);
      },
      (err) => {
        setSharedProfileRemote(null);
        setSharedRemoteError(err?.code || "permission-denied");
        setSharedRemoteLoading(false);
      },
    );

    const unsubBooks = onSnapshot(
      booksCol,
      (snap) => {
        setSharedBooksRemote(
          snap.docs
            .map((d) => ({ ...d.data(), uid: d.id }))
            .sort((a, b) => createdAtOrderValue(b) - createdAtOrderValue(a)),
        );
      },
      (err) => {
        setSharedBooksRemote([]);
        setSharedRemoteError(err?.code || "permission-denied");
      },
    );

    return () => {
      unsubProfile();
      unsubBooks();
    };
  }, [sharedRoute.ownerId, userId, sharedAccessNonce]);

  useEffect(() => {
    if (!userId) return;
    if (sharedRoute.source !== "link") return;
    if (!sharedRoute.ownerId || sharedRoute.ownerId === userId) return;
    const normalizedCode = normalizeInviteCode(sharedRoute.inviteCode);
    if (!normalizedCode) return;
    const key = [sharedRoute.ownerId, userId, normalizedCode].join(":");
    if (sharedAutoUnlockKeyRef.current === key) return;
    sharedAutoUnlockKeyRef.current = key;
    setSharedUnlockCode(formatInviteCode(normalizedCode));
    void unlockSharedShelfWithCode(normalizedCode, {
      silentSuccess: true,
      silentError: true,
      keepError: true,
    });
  }, [sharedRoute.ownerId, sharedRoute.inviteCode, sharedRoute.source, userId, unlockSharedShelfWithCode]);

  function openResetSheet(scope) {
    setResetScope(scope);
    setResetConfirmText("");
  }

  function closeResetSheet() {
    if (resetBusy) return;
    setResetScope(null);
    setResetConfirmText("");
  }

  async function clearUserCollection(path, items) {
    if (!userId) return;
    for (const item of items) {
      if (!item?.uid) continue;
      await deleteDoc(doc(collection(db, "users", userId, path), item.uid));
    }
  }

  async function confirmResetCollection() {
    if (!resetScope || resetBusy) return;
    const needsPhrase = resetScope === "books" || resetScope === "all";
    if (needsPhrase && resetConfirmText.trim().toUpperCase() !== "RESET") {
      return;
    }

    const clearBooks = resetScope === "books" || resetScope === "all";
    const clearNextUp = resetScope === "nextup" || resetScope === "all";
    setResetBusy(true);

    try {
      if (userId) {
        if (clearBooks) await clearUserCollection("books", books);
        if (clearNextUp) await clearUserCollection("nextup", nextUp);
      }
      if (clearBooks) setBooks([]);
      if (clearNextUp) setNextUp([]);

      if (resetScope === "nextup") showToast("Next Up cleared");
      if (resetScope === "books") showToast("My Books cleared");
      if (resetScope === "all") showToast("Library reset");

      setResetScope(null);
      setResetConfirmText("");
    } catch (e) {
      showToast("Could not reset collection", true);
    } finally {
      setResetBusy(false);
    }
  }

  function openImportView(returnToView) {
    setImportReturnView(returnToView || view);
    setImportSource(null);
    setImportDraft(null);
    setImportErr("");
    setImportReport(null);
    setImportEnrichBusy(false);
    setImportEnrichProgress({ done: 0, total: 0, updated: 0 });
    setView("import");
  }

  function closeImportView() {
    if (importBusy || importEnrichBusy) return;
    setView(importReturnView || "settings");
  }

  function triggerImportFilePicker() {
    if (importBusy || importEnrichBusy) return;
    importFileRef.current?.click();
  }

  async function handleImportFilePicked(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportErr("");
    setImportReport(null);
    setImportEnrichProgress({ done: 0, total: 0, updated: 0 });

    try {
      const text = await file.text();
      if (!text.trim()) {
        setImportSource(null);
        setImportDraft(null);
        setImportErr("This file is empty. Please choose a CSV export with books.");
      } else {
        setImportSource({ fileName: file.name, text });
      }
    } catch (err) {
      setImportSource(null);
      setImportDraft(null);
      setImportErr("Could not read this file. Please export again and retry.");
    } finally {
      e.target.value = "";
    }
  }

  async function applyImportDraft() {
    if (!importDraft || importBusy || importEnrichBusy) return;
    setImportBusy(true);
    setImportErr("");
    setImportEnrichProgress({ done: 0, total: 0, updated: 0 });

    const existingBookKeys = new Set(books.flatMap((b) => getBookIdentityKeys(b)));
    const existingNextUpKeys = new Set(nextUp.flatMap((b) => getBookIdentityKeys(b)));
    const sessionBookKeys = new Set();
    const sessionNextUpKeys = new Set();
    const localNewBooks = [];
    const localNewNextUp = [];
    const importedEntries = [];
    let importedBooks = 0;
    let importedNextUp = 0;
    let skippedDuplicatesCount = 0;
    let failedCount = 0;

    for (const item of importDraft.candidates) {
      const target = item.target === "nextup" ? "nextup" : "books";
      const targetKeys = target === "nextup" ? existingNextUpKeys : existingBookKeys;
      const sessionKeys = target === "nextup" ? sessionNextUpKeys : sessionBookKeys;
      const isDuplicate =
        importSkipDuplicates &&
        item.identityKeys.some((key) => targetKeys.has(key) || sessionKeys.has(key));

      if (isDuplicate) {
        skippedDuplicatesCount++;
        continue;
      }

      const uid = mkid();
      const payload = {
        ...item.book,
        uid,
        createdAt: Date.now(),
      };

      try {
        if (userId) {
          await setDoc(
            doc(collection(db, "users", userId, target), uid),
            { ...payload, createdAtServer: serverTimestamp() },
            { merge: true },
          );
        } else if (target === "nextup") {
          localNewNextUp.push(payload);
        } else {
          localNewBooks.push(payload);
        }

        for (const key of item.identityKeys) {
          targetKeys.add(key);
          sessionKeys.add(key);
        }

        importedEntries.push({
          uid,
          target,
          title: payload.title || "",
          authors: payload.authors || "",
          isbn13: payload.isbn13 || payload.isbn || "",
          isbn10: payload.isbn10 || "",
        });

        if (target === "nextup") importedNextUp++;
        else importedBooks++;
      } catch (err) {
        failedCount++;
      }
    }

    if (!userId) {
      if (localNewBooks.length) setBooks((prev) => [...localNewBooks, ...prev]);
      if (localNewNextUp.length) setNextUp((prev) => [...localNewNextUp, ...prev]);
    }

    const importedTotal = importedBooks + importedNextUp;
    const report = {
      fileName: importDraft.fileName || "CSV",
      importedTotal,
      importedBooks,
      importedNextUp,
      skippedDuplicates: skippedDuplicatesCount,
      failedCount,
      importedEntries,
      processedAt: new Date().toISOString(),
    };
    setImportReport(report);
    setImportBusy(false);

    if (importedTotal > 0) {
      showToast(`Imported ${importedTotal} book${importedTotal === 1 ? "" : "s"}`);
    } else if (skippedDuplicatesCount > 0) {
      showToast("No new books to import");
    } else if (failedCount > 0) {
      showToast("Import failed", true);
    } else {
      showToast("Nothing imported", true);
    }
  }

  async function enrichLastImportMetadata() {
    const entries = importReport?.importedEntries || [];
    if (!entries.length || importBusy || importEnrichBusy) return;

    setImportEnrichBusy(true);
    setImportErr("");
    setImportEnrichProgress({ done: 0, total: entries.length, updated: 0 });
    let updatedCount = 0;
    let failedCount = 0;

    try {
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const sourceList = entry.target === "nextup" ? nextUp : books;
        const currentBook = sourceList.find((b) => b.uid === entry.uid);

        if (!currentBook || !needsImportEnrichment(currentBook)) {
          setImportEnrichProgress({
            done: i + 1,
            total: entries.length,
            updated: updatedCount,
          });
          continue;
        }

        let found = null;
        const isbnCandidate = normalizeIsbn(
          entry.isbn13 ||
            entry.isbn10 ||
            currentBook.isbn13 ||
            currentBook.isbn10 ||
            currentBook.isbn ||
            "",
        );
        if (isbnCandidate) found = await searchByIsbn(isbnCandidate);

        if (!found) {
          const firstAuthor = String(currentBook.authors || "")
            .split(",")[0]
            .trim();
          const query = firstAuthor
            ? `${currentBook.title} by ${firstAuthor}`
            : currentBook.title;
          const results = await searchBooks(query);
          found = results[0] || null;
        }

        const patch = buildImportEnrichmentPatch(currentBook, found);
        const hasPatch = Object.keys(patch).length > 0;
        if (!hasPatch) {
          setImportEnrichProgress({
            done: i + 1,
            total: entries.length,
            updated: updatedCount,
          });
          continue;
        }

        try {
          if (userId) {
            await setDoc(
              doc(collection(db, "users", userId, entry.target), entry.uid),
              patch,
              { merge: true },
            );
          } else if (entry.target === "nextup") {
            setNextUp((prev) =>
              prev.map((b) => (b.uid === entry.uid ? { ...b, ...patch } : b)),
            );
          } else {
            setBooks((prev) =>
              prev.map((b) => (b.uid === entry.uid ? { ...b, ...patch } : b)),
            );
          }
          updatedCount++;
        } catch (err) {
          failedCount++;
        }

        setImportEnrichProgress({
          done: i + 1,
          total: entries.length,
          updated: updatedCount,
        });
      }

      setImportReport((prev) =>
        prev
          ? {
              ...prev,
              enrichUpdated: updatedCount,
              enrichFailed: failedCount,
              enrichCompletedAt: new Date().toISOString(),
            }
          : prev,
      );

      if (updatedCount > 0) {
        showToast(`Updated ${updatedCount} book${updatedCount === 1 ? "" : "s"}`);
      } else if (failedCount > 0) {
        showToast("Metadata refresh finished with errors", true);
      } else {
        showToast("No additional metadata found");
      }
    } finally {
      setImportEnrichBusy(false);
    }
  }

  async function repairLibraryCovers() {
    if (coverRepairBusy) return;

    const targets = [
      ...books.map((book) => ({ book, collectionName: "books" })),
      ...nextUp.map((book) => ({ book, collectionName: "nextup" })),
    ].filter(({ book }) => bookNeedsCoverRepair(book));

    setCoverRepairBusy(true);
    setCoverRepairReport(null);
    setCoverRepairProgress({ done: 0, total: targets.length, updated: 0 });

    let coversUpdated = 0;
    let isbnsRecovered = 0;
    let unresolved = 0;
    const resolvedCache = new Map();

    const resolveBook = async (book) => {
      const knownIsbn = preferredBookIsbn(book);
      const firstAuthor = firstAuthorFromText(book.authors || "");
      const lookupKey = knownIsbn
        ? `isbn:${knownIsbn}`
        : `book:${normalizeSearchText(book.title || "")}::${normalizeSearchText(firstAuthor)}`;
      if (resolvedCache.has(lookupKey)) return resolvedCache.get(lookupKey);

      let resolved = null;
      if (knownIsbn) {
        resolved = await searchByIsbn(knownIsbn);
      } else if (book.title) {
        const query = firstAuthor
          ? `${book.title} by ${firstAuthor}`
          : String(book.title);
        const matches = await searchBooks(query);
        const isbnMatch = matches.find((match) => preferredBookIsbn(match));
        const recoveredIsbn = preferredBookIsbn(isbnMatch);
        if (recoveredIsbn) resolved = await searchByIsbn(recoveredIsbn);
      }

      resolvedCache.set(lookupKey, resolved);
      return resolved;
    };

    try {
      for (let i = 0; i < targets.length; i++) {
        const { book, collectionName } = targets[i];
        const patch = {};
        try {
          const resolved = await resolveBook(book);
          const replacementCover = normalizeCoverUrl(resolved?.cover || "");
          const replacementIsbn10 =
            normalizeIsbn(resolved?.isbn10 || resolved?._isbn10 || "") || "";
          const replacementIsbn13 =
            normalizeIsbn(resolved?.isbn13 || resolved?._isbn13 || "") || "";

          if (
            replacementCover &&
            !isGoogleHostedCoverUrl(replacementCover) &&
            replacementCover !== normalizeCoverUrl(book.cover || "")
          ) {
            patch.cover = replacementCover;
            patch.coverSource = String(resolved?.metaSource || "isbn_lookup_repair");
          }
          if (replacementIsbn10 && replacementIsbn10 !== normalizeIsbn(book.isbn10 || "")) {
            patch.isbn10 = replacementIsbn10;
          }
          if (replacementIsbn13 && replacementIsbn13 !== normalizeIsbn(book.isbn13 || "")) {
            patch.isbn13 = replacementIsbn13;
          }
          const recoveredMissingIsbn =
            !!(patch.isbn10 || patch.isbn13) && !preferredBookIsbn(book);
          if (Object.keys(patch).length) {
            if (userId) {
              await setDoc(
                doc(db, "users", userId, collectionName, book.uid),
                patch,
                { merge: true },
              );
            }
            const updateList = (list) =>
              list.map((entry) =>
                entry.uid === book.uid ? { ...entry, ...patch } : entry,
              );
            if (collectionName === "nextup") setNextUp(updateList);
            else setBooks(updateList);
          }
          if (patch.cover) coversUpdated += 1;
          else unresolved += 1;
          if (recoveredMissingIsbn) isbnsRecovered += 1;
        } catch {
          unresolved += 1;
        }

        setCoverRepairProgress({
          done: i + 1,
          total: targets.length,
          updated: coversUpdated,
        });
      }

      const report = {
        checked: targets.length,
        coversUpdated,
        isbnsRecovered,
        unresolved,
        ranAt: new Date().toISOString(),
      };
      setCoverRepairReport(report);
      if (coversUpdated > 0) {
        showToast(
          `Restored ${coversUpdated} cover${coversUpdated === 1 ? "" : "s"}`,
        );
      } else if (targets.length === 0) {
        showToast("No covers need repair");
      } else {
        showToast("No replacement covers were found", true);
      }
    } finally {
      setCoverRepairBusy(false);
    }
  }

  useEffect(() => {
    if (!importSource?.text) {
      setImportDraft(null);
      return;
    }
    const nextDraft = buildImportDraft(importSource.text, {
      dnfLabel,
      routeQueueToNextUp: importRouteQueueToNextUp,
    });
    if (nextDraft.error) {
      setImportDraft(null);
      setImportErr(nextDraft.error);
      return;
    }
    setImportErr("");
    setImportDraft({
      ...nextDraft,
      fileName: importSource.fileName || "CSV",
    });
  }, [importSource, importRouteQueueToNextUp, dnfLabel]);

  const currentYearGoal = yearlyGoals[CUR_YR] || 0;
  const currentYearFinishedCount = books.filter(
    (b) => hasCompletedReadHistory(b) && inYear(b.dateRead, CUR_YR),
  ).length;

  useEffect(() => {
    if (!currentYearGoal || !currentYearFinishedCount) return;
    const pct = Math.round((currentYearFinishedCount / currentYearGoal) * 100);
    const milestones = {
      25: "🌱 Just getting started!",
      50: "🔥 Great momentum!",
      75: "⭐ More than halfway!",
      100: "🏆 Goal crushed!",
    };
    if (milestones[pct]) showToast(milestones[pct]);
  }, [currentYearFinishedCount, currentYearGoal, showToast]);

  async function runSearchNow(queryOverride, modeOverride) {
    const q = String(queryOverride ?? sq).trim();
    const mode = modeOverride || searchMode;
    const canSearch =
      mode === "isbn" ? !!extractIsbnFromText(q) : shouldRunSearch(q);

    searchReqRef.current += 1;
    const reqId = searchReqRef.current;

    if (!q || !canSearch) {
      setResults([]);
      setSrching(false);
      setSearchHasRun(false);
      return;
    }

    setSrching(true);
    setSearchHasRun(true);
    try {
      const found = await searchBooks(q, { mode });
      if (reqId !== searchReqRef.current) return;
      setResults(found);
    } finally {
      if (reqId === searchReqRef.current) setSrching(false);
    }
  }

  useEffect(() => {
    if (sq.trim()) return;
    searchReqRef.current += 1;
    setResults([]);
    setSrching(false);
    setSearchHasRun(false);
  }, [sq]);
  function scrollAllToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    [
      myBooksScrollRef.current,
      nextUpScrollRef.current,
      searchScrollRef.current,
      detailViewScrollRef.current,
      detailEditScrollRef.current,
      privateShelfScrollRef.current,
      sharedProfileScrollRef.current,
      sharedBookshelfScrollRef.current,
    ]
      .filter(Boolean)
      .forEach((el) => {
        if (typeof el.scrollTo === "function") {
          el.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
  }
  useEffect(() => {
    requestAnimationFrame(() => {
      scrollAllToTop();
    });
  }, [view]);
  useEffect(() => {
    if (view !== "search") return;
    searchScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [sq, view]);
  useEffect(() => {
    if (view === "nextup") return;
    setNextUpDragUid("");
    setNextUpDropUid("");
  }, [view]);
  useEffect(() => {
    if (view !== "mybooks") return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      myBooksScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [view, collectionSq, fStatus, fGenre, fFmt, fFav, sortBy, gridMode]);
  useEffect(() => {
    if (view !== "detail") return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (detailTab === "edit") {
        detailEditScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else {
        detailViewScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    });
  }, [view, detailTab, active?.uid]);
  useEffect(() => {
    setShowScrollTop(false);
  }, [view]);
  useEffect(() => {
    if (
      !["mybooks", "settings-private", "sharedprofile", "sharedbookshelf"].includes(
        view,
      )
    ) {
      return undefined;
    }
    const onWindowScroll = () => {
      handleScrollTopVisibility(
        window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0,
      );
    };
    onWindowScroll();
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, [view]);
  function handleScrollTopVisibility(scrollTop) {
    setShowScrollTop(scrollTop > 280);
  }
  function scrollActivePanelToTop() {
    const activeRef =
      view === "mybooks"
        ? myBooksScrollRef.current
        : view === "settings-private"
          ? privateShelfScrollRef.current
          : view === "sharedprofile"
            ? sharedProfileScrollRef.current
            : view === "sharedbookshelf"
              ? sharedBookshelfScrollRef.current
              : null;
    if (activeRef && typeof activeRef.scrollTo === "function") {
      activeRef.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }
  const streak = currentStreakFromDates(checkIns);

  function logReadToday(bookUid, e) {
    e.stopPropagation();
    const t = TODAY();
    const book = books.find((b) => b.uid === bookUid);
    if (!book) return;
    const bookCheckIns = book.checkIns || [];
    if (bookCheckIns.includes(t)) {
      showToast("Already logged for this book today!");
      return;
    }
    const updatedBookCheckIns = [...bookCheckIns, t];
    const updatedBook = {
      ...book,
      checkIns: updatedBookCheckIns,
      dateStarted: book.dateStarted || t,
    };
    setBooks((p) => p.map((b) => (b.uid === bookUid ? updatedBook : b)));
    if (userId) {
      const col = collection(db, "users", userId, "books");
      setDoc(
        doc(col, bookUid),
        { checkIns: updatedBookCheckIns, dateStarted: updatedBook.dateStarted },
        { merge: true },
      );
    }
    const globalUpdated = [...new Set([...checkIns, t])];
    setCheckIns(globalUpdated);
    saveSettings({ checkIns: globalUpdated });
    const nextStreak = currentStreakFromDates(globalUpdated, t);
    showToast("Logged! " + nextStreak + " day streak \uD83D\uDD25");
  }
  function hasLoggedToday(book) {
    const t = TODAY();
    return Array.isArray(book?.checkIns) && book.checkIns.includes(t);
  }

  async function startScan() {
    setScanErr("");
    setScanOn(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (vidRef.current) {
        vidRef.current.srcObject = stream;
        await vidRef.current.play();
      }
      setScanOn(true);
      setTimeout(() => initQuagga(), 800);
    } catch (e) {
      setScanErr(
        "Camera access denied. Please allow camera in your browser settings.",
      );
    }
  }

  function stopScan() {
    if (window._scanInterval) {
      clearInterval(window._scanInterval);
      window._scanInterval = null;
    }
    if (streamRef.current)
      streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (vidRef.current) vidRef.current.srcObject = null;
    setScanOn(false);
    setScanErr("");
    try {
      window._zxingReader && window._zxingReader.reset();
    } catch (e) {}
  }

  function initQuagga() {
    if (!vidRef.current || !streamRef.current) return;
    import("@zxing/browser").then(({ BrowserMultiFormatReader }) => {
      const codeReader = new BrowserMultiFormatReader();
      window._zxingReader = codeReader;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let scanning = true;

      async function scanFrame() {
        if (!scanning || !vidRef.current || vidRef.current.readyState < 2)
          return;
        const vid = vidRef.current;
        canvas.width = vid.videoWidth;
        canvas.height = vid.videoHeight;
        ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
        try {
          const result = await codeReader.decodeFromCanvas(canvas);
          if (result) {
            scanning = false;
            clearInterval(window._scanInterval);
            window._scanInterval = null;
            const code = result.getText();
            stopScan();
            setSrching(true);
            showToast("Barcode detected! Searching...");
            const book = await searchByIsbn(code);
            setSrching(false);
            if (book) {
              openAdd(book);
            } else {
              const r = await searchBooks("isbn:" + code);
              if (r.length) openAdd(r[0]);
              else setScanErr("Book not found. Try searching manually.");
            }
          }
        } catch (e) {
          /* no result this frame, keep trying */
        }
      }

      window._scanInterval = setInterval(scanFrame, 400);
    });
  }
  async function simScan() {
    stopScan();
    setSrching(true);
    const r = await searchBooks("The Name of the Wind Patrick Rothfuss");
    setSrching(false);
    if (r.length) openAdd(r[0]);
    showToast("Barcode scanned");
  }

  async function openAdd(book) {
    if (!book) return;
    let resolvedBook = book;
    const isbn = preferredBookIsbn(book);
    const shouldEnrichWithIsbnDb =
      !!isbn && String(book.metaSource || "") === "google_books_search";

    if (shouldEnrichWithIsbnDb) {
      setSrching(true);
      try {
        const enriched = await searchByIsbn(isbn);
        resolvedBook = mergeSelectedBookMetadata(book, enriched);
      } catch {
        resolvedBook = book;
      } finally {
        setSrching(false);
      }
    }

    const lane = inferLaneFromSelectedGenres(
      resolvedBook.genres || [],
      resolvedBook.genreLane || "fiction",
    );
    setPending(resolvedBook);
    setATitle(normalizeEditableBookTitle(resolvedBook.title));
    setAFmts(["Physical"]);
    setAStat("Unstarted");
    setAGenreLane(lane);
    setAGenres(restrictGenresToLane(resolvedBook.genres || [], lane));
    setAVibes(resolvedBook.vibes || []);
    setATags([]);
    setARating(0);
    setAFav(false);
    setANotes("");
    setASeriesName(resolvedBook.seriesName || "");
    setASeriesNum(resolvedBook.seriesNum || "");
    setAEdition(resolvedBook.edition || "");
    setAStartDate("");
    setAEndDate("");
    setView("add");
  }

  async function confirmAdd() {
    if (!pending) return;
    const normalizedAddTitle = normalizeEditableBookTitle(aTitle);
    if (!normalizedAddTitle) {
      showToast("Book title is required", true);
      return;
    }
    const resolvedAddStatus =
      aEndDate && aStat !== dnfLabel && aStat !== "DNF" ? "Read" : aStat;
    const pendingSourceTag =
      String(pending.metaSource || "").trim() ||
      (extractIsbnFromText(pending.id || "") ? "isbn_lookup" : "app_manual_add");
    const pendingSourceClaims = Array.isArray(pending._sourceClaims)
      ? pending._sourceClaims
      : [];
    const pendingClean = sanitizeBookForStorage(pending);
    const newId = mkid();
    const b = {
      ...pendingClean,
      title: normalizedAddTitle,
      uid: newId,
      formats: aFmts,
      status: resolvedAddStatus,
      genreLane: aGenreLane,
      genres: restrictGenresToLane(aGenres, aGenreLane),
      vibes: aVibes,
      tags: aTags,
      rating: aRating,
      fav: aFav,
      notes: String(aNotes || "").trim(),
      seriesName: aSeriesName,
      seriesNum: aSeriesNum,
      edition: aEdition,
      dateStarted: aStartDate || null,
      dateRead:
        resolvedAddStatus === "Read" || resolvedAddStatus === dnfLabel
          ? aEndDate || null
          : null,
      reads: [],
      createdAt: Date.now(),
    };
    setPending(null);
    setView(addTarget === "nextup" ? "nextup" : "mybooks");
    setSq("");
    setResults([]);
    showToast('"' + b.title + '" added');
    if (userId) {
      const col =
        addTarget === "nextup"
          ? collection(db, "users", userId, "nextup")
          : collection(db, "users", userId, "books");
      const payload = { ...b, createdAtServer: serverTimestamp() };
      if (addTarget === "nextup") {
        payload.queueOrder = nextUpHighestOrder(nextUp) + 1;
      }
      await setDoc(doc(col, newId), payload);
      await upsertCatalogMetadata(b, {
        sourceTag: pendingSourceTag,
        sourceClaims: pendingSourceClaims,
        context: addTarget === "nextup" ? "add_nextup" : "add_library",
      });
    } else {
      if (addTarget === "nextup") {
        setNextUp((p) => [
          { ...b, queueOrder: nextUpHighestOrder(p) + 1 },
          ...p,
        ]);
      }
      else setBooks((p) => [b, ...p]);
    }
  }

  async function addFromLibraryToNextUp(book) {
    if (!book) return;
    const key = normalizeText((book.title || "") + "::" + (book.authors || ""));
    const alreadyQueued = nextUp.some((q) => {
      const qKey = normalizeText((q.title || "") + "::" + (q.authors || ""));
      return q.uid === book.uid || qKey === key;
    });
    if (alreadyQueued) {
      showToast("Already in Next Up");
      return;
    }

    const queueId = book.uid || mkid();
    const nextOrder = nextUpHighestOrder(nextUp) + 1;
    const queuedBook = {
      ...book,
      uid: queueId,
      createdAt: Date.now(),
      queueOrder: nextOrder,
    };

    setNextUp((p) => [queuedBook, ...p.filter((b) => b.uid !== queueId)]);

    if (userId) {
      await setDoc(
        doc(collection(db, "users", userId, "nextup"), queueId),
        {
          ...book,
          uid: queueId,
          createdAt: queuedBook.createdAt,
          createdAtServer: serverTimestamp(),
          queueOrder: nextOrder,
        },
        { merge: true },
      );
    }

    showToast('"' + (book.title || "Book") + '" added to Next Up');
  }

  function openMetadataReport(bookOverride) {
    const book = bookOverride || active;
    if (!book) return;
    setMetadataReportBook({
      uid: book.uid || "",
      title: book.title || "",
      authors: book.authors || "",
      isbn: normalizeIsbn(book.isbn || "") || "",
      isbn10: normalizeIsbn(book.isbn10 || book._isbn10 || "") || "",
      isbn13: normalizeIsbn(book.isbn13 || book._isbn13 || "") || "",
      cover: normalizeCoverUrl(book.cover || "") || "",
      seriesName: book.seriesName || "",
      seriesNum: book.seriesNum || "",
      desc: book.desc || "",
      publisher: book.publisher || "",
      year: book.year || "",
      metaSource: book.metaSource || "",
    });
    setMetadataReportIssues([]);
    setMetadataReportNote("");
    setMetadataReportOpen(true);
  }

  function closeMetadataReport(force = false) {
    if (metadataReportBusy && !force) return;
    setMetadataReportOpen(false);
    setMetadataReportBook(null);
    setMetadataReportIssues([]);
    setMetadataReportNote("");
  }

  function toggleMetadataReportIssue(value) {
    setMetadataReportIssues((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  }

  async function submitMetadataReport() {
    if (!userId || !metadataReportBook || metadataReportBusy) return;
    if (!metadataReportIssues.length) {
      showToast("Choose at least one issue to review", true);
      return;
    }
    setMetadataReportBusy(true);
    try {
      const reportId = `rpt_${mkid()}`;
      const snapshot = {
        title: metadataReportBook.title || "",
        authors: metadataReportBook.authors || "",
        isbn: metadataReportBook.isbn || "",
        isbn10: metadataReportBook.isbn10 || "",
        isbn13: metadataReportBook.isbn13 || "",
        cover: metadataReportBook.cover || "",
        seriesName: metadataReportBook.seriesName || "",
        seriesNum: metadataReportBook.seriesNum || "",
        desc: metadataReportBook.desc || "",
        publisher: metadataReportBook.publisher || "",
        year: metadataReportBook.year || "",
        metaSource: metadataReportBook.metaSource || "",
      };
      await setDoc(doc(db, "metadataReports", reportId), {
        reportId,
        status: "open",
        issueType: metadataReportIssues[0],
        issueTypes: metadataReportIssues,
        note: String(metadataReportNote || "").trim(),
        reporterUid: userId,
        reporterEmail: auth.currentUser?.email || "",
        reporterName: auth.currentUser?.displayName || "",
        bookUid: metadataReportBook.uid || "",
        contextPath: fromNextUp ? "nextup" : "books",
        overrideDocHints: buildCatalogOverrideDocIds(metadataReportBook),
        snapshot,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      closeMetadataReport(true);
      setMetadataReportSentOpen(true);
    } catch (e) {
      showToast("Could not send report right now", true);
    } finally {
      setMetadataReportBusy(false);
    }
  }

  async function applyAdminMetadataFixes() {
    if (!userId || !isAdminUser || adminFixBusy) return;
    setAdminFixBusy(true);
    setAdminFixReport(null);
    try {
      const overrideSnap = await getDocs(collection(db, "catalogOverrides"));
      const overrides = overrideSnap.docs
        .map((snap) => normalizeCatalogOverride(snap.data(), snap.id))
        .filter((item) => item && item.active !== false);
      const overrideDocMap = new Map();
      overrides.forEach((override) => {
        if (override?.overrideId) overrideDocMap.set(String(override.overrideId), override);
      });

      let batch = writeBatch(db);
      let batchOps = 0;
      let overridesProcessed = 0;
      let mirroredOverrides = 0;
      let matchedDocs = 0;
      let updatedDocs = 0;
      let reportTargets = 0;
      const touchedRefs = new Set();

      const queuePatch = async (ref, patch) => {
        if (!patch || !Object.keys(patch).length) return;
        batch.set(ref, patch, { merge: true });
        batchOps += 1;
        updatedDocs += 1;
        if (batchOps >= 300) {
          await batch.commit();
          batch = writeBatch(db);
          batchOps = 0;
        }
      };

      const processDocSnap = async (snap, override) => {
        if (!snap?.exists()) return;
        const refPath = snap.ref.path;
        if (touchedRefs.has(refPath)) return;
        touchedRefs.add(refPath);
        matchedDocs += 1;
        const patch = buildCatalogOverridePatchData(snap.data(), override);
        await queuePatch(snap.ref, patch);
      };

      for (const override of overrides) {
        const overrideId = String(override.overrideId || "").trim();
        if (!overrideId) continue;
        overridesProcessed += 1;

        if (
          overrideId.startsWith("isbn13_") ||
          overrideId.startsWith("isbn10_")
        ) {
          const titleAuthorDocId = buildTitleAuthorOverrideDocIdFromOverride(override);
          if (titleAuthorDocId && titleAuthorDocId !== overrideId) {
            const mirroredPayload = buildMirroredOverridePayload(override, titleAuthorDocId);
            if (mirroredPayload) {
              overrideDocMap.set(
                titleAuthorDocId,
                normalizeCatalogOverride(mirroredPayload, titleAuthorDocId),
              );
              batch.set(doc(db, "catalogOverrides", titleAuthorDocId), mirroredPayload, {
                merge: true,
              });
              batchOps += 1;
              mirroredOverrides += 1;
              if (batchOps >= 300) {
                await batch.commit();
                batch = writeBatch(db);
                batchOps = 0;
              }
            }
          }
        }
      }

      const resolveOverrideForBook = (book) => {
        const matchingOverrides = buildCatalogOverrideDocIds(book)
          .map((id) => overrideDocMap.get(id))
          .filter(Boolean);
        return mergeCatalogOverrides(matchingOverrides);
      };

      const processCollectionDocs = async (snap) => {
        for (const docSnap of snap.docs) {
          const override = resolveOverrideForBook(docSnap.data());
          if (!override) continue;
          await processDocSnap(docSnap, override);
        }
      };

      const usersSnap = await getDocs(collection(db, "users"));
      for (const userDoc of usersSnap.docs) {
        const ownerId = userDoc.id;
        const booksSnap = await getDocs(collection(db, "users", ownerId, "books"));
        await processCollectionDocs(booksSnap);
        const nextUpSnap = await getDocs(collection(db, "users", ownerId, "nextup"));
        await processCollectionDocs(nextUpSnap);
      }

      const sharedShelvesSnap = await getDocs(collection(db, "sharedShelves"));
      for (const shelfDoc of sharedShelvesSnap.docs) {
        const ownerId = shelfDoc.id;
        const sharedBooksSnap = await getDocs(
          collection(db, "sharedShelves", ownerId, "books"),
        );
        await processCollectionDocs(sharedBooksSnap);
      }

      const reportSnap = await getDocs(collection(db, "metadataReports"));
      for (const reportDoc of reportSnap.docs) {
        const report = reportDoc.data() || {};
        const ownerId = String(report.reporterUid || "").trim();
        const contextPath = report.contextPath === "nextup" ? "nextup" : "books";
        const bookUid = String(report.bookUid || "").trim();
        if (!ownerId || !bookUid) continue;
        const targetRef = doc(db, "users", ownerId, contextPath, bookUid);
        const targetSnap = await getDoc(targetRef);
        if (targetSnap.exists()) {
          reportTargets += 1;
          const hintedOverride = mergeCatalogOverrides(
            (Array.isArray(report.overrideDocHints) ? report.overrideDocHints : [])
              .map((id) => overrideDocMap.get(String(id || "").trim()))
              .filter(Boolean),
          );
          const fallbackOverride = resolveOverrideForBook(targetSnap.data());
          const reportOverride = hintedOverride || fallbackOverride;
          if (reportOverride) {
            await processDocSnap(targetSnap, reportOverride);
          }
        }
      }

      if (batchOps > 0) {
        await batch.commit();
      }

      const summary = {
        overridesProcessed,
        mirroredOverrides,
        matchedDocs,
        updatedDocs,
        reportTargets,
        ranAt: new Date().toISOString(),
      };
      setAdminFixReport(summary);
      if (updatedDocs > 0) {
        showToast(
          `Applied metadata fixes to ${updatedDocs} book${updatedDocs === 1 ? "" : "s"}`,
        );
      } else {
        showToast("No matching books needed updates");
      }
    } catch (e) {
      const code = String(e?.code || "").trim();
      const rawMessage = String(e?.message || "").trim();
      const needsIndex =
        code === "failed-precondition" &&
        /index/i.test(rawMessage);
      const helpfulMessage = needsIndex
        ? "Metadata fixes need Firestore indexes before they can run. Deploy firestore indexes and try again."
        : rawMessage || "Could not apply metadata fixes right now.";
      setAdminFixReport({
        error: true,
        message: helpfulMessage,
      });
      showToast(helpfulMessage, true);
    } finally {
      setAdminFixBusy(false);
    }
  }

  function openDetail(book, fromNU) {
    const lane = inferLaneFromSelectedGenres(
      book.genres || [],
      book.genreLane || "fiction",
    );
    setActive(book);
    setDTitle(normalizeEditableBookTitle(book.title));
    setDNotes(book.notes || "");
    setDDesc(book.desc || "");
    setDRating(book.rating || 0);
    setDStatus(book.status);
    setDGenreLane(lane);
    setDFmts(book.formats || []);
    setDFav(book.fav || false);
    setDGenres(restrictGenresToLane(book.genres || [], lane));
    setDVibes(book.vibes || []);
    setDTags(book.tags || []);
    setDSeriesName(book.seriesName || "");
    setDSeriesNum(book.seriesNum || "");
    setDEdition(book.edition || "");
    setDCover(normalizeCoverUrl(book.cover || ""));
    setDIsbn(book.isbn13 || book.isbn10 || book.isbn || "");
    setDPublishedDate(book.publishedDate || "");
    setDPublisher(book.publisher && book.publisher !== "--" ? book.publisher : "");
    setDPages(
      book.pages && book.pages !== "--" && Number(book.pages) > 0
        ? String(book.pages)
        : "",
    );
    setDStartDate(book.dateStarted || "");
    setDEndDate(book.dateRead || "");
    setDReads(book.reads || []);
    setDRereading(!!book.rereading);
    setDRereadStartDate(book.rereadStartDate || "");
    setFromNextUp(!!fromNU);
    setDetailEditSection("");
    setDetailTab("view");
    setView("detail");
  }

  function handleDetailStatusChange(nextStatus) {
    const next = deriveDetailStatusChange({
      nextStatus,
      activeDateRead: active?.dateRead || "",
      draftEndDate: dEndDate,
      fromNextUp,
      currentRereadStartDate: dRereadStartDate,
      dnfLabel,
      today: TODAY(),
    });

    setDStatus(next.status);
    if (next.startDate !== undefined) setDStartDate(next.startDate);
    if (next.endDate !== undefined) setDEndDate(next.endDate);
    if (next.rating !== undefined) setDRating(next.rating);
    setDRereading(!!next.rereading);
    setDRereadStartDate(next.rereadStartDate || "");
  }

  async function findCoverCandidates(book, isbnDraft) {
    const candidates = [];
    const seen = new Set();
    const addCandidate = (rawUrl, source, detail = "") => {
      const url = normalizeUserCoverUrl(rawUrl);
      if (!url || isPlaceholderCoverUrl(url) || seen.has(url)) return;
      seen.add(url);
      candidates.push({ url, source: source || coverProviderLabel(url), detail });
    };

    const isbn = normalizeIsbn(isbnDraft || "") || preferredBookIsbn(book);
    const title = String(book?.title || "").trim();
    const firstAuthor = firstAuthorFromText(book?.authors || "");
    const tasks = [];

    if (isbn) {
      tasks.push(
        searchByIsbn(isbn, { refresh: true }).then((matched) => {
          if (!matched?.cover) return;
          addCandidate(
            matched.cover,
            coverProviderLabel(matched.cover),
            `ISBN ${isbn}`,
          );
        }),
      );
    }

    if (title) {
      const queries = firstAuthor
        ? [
            `intitle:"${title}" inauthor:"${firstAuthor}"`,
            `intitle:${title} inauthor:${firstAuthor}`,
          ]
        : [`intitle:"${title}"`, `intitle:${title}`];
      tasks.push(
        Promise.allSettled(queries.map((query) => fetchGoogleItems(query, 20))).then(
          (responses) => {
            const googleBooks = dedupeMappedBooks(
              responses.flatMap((response) =>
                response.status === "fulfilled"
                  ? response.value.map(mapGoogleItemForApp)
                  : [],
              ),
            );
            googleBooks.forEach((match) => {
              if (!match.cover) return;
              const detail = [
                match.year && match.year !== "--" ? match.year : "",
                match.edition || "",
              ]
                .filter(Boolean)
                .join(" · ");
              addCandidate(match.cover, "Google Books", detail);
            });
          },
        ),
      );
    }

    await Promise.allSettled(tasks);
    return candidates.slice(0, 12);
  }


  async function saveDetail() {
    if (!active) return;
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    const normalizedDetailTitle = normalizeEditableBookTitle(dTitle);
    if (!normalizedDetailTitle) {
      showToast("Book title is required", true);
      return;
    }
    const normalizedDetailIsbn = normalizeIsbn(dIsbn || "") || "";
    const rawDetailCover = String(dCover || "").trim();
    const normalizedDetailCover = normalizeUserCoverUrl(rawDetailCover);
    if (rawDetailCover && !normalizedDetailCover) {
      showToast("Enter a valid http or https cover image URL", true);
      return;
    }
    const parsedDetailPages = parsePageCount(dPages);
    const statusFields = resolveDetailStatusFields({
      active,
      dStatus,
      dRating,
      dStartDate,
      dEndDate,
      dReads,
      dRereading,
      dRereadStartDate,
      dnfLabel,
      today: TODAY(),
    });
    const updated = {
      ...active,
      title: normalizedDetailTitle,
      notes: dNotes,
      desc: cleanBookDescription(dDesc || ""),
      rating: statusFields.rating,
      status: statusFields.status,
      genreLane: dGenreLane,
      formats: dFmts,
      fav: dFav,
      genres: restrictGenresToLane(dGenres, dGenreLane),
      vibes: dVibes,
      tags: dTags,
      seriesName: dSeriesName,
      seriesNum: dSeriesNum,
      edition: dEdition,
      cover: normalizedDetailCover || null,
      isbn: normalizedDetailIsbn,
      isbn10: normalizedDetailIsbn.length === 10 ? normalizedDetailIsbn : "",
      isbn13: normalizedDetailIsbn.length === 13 ? normalizedDetailIsbn : "",
      publishedDate: String(dPublishedDate || "").trim(),
      publisher: String(dPublisher || "").trim() || "--",
      pages: parsedDetailPages || "--",
      dateStarted: statusFields.dateStarted,
      dateRead: statusFields.dateRead,
      rereading: statusFields.rereading,
      rereadStartDate: statusFields.rereadStartDate,
      reads: statusFields.reads,
    };
    try {
      if (userId) {
        const col = fromNextUp
          ? collection(db, "users", userId, "nextup")
          : collection(db, "users", userId, "books");
        await setDoc(doc(col, active.uid), updated, { merge: true });
      } else {
        if (fromNextUp)
          setNextUp((p) => p.map((b) => (b.uid === active.uid ? updated : b)));
        else
          setBooks((p) => p.map((b) => (b.uid === active.uid ? updated : b)));
      }
      if (statusFields.status === "Read") {
        await removeMatchingNextUpEntries(updated);
      }
      setActive(updated);
      setDetailEditSection("");
      setDetailTab("view");
      setView("detail");
      requestAnimationFrame(() => {
        scrollAllToTop();
      });
      showToast("Changes saved!");
    } catch (e) {
      showToast("Could not save changes", true);
    }
  }

  async function moveToLibrary(bookOverride) {
    if (!userId) return;
    const book = bookOverride || active;
    const updated = { ...book, status: "In Progress", dateStarted: TODAY() };
    await deleteDoc(doc(collection(db, "users", userId, "nextup"), book.uid));
    await setDoc(
      doc(collection(db, "users", userId, "books"), book.uid),
      updated,
    );
    setView("mybooks");
    showToast("Start reading \uD83D\uDCDA");
  }
  async function startReadingInLibrary(bookOverride) {
    const book = bookOverride || active;
    if (!book) return;

    const startedOn = book.dateStarted || TODAY();
    const updated = {
      ...book,
      status: "In Progress",
      dateStarted: startedOn,
      rereading: false,
      rereadStartDate: null,
    };
    const wasDnf = book.status === dnfLabel || book.status === "DNF";

    setActive(updated);
    setDStatus("In Progress");
    setDStartDate(startedOn);
    setDRereading(false);
    setDRereadStartDate("");
    setBooks((p) => p.map((b) => (b.uid === book.uid ? updated : b)));

    if (userId) {
      await setDoc(
        doc(collection(db, "users", userId, "books"), book.uid),
        {
          status: "In Progress",
          dateStarted: startedOn,
          rereading: false,
          rereadStartDate: null,
        },
        { merge: true },
      );
    }

    showToast(wasDnf ? "Continue reading" : "Start reading");
  }

  async function startRereadInLibrary(bookOverride) {
    const book = bookOverride || active;
    if (!book) return;

    const rereadStartedOn = book.rereadStartDate || dRereadStartDate || TODAY();
    const updated = {
      ...book,
      status: "In Progress",
      rereading: true,
      rereadStartDate: rereadStartedOn,
    };

    setActive(updated);
    setDStatus("In Progress");
    setDRereading(true);
    setDRereadStartDate(rereadStartedOn);
    setBooks((p) => p.map((b) => (b.uid === book.uid ? updated : b)));

    if (userId) {
      await setDoc(
        doc(collection(db, "users", userId, "books"), book.uid),
        {
          status: "In Progress",
          rereading: true,
          rereadStartDate: rereadStartedOn,
        },
        { merge: true },
      );
    }

    showToast("Reread started");
  }
  async function finishReadingInLibrary(bookOverride) {
    const book = bookOverride || active;
    if (!book) return;

    const finishState = buildFinishReadingState({
      book,
      dRereading,
      dRereadStartDate,
      dReads,
      today: TODAY(),
      readingDays,
    });

    if (finishState.isRereadSession) {
      const updated = finishState.updated;

      setActive(updated);
      setDStatus("Read");
      setDRereading(false);
      setDRereadStartDate("");
      setDReads(finishState.nextReads);
      setBooks((p) => p.map((b) => (b.uid === book.uid ? updated : b)));

      if (userId) {
        await setDoc(
          doc(collection(db, "users", userId, "books"), book.uid),
          {
            status: "Read",
            rereading: false,
            rereadStartDate: null,
            reads: finishState.nextReads,
          },
          { merge: true },
        );
      }

      await removeMatchingNextUpEntries(updated);
      openFinishPrompt(updated);
      showToast("Reread finished");
      return;
    }

    const startedOn = finishState.startedOn;
    const finishedOn = finishState.updated.dateRead;
    const updated = finishState.updated;

    setActive(updated);
    setDStatus("Read");
    setDStartDate(startedOn);
    setDEndDate(finishedOn);
    setDRereading(false);
    setDRereadStartDate("");
    setBooks((p) => p.map((b) => (b.uid === book.uid ? updated : b)));

    if (userId) {
      await setDoc(
        doc(collection(db, "users", userId, "books"), book.uid),
        {
          status: "Read",
          dateStarted: startedOn,
          dateRead: finishedOn,
          rereading: false,
          rereadStartDate: null,
        },
        { merge: true },
      );
    }

    await removeMatchingNextUpEntries(updated);
    openFinishPrompt(updated);
    showToast("Marked finished");
  }

  async function saveFinishPromptDetails() {
    if (!active || finishPromptBusy) return;
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    setFinishPromptBusy(true);
    try {
      const cleanedNotes = String(finishPromptNotes || "");
      const updated = {
        ...active,
        rating: finishPromptRating,
        fav: finishPromptFav,
        notes: cleanedNotes,
      };

      setActive(updated);
      setDRating(finishPromptRating);
      setDFav(finishPromptFav);
      setDNotes(cleanedNotes);
      setBooks((prev) => prev.map((b) => (b.uid === updated.uid ? updated : b)));

      if (userId) {
        await setDoc(
          doc(collection(db, "users", userId, "books"), updated.uid),
          { rating: finishPromptRating, fav: finishPromptFav, notes: cleanedNotes },
          { merge: true },
        );
      }

      closeFinishPrompt(true);
      showToast("Saved your rating");
    } catch (e) {
      showToast("Could not save rating", true);
    } finally {
      setFinishPromptBusy(false);
    }
  }

  async function toggleDetailFavorite() {
    if (!active) return;
    const newFav = !dFav;
    setDFav(newFav);
    if (userId) {
      const col = fromNextUp
        ? collection(db, "users", userId, "nextup")
        : collection(db, "users", userId, "books");
      await setDoc(
        doc(col, active.uid),
        { fav: newFav },
        { merge: true },
      );
    }
    showToast(
      newFav
        ? "Added to favorites ❤️"
        : "Removed from favorites",
    );
  }

  async function deleteBook() {
    if (!userId) return;
    const col = fromNextUp
      ? collection(db, "users", userId, "nextup")
      : collection(db, "users", userId, "books");
    await deleteDoc(doc(col, active.uid));
    setView(fromNextUp ? "nextup" : "mybooks");
    showToast("Removed", true);
  }

  function startRereadDraft() {
    if (dRereading) return;
    setDStatus("In Progress");
    setDRereading(true);
    setDRereadStartDate((prev) => prev || TODAY());
    showToast("Reread started");
  }

  function isSameBookRef(a, b) {
    if (!a || !b) return false;
    if (a.uid && b.uid && String(a.uid) === String(b.uid)) return true;
    const aKey = normalizeText(`${a.title || ""}::${a.authors || ""}`);
    const bKey = normalizeText(`${b.title || ""}::${b.authors || ""}`);
    return !!aKey && aKey === bKey;
  }

  async function startSmartPickedBook(bookOverride) {
    const book = bookOverride || smartNextPick?.book;
    if (!book) return;

    const startedOn = book.dateStarted || TODAY();
    const updated = { ...book, status: "In Progress", dateStarted: startedOn };
    const queuedMatch = nextUp.find((entry) => isSameBookRef(entry, book));
    const wasDnf = book.status === dnfLabel || book.status === "DNF";

    setSmartNextPick(null);
    setBooks((prev) => prev.map((entry) => (isSameBookRef(entry, book) ? updated : entry)));
    if (queuedMatch) {
      setNextUp((prev) => prev.filter((entry) => !isSameBookRef(entry, book)));
    }
    if (active?.uid === book.uid) {
      setActive(updated);
      setDStatus("In Progress");
      setDStartDate(startedOn);
    }

    if (userId) {
      await setDoc(
        doc(collection(db, "users", userId, "books"), book.uid),
        { status: "In Progress", dateStarted: startedOn },
        { merge: true },
      );
      if (queuedMatch) {
        await deleteDoc(doc(collection(db, "users", userId, "nextup"), queuedMatch.uid));
      }
    }

    showToast(wasDnf ? "Continue reading" : "Start reading");
  }

  function statusRankForSmart(status) {
    if (status === "In Progress") return 0;
    if (status === "Unstarted" || status === "Unread") return 1;
    if (status === "Read") return 2;
    if (status === dnfLabel || status === "DNF") return 3;
    return 4;
  }

  function compareBooksSmart(a, b) {
    if (fStatus === "All") {
      const rankDiff = statusRankForSmart(a.status) - statusRankForSmart(b.status);
      if (rankDiff) return rankDiff;
    }

    const favDiff = Number(!!b.fav) - Number(!!a.fav);
    if (favDiff) return favDiff;

    if (
      fStatus === "All" &&
      statusRankForSmart(a.status) === 2 &&
      statusRankForSmart(b.status) === 2
    ) {
      const readDiff = dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead));
      if (readDiff) return readDiff;
    }

    const createdDiff = createdAtOrderValue(b) - createdAtOrderValue(a);
    if (createdDiff) return createdDiff;
    return (a.title || "").localeCompare(b.title || "");
  }

  const shown = books
    .filter((b) => {
      const status =
        fStatus === "All" ||
        b.status === fStatus ||
        (fStatus === dnfLabel && (b.status === dnfLabel || b.status === "DNF"));
      if (fStatus !== "All" && !status) return false;
      if (fFav && !b.fav) return false;
      if (
        fGenre !== "All" &&
        ![...(b.genres || []), ...(b.vibes || [])].includes(fGenre)
      )
        return false;
      if (fFmt !== "All" && !(b.formats || []).includes(fFmt)) return false;
      if (collectionSq.trim()) {
        const terms = collectionSq.toLowerCase().trim().split(/\s+/);
        const haystack = [
          b.title,
          b.authors,
          b.seriesName,
          ...(b.genres || []),
          ...(b.vibes || []),
          ...(b.tags || []),
        ]
          .join(" ")
          .toLowerCase();
        if (!terms.every((t) => haystack.includes(t))) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "smart") {
        if (fStatus === "Read") {
          const readDiff = dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead));
          if (readDiff) return readDiff;
        }
        return compareBooksSmart(a, b);
      }
      if (sortBy === "az") return a.title.localeCompare(b.title);
      if (sortBy === "za") return b.title.localeCompare(a.title);
      if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sortBy === "author") return a.authors.localeCompare(b.authors);
      return createdAtOrderValue(b) - createdAtOrderValue(a);
    });

  const nextUpKeys = new Set(
    nextUp.flatMap((b) => [
      b.uid ? "id:" + b.uid : null,
      "meta:" + normalizeText((b.title || "") + "::" + (b.authors || "")),
    ]),
  );
  const unreadBooksForSmartPick = books.filter(
    (b) => b.status === "Unstarted" || b.status === "Unread",
  );
  const recentFinishedForSmartPick = [...books]
    .filter((b) => b.status === "Read" && b.dateRead)
    .sort((a, b) => dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead)))
    .slice(0, 5);

  function buildSmartNextPick(sessionInput) {
    if (!unreadBooksForSmartPick.length) return null;
    const session = {
      offersShown: 0,
      moodRejects: 0,
      seenUids: [],
      ...(sessionInput || {}),
    };
    const totalOffers = 5;
    const offerNumber = session.offersShown + 1;
    if (offerNumber > totalOffers) return null;
    const phase = session.moodRejects >= 3 ? "switch" : "mood";

    const genreWeights = new Map();
    const vibeWeights = new Map();
    recentFinishedForSmartPick.forEach((book, index) => {
      const weight = Math.max(1, 5 - index);
      (book.genres || []).filter(Boolean).forEach((genre) => {
        genreWeights.set(genre, (genreWeights.get(genre) || 0) + weight);
      });
      (book.vibes || []).filter(Boolean).forEach((vibe) => {
        vibeWeights.set(vibe, (vibeWeights.get(vibe) || 0) + weight);
      });
    });
    const dominantGenres = [...genreWeights.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([genre]) => genre)
      .slice(0, 3);

    const candidates = unreadBooksForSmartPick
      .filter(
        (book) =>
          !session.seenUids.includes(book.uid) ||
          session.seenUids.length >= unreadBooksForSmartPick.length,
      )
      .map((book) => {
        const matchedGenres = (book.genres || []).filter((genre) => genreWeights.has(genre));
        const matchedVibes = (book.vibes || []).filter((vibe) => vibeWeights.has(vibe));
        const differentGenres = (book.genres || []).filter(
          (genre) => genre && !genreWeights.has(genre),
        );
        const genreScore = matchedGenres.reduce(
          (sum, genre) => sum + (genreWeights.get(genre) || 0) * 4,
          0,
        );
        const vibeScore = matchedVibes.reduce(
          (sum, vibe) => sum + (vibeWeights.get(vibe) || 0) * 5,
          0,
        );
        const queueKey =
          (book.uid && nextUpKeys.has("id:" + book.uid)) ||
          nextUpKeys.has(
            "meta:" + normalizeText((book.title || "") + "::" + (book.authors || "")),
          );
        const inQueue = !!queueKey;
        const favouriteBoost = book.fav ? 8 : 0;
        const queueBoost = inQueue ? 10 : 0;
        const metadataBoost =
          (book.cover ? 2 : 0) + (Number(book.pages) > 0 ? 1 : 0) + (book.seriesName ? 1 : 0);
        const moodScore =
          genreScore + vibeScore + favouriteBoost + queueBoost + metadataBoost;
        const switchScore =
          favouriteBoost +
          queueBoost +
          metadataBoost +
          differentGenres.length * 7 +
          (matchedGenres.length === 0 ? 18 : 0) +
          (matchedVibes.length === 0 ? 12 : 0) -
          matchedGenres.length * 10 -
          matchedVibes.length * 8;

        const reasonParts = [];
        const switchReasonParts = [];
        if (matchedVibes.length) {
          reasonParts.push(
            `Matches your recent ${matchedVibes.slice(0, 2).join(" + ")} mood`,
          );
        }
        if (!matchedVibes.length && matchedGenres.length) {
          reasonParts.push(
            `Fits the ${matchedGenres.slice(0, 2).join(" + ")} books you've been finishing`,
          );
        } else if (matchedGenres.length) {
          reasonParts.push(
            `Also lines up with your ${matchedGenres.slice(0, 2).join(" + ")} reads`,
          );
        }
        if (recentFinishedForSmartPick.length > 0 && dominantGenres.length > 0) {
          switchReasonParts.push(
            `A switch-up from your recent ${dominantGenres.slice(0, 2).join(" + ")} streak`,
          );
        }
        if (differentGenres.length) {
          switchReasonParts.push(
            `Leans more toward ${differentGenres.slice(0, 2).join(" + ")}`,
          );
        } else if ((book.genres || []).length) {
          switchReasonParts.push(
            `A different angle through ${(book.genres || []).slice(0, 2).join(" + ")}`,
          );
        }
        if (inQueue) {
          reasonParts.push("Already waiting in your Next Up queue");
          switchReasonParts.push("Already waiting in your Next Up queue");
        }
        if (book.fav) {
          reasonParts.push("You've already marked it as a favorite");
          switchReasonParts.push("You've already marked it as a favorite");
        }
        if (!reasonParts.length && recentFinishedForSmartPick.length === 0) {
          reasonParts.push("A fresh pick from your unread shelf");
        }
        if (!reasonParts.length) {
          reasonParts.push("A strong fit from your unread shelf");
        }
        if (!switchReasonParts.length) {
          switchReasonParts.push("A different direction from your unread shelf");
        }

        return {
          book,
          moodScore,
          switchScore,
          inQueue,
          matchedGenres,
          matchedVibes,
          reasonParts: reasonParts.slice(0, 2),
          switchReasonParts: switchReasonParts.slice(0, 2),
        };
      })
      .sort((a, b) => {
        const scoreDiff =
          phase === "switch"
            ? b.switchScore - a.switchScore
            : b.moodScore - a.moodScore;
        if (scoreDiff) return scoreDiff;
        return (a.book.title || "").localeCompare(b.book.title || "");
      });

    if (!candidates.length) return null;
    const shortlist = candidates.slice(0, Math.min(5, candidates.length));
    const weightedShortlist = shortlist.map((candidate, index) => ({
      ...candidate,
      weight: Math.max(
        1,
        (phase === "switch" ? candidate.switchScore : candidate.moodScore) -
          index * 2,
      ),
    }));
    const totalWeight = weightedShortlist.reduce((sum, candidate) => sum + candidate.weight, 0);
    let roll = Math.random() * totalWeight;
    let picked = weightedShortlist[0];
    for (const candidate of weightedShortlist) {
      roll -= candidate.weight;
      if (roll <= 0) {
        picked = candidate;
        break;
      }
    }

    return {
      ...picked,
      phase,
      offerNumber,
      totalOffers,
      canPickAgain: offerNumber < totalOffers,
      session: {
        ...session,
        offersShown: offerNumber,
        seenUids: [...new Set([...(session.seenUids || []), picked.book.uid])],
      },
      headline:
        phase === "switch"
          ? "Switching things up"
          : recentFinishedForSmartPick.length > 0
            ? "Picked for your current reading mood"
            : "Picked from your unread shelf",
      detailLine:
        phase === "switch"
          ? picked.switchReasonParts.join(" · ")
          : picked.reasonParts.join(" · "),
    };
  }

  function openSmartNextPick() {
    const suggestion = buildSmartNextPick();
    if (!suggestion) {
      showToast("No unread books to pick from yet", true);
      return;
    }
    setSmartNextPick(suggestion);
  }

  function rejectSmartNextPick() {
    if (!smartNextPick?.session) {
      openSmartNextPick();
      return;
    }
    const nextSession = {
      ...smartNextPick.session,
      moodRejects:
        smartNextPick.phase === "mood"
          ? (smartNextPick.session.moodRejects || 0) + 1
          : smartNextPick.session.moodRejects || 0,
    };
    const suggestion = buildSmartNextPick(nextSession);
    if (!suggestion) {
      setSmartNextPick(null);
      showToast("That was the full set of picks for this round");
      return;
    }
    if (smartNextPick.phase === "mood" && suggestion.phase === "switch") {
      showToast("Switching it up");
    }
    setSmartNextPick(suggestion);
  }
  const nextUpFromLibraryList = books
    .filter((b) => {
      const idKey = b.uid ? "id:" + b.uid : null;
      const metaKey =
        "meta:" + normalizeText((b.title || "") + "::" + (b.authors || ""));
      return !(idKey && nextUpKeys.has(idKey)) && !nextUpKeys.has(metaKey);
    })
    .filter((b) => {
      const query = nextUpFromLibrarySq.trim().toLowerCase();
      if (!query) return true;
      const terms = query.split(/\s+/).filter(Boolean);
      const haystack = [
        b.title,
        b.authors,
        b.seriesName,
        ...(b.genres || []),
        ...(b.vibes || []),
        ...(b.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return terms.every((t) => haystack.includes(t));
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  const allTagOptions = [
    "All",
    ...Array.from(
      new Set(
        books
          .flatMap((b) => [...(b.genres || []), ...(b.vibes || [])])
          .filter(Boolean),
      ),
    ).sort((a, b) =>
      String(a || "").localeCompare(String(b || ""), undefined, {
        sensitivity: "base",
      }),
    ),
  ];
  const filtersOn =
    sortBy !== "smart" || fGenre !== "All" || fFmt !== "All" || fFav;
  const hasActiveRefinements =
    filtersOn || fStatus !== "All" || !!collectionSq.trim();
  const matchedTags = collectionSq.trim()
    ? [
        ...new Set(
          books
            .flatMap((b) => [
              ...(b.genres || []),
              ...(b.vibes || []),
              ...(b.tags || []),
            ])
            .filter(
              (t) =>
                t &&
                collectionSq
                  .toLowerCase()
                  .split(/\s+/)
                  .some((term) => t.toLowerCase().includes(term)),
            ),
        ),
      ]
    : [];
  const selectedYear = Number(yr);
  const currentReadsAll = books.filter((b) => b.status === "In Progress");
  const currentReads =
    selectedYear === CUR_YR
      ? currentReadsAll
      : currentReadsAll.filter((b) => {
          if (b.dateStarted && inYear(b.dateStarted, selectedYear)) return true;
          if (Array.isArray(b.checkIns)) {
            return b.checkIns.some((d) => inYear(d, selectedYear));
          }
          return false;
        });
  const currentReadsHeading =
    selectedYear === CUR_YR ? "Currently Reading" : "Reading in " + selectedYear;
  const readBooks = books.filter(
    (b) =>
      hasCompletedReadHistory(b) ||
      ((b.status === dnfLabel || b.status === "DNF") && b.dateRead),
  );
  const allYears = [...new Set(readBooks.map((b) => dateYear(b.dateRead)).filter(Boolean))].sort(
    (a, b) => Number(b) - Number(a),
  );
  const displayYrs = allYears.length ? allYears : [String(CUR_YR)];
  const finishedYr = books.filter(
    (b) => hasCompletedReadHistory(b) && inYear(b.dateRead, selectedYear),
  );
  const recentlyFinished = [...finishedYr]
    .sort((a, b) => dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead)))
    .slice(0, 3);
  const byMo = MONTHS_SHORT.map((m, i) => {
    const booksInMonth = finishedYr
      .filter((b) => {
        const monthIndex = dateMonthIndex(b.dateRead);
        return (monthIndex === null ? 0 : monthIndex) === i;
      })
      .sort((a, b) => dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead)));
    return { m, n: booksInMonth.length, books: booksInMonth, monthIndex: i };
  });
  const maxMo = Math.max(...byMo.map((x) => x.n), 1);
  const selectedYearCheckIns = (() => {
    const dates = new Set();
    (checkIns || []).forEach((d) => {
      if (inYear(d, selectedYear)) dates.add(String(d).slice(0, 10));
    });
    (books || []).forEach((book) => {
      (book.checkIns || []).forEach((d) => {
        if (inYear(d, selectedYear)) dates.add(String(d).slice(0, 10));
      });
    });
    return [...dates];
  })();
  const bestYearStreak = bestStreakFromDates(selectedYearCheckIns);
  const streakCardLabel = selectedYear === CUR_YR ? "Streak" : "Best Streak";
  const streakCardDays = selectedYear === CUR_YR ? streak : bestYearStreak;
  const streakCardValue =
    streakCardDays + " day" + (streakCardDays === 1 ? "" : "s");
  const booksWithPageData = finishedYr.filter((b) => Number(b.pages) > 0);
  const totalKnownPages = booksWithPageData.reduce(
    (acc, book) => acc + Number(book.pages || 0),
    0,
  );
  const missingPageCount = Math.max(0, finishedYr.length - booksWithPageData.length);
  const inferredAvgPages = booksWithPageData.length
    ? totalKnownPages / booksWithPageData.length
    : 320;
  const estimatedPagesRaw =
    totalKnownPages + (missingPageCount > 0 ? inferredAvgPages * missingPageCount : 0);
  const estimatedPagesRounded = roundPagesEstimate(estimatedPagesRaw);
  const pagesStatLabel = missingPageCount > 0 ? "Est. Pages" : "Pages";
  const pagesStatValue =
    estimatedPagesRounded > 0 ? formatPagesEstimate(estimatedPagesRounded) : "--";
  const ratedFinishedYr = finishedYr.filter((b) => Number(b.rating) > 0);
  const avgStar = ratedFinishedYr.length
    ? (
        ratedFinishedYr.reduce((a, b) => a + Number(b.rating || 0), 0) /
        ratedFinishedYr.length
      ).toFixed(1)
    : "--";
  const goalTarget = yearlyGoals[yr] || 0;
  const hasGoalForYear = goalTarget > 0;
  const goalPct = hasGoalForYear
    ? Math.min(100, Math.round((finishedYr.length / goalTarget) * 100))
    : 0;
  const { emoji: gEmoji, msg: gMsg } = getEncouragement(goalPct);
  const goalHeroEmoji = hasGoalForYear ? gEmoji : "📚";
  const goalHeroMsg = hasGoalForYear ? gMsg : "No reading goal set for " + selectedYear;
  const goalDeltaMsg = hasGoalForYear
    ? finishedYr.length < goalTarget
      ? goalTarget - finishedYr.length + " to go"
      : finishedYr.length > goalTarget
        ? finishedYr.length - goalTarget + " above goal!"
        : ""
    : finishedYr.length + " finished in " + selectedYear;
  const top5Genres = (() => {
    const g = {};
    finishedYr.forEach((b) =>
      (b.genres || [])
        .filter(Boolean)
        .forEach((gn) => (g[gn] = (g[gn] || 0) + 1)),
    );
    return Object.entries(g)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  })();
  const top5Vibes = (() => {
    const g = {};
    finishedYr.forEach((b) =>
      (b.vibes || [])
        .filter(Boolean)
        .forEach((gn) => (g[gn] = (g[gn] || 0) + 1)),
    );
    return Object.entries(g)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  })();
  const booksWithPages = booksWithPageData;
  const shortest = booksWithPages.length
    ? booksWithPages.reduce((a, b) =>
        Number(b.pages) < Number(a.pages) ? b : a,
      )
    : null;
  const longest = booksWithPages.length
    ? booksWithPages.reduce((a, b) =>
        Number(b.pages) > Number(a.pages) ? b : a,
      )
    : null;
  const fastest =
    finishedYr
      .filter((b) => readingDays(b.dateStarted, b.dateRead) !== null)
      .map((b) => ({
        ...b,
        days: readingDays(b.dateStarted, b.dateRead),
      }))
      .sort((a, b) => a.days - b.days)[0] || null;

  // Summary counts
  const ownedCount = books.length;
  const unreadCount = unreadBooksForSmartPick.length;
  const resetMeta = {
    nextup: {
      icon: "🔖",
      title: "Clear Next Up?",
      body: "This will remove all books from your Next Up queue. You can add them back anytime.",
      cta: "Clear Next Up",
    },
    books: {
      icon: "🗑️",
      title: "Clear My Books?",
      body: "This permanently removes all books from My Books. This cannot be undone.",
      cta: "Clear My Books",
    },
    all: {
      icon: "⚠️",
      title: "Reset Entire Library?",
      body: "This permanently removes all books from My Books and Next Up. This cannot be undone.",
      cta: "Reset Library",
    },
  };
  const resetInfo = resetScope ? resetMeta[resetScope] : null;
  const resetNeedsPhrase = resetScope === "books" || resetScope === "all";
  const resetPhraseValid =
    !resetNeedsPhrase || resetConfirmText.trim().toUpperCase() === "RESET";
  const collectionCountLabel =
    shown.length < ownedCount ? `${shown.length} of ${ownedCount}` : `${ownedCount}`;
  const headerGreetingName = (name || "").trim() || "Reader";
  const activePublishedDisplay = active
    ? formatPublishedReleaseDate(active.publishedDate, active.year)
    : "";
  const activeHasPages = Number(active?.pages) > 0;
  const showStartReadingButton =
    fromNextUp ||
    (!fromNextUp &&
      (dStatus === "Unstarted" ||
        dStatus === "Unread" ||
        dStatus === "In Progress" ||
        dStatus === "Read" ||
        dStatus === dnfLabel ||
        dStatus === "DNF"));
  const startReadingButtonLabel =
    dStatus === "In Progress"
      ? "Finished"
      : dStatus === "Read" && !dRereading
        ? "Start Reread"
        : fromNextUp || dStatus === "Unstarted" || dStatus === "Unread"
          ? "Start Reading"
          : "Continue Reading";
  const importNavTab = ["mybooks", "reading", "nextup", "settings"].includes(
    importReturnView,
  )
    ? importReturnView
    : "settings";
  const shareInviteCode = formatInviteCode(shelfSharing.inviteCode);
  const shareInviteUrl =
    userId && typeof window !== "undefined"
      ? buildShelfInviteLink(userId, shareInviteCode)
      : "";
  const settingsHintColor = T.sub;
  const bottomNavPad = "calc(76px + env(safe-area-inset-bottom))";
  const isDarkTheme = true;
  const analyticsHeadingStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: isDarkTheme ? "0.13em" : "0.11em",
    textTransform: "uppercase",
    color: isDarkTheme ? T.mute : T.sub,
  };
  const readingSectionHeadingStyle = {
    ...analyticsHeadingStyle,
    color: isDarkTheme ? T.sub : T.text,
    fontSize: 11,
    letterSpacing: isDarkTheme ? "0.13em" : "0.1em",
  };
  const readingListTitleStyle = {
    fontFamily: DISPLAY_SERIF,
    fontWeight: 600,
    fontSize: isDarkTheme ? 15 : 16,
    color: isDarkTheme ? T.text : "#1F2D3D",
    lineHeight: 1.3,
  };
  const readingListAuthorStyle = {
    fontSize: isDarkTheme ? 12 : 13,
    color: isDarkTheme ? T.sub : "#5A6778",
    fontStyle: "italic",
  };
  const shareGenreOptions = Array.from(
    new Set(
      books
        .flatMap((b) => [...(b.genres || []), ...(b.vibes || [])])
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));
  const shareCurrentReadsPool = [...currentReadsAll].sort((a, b) =>
    (a.title || "").localeCompare(b.title || ""),
  );
  const shareCurrentReadIdSet = new Set(shareCurrentReadsPool.map((b) => b.uid));
  const shareCurrentReadSelections = (shelfSharing.featuredCurrentReads || []).filter(
    (uid) => shareCurrentReadIdSet.has(uid),
  );
  const shareCurrentReadsPickerBooks = (() => {
    const selected = shareCurrentReadSelections
      .map((uid) => shareCurrentReadsPool.find((b) => b.uid === uid))
      .filter(Boolean);
    const selectedSet = new Set(selected.map((book) => book.uid));
    const unselected = shareCurrentReadsPool.filter((book) => !selectedSet.has(book.uid));
    return [...selected, ...unselected];
  })();
  const shareRecommendedPool = [...books]
    .sort((a, b) => {
      const aFav = a.fav ? 1 : 0;
      const bFav = b.fav ? 1 : 0;
      if (bFav !== aFav) return bFav - aFav;
      const rankStatus = (status) => {
        if (status === "Read") return 0;
        if (status === "In Progress") return 1;
        if (status === "Unstarted" || status === "Unread") return 2;
        if (status === dnfLabel || status === "DNF") return 3;
        return 4;
      };
      const statusDiff = rankStatus(a.status) - rankStatus(b.status);
      if (statusDiff) return statusDiff;
      const ratingDiff = (b.rating || 0) - (a.rating || 0);
      if (ratingDiff) return ratingDiff;
      const readDiff = dateSortKey(b.dateRead).localeCompare(dateSortKey(a.dateRead));
      if (readDiff) return readDiff;
      return (a.title || "").localeCompare(b.title || "");
    });
  const shareRecommendedPickerBooks = (() => {
    const selectedIds = shelfSharing.recommendedBookIds || [];
    const query = normalizeText(shareRecommendedQuery || "");
    const matchesQuery = (book) => {
      if (!query) return true;
      const haystack = normalizeText(
        [
          book.title,
          book.authors,
          book.seriesName,
          ...(book.genres || []),
          ...(book.vibes || []),
          ...(book.tags || []),
        ]
          .filter(Boolean)
          .join(" "),
      );
      return haystack.includes(query);
    };
    const selected = selectedIds
      .map((uid) => books.find((b) => b.uid === uid))
      .filter(Boolean)
      .filter(matchesQuery);
    const selectedSet = new Set(selected.map((book) => book.uid));
    const unselected = shareRecommendedPool.filter(
      (book) => !selectedSet.has(book.uid) && matchesQuery(book),
    );
    return [...selected, ...unselected];
  })();
  const SHARE_RECOMMEND_VISIBLE_LIMIT = 15;
  const shareRecommendedVisibleBooks = shareRecommendedQuery.trim()
    ? shareRecommendedPickerBooks
    : shareRecommendedPickerBooks.slice(
        0,
        Math.max(
          SHARE_RECOMMEND_VISIBLE_LIMIT,
          (shelfSharing.recommendedBookIds || []).length,
        ),
      );
  const sharedOwnerId = sharedRoute.ownerId || userId || "";
  const sharedOwnerIsCurrentUser = !!userId && !!sharedOwnerId && sharedOwnerId === userId;
  const sharedRouteActive =
    sharedRoute.source === "preview" || sharedRoute.source === "link";
  const sharedConfig = normalizeShelfSharingConfig(
    sharedOwnerIsCurrentUser ? shelfSharing : sharedProfileRemote || {},
  );
  const sharedDnfLabel = sharedOwnerIsCurrentUser
    ? dnfLabel
    : String(sharedProfileRemote?.dnfLabel || dnfLabel || "DNF");
  const sharedSourceBooks = sharedOwnerIsCurrentUser ? books : sharedBooksRemote;
  const sharedVisibleBooks = sharedSourceBooks.filter((b) =>
    isSharedShelfStatusVisible(b.status || "Unstarted", sharedConfig, sharedDnfLabel),
  );
  const sharedCurrentReadsSource = sharedVisibleBooks.filter(
    (b) => b.status === "In Progress",
  );
  const sharedAccessAuthorized =
    sharedRoute.source === "preview"
      ? sharedOwnerIsCurrentUser
      : sharedRoute.source === "link"
        ? sharedOwnerIsCurrentUser ||
          (!!sharedProfileRemote &&
            sharedProfileRemote.enabled !== false &&
            !sharedRemoteError)
        : false;
  const sharedAccessMessage = !sharedRouteActive
    ? "This bookshelf is invite-only."
    : sharedRemoteLoading
      ? "Loading shared bookshelf..."
      : sharedRoute.source === "link" && sharedRemoteError === "permission-denied"
        ? "This bookshelf is invite-only. Enter the invite code to unlock."
        : sharedRoute.source === "link" && sharedRemoteError
          ? "Could not load this bookshelf right now."
          : sharedRoute.source === "link" && !sharedProfileRemote
            ? "This bookshelf is not available."
            : sharedRoute.source === "link" && sharedProfileRemote?.enabled === false
              ? "This bookshelf is currently private."
              : sharedRoute.source === "link" && !sharedOwnerIsCurrentUser
        ? "This bookshelf is invite-only."
          : "This bookshelf is invite-only.";
  const sharedDisplayName = sharedOwnerIsCurrentUser
    ? headerGreetingName
    : String(sharedProfileRemote?.displayName || "Reader");
  const sharedReadCount = sharedOwnerIsCurrentUser
    ? books.filter(hasCompletedReadHistory).length
    : Number(
        sharedProfileRemote?.readCount ??
          sharedSourceBooks.filter(hasCompletedReadHistory).length,
      );
  const sharedMemberSinceYear = sharedOwnerIsCurrentUser
    ? computeMemberSinceYear(auth.currentUser)
    : String(sharedProfileRemote?.memberSinceYear || CUR_YR);
  const sharedBio = String(sharedConfig.bio || "").trim();
  const sharedHeroIsDark = true;
  const sharedHeroBackground = sharedHeroIsDark
    ? `linear-gradient(145deg, ${T.s2} 0%, ${T.surface} 52%, rgba(255,255,255,0.04) 100%)`
    : `linear-gradient(145deg, ${T.surface} 0%, ${T.s2} 58%, ${T.bg} 100%)`;
  const sharedHeroShadow = sharedHeroIsDark
    ? "0 18px 40px rgba(0,0,0,0.22)"
    : "0 14px 30px rgba(123,108,92,0.09)";
  const sharedSectionGap = 22;
  const sharedPromptQuestionColor = T.text;
  const sharedPromptAnswerColor = T.sub;
  const sharedCanOpenFullShelf = sharedOwnerIsCurrentUser || sharedConfig.showFullShelf !== false;
  const sharedUsesContinuousShelf = sharedConfig.fullShelfLayout === "continuous";
  const sharedStatusFilter = "All";
  const sharedShowsReadCount = sharedConfig.showReadCount !== false;
  const sharedMetaLine = sharedShowsReadCount
    ? `${sharedReadCount} books read · Member since ${sharedMemberSinceYear}`
    : `Member since ${sharedMemberSinceYear}`;
  const sharedSectionTitleStyle = {
    fontFamily: DISPLAY_SERIF,
    fontWeight: 700,
    fontSize: 20,
    color: T.text,
    lineHeight: 1.1,
    marginBottom: 12,
    textAlign: "center",
  };
  const sharedTopGenresFallback = (() => {
    const counts = {};
    sharedVisibleBooks.forEach((b) =>
      [...(b.genres || []), ...(b.vibes || [])]
        .filter(Boolean)
        .forEach((label) => {
          counts[label] = (counts[label] || 0) + 1;
        }),
    );
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([label]) => label)
      .slice(0, 5);
  })();
  const sharedTopGenres =
    sharedConfig.topGenres.length > 0
      ? sharedConfig.topGenres.slice(0, 5)
      : sharedTopGenresFallback;
  const sharedCurrentReads = (() => {
    if (sharedConfig.showReading === false) return [];
    const picked = (sharedConfig.featuredCurrentReads || [])
      .map((uid) => sharedCurrentReadsSource.find((b) => b.uid === uid))
      .filter(Boolean);
    return (picked.length ? picked : sharedCurrentReadsSource).slice(0, 3);
  })();
  const sharedPromptAnswers = (sharedConfig.promptAnswers || [])
    .map((entry) => ({
      prompt: String(entry?.prompt || "").trim(),
      answer: String(entry?.answer || "").trim(),
    }))
    .filter((entry) => entry.prompt && entry.answer)
    .slice(0, 5);
  const sharedRecommendedBooks = (() => {
    const picked = (sharedConfig.recommendedBookIds || [])
      .map((uid) => sharedSourceBooks.find((b) => b.uid === uid))
      .filter(Boolean);
    if (picked.length) return picked.slice(0, 6);
    return [...sharedVisibleBooks]
      .filter((b) => b.status === "Read")
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 6);
  })();
  const sharedShelfGenreOptions = [
    "All",
    ...Array.from(
      new Set(
        sharedSourceBooks
          .flatMap((b) => [...(b.genres || []), ...(b.vibes || [])])
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b)),
  ];
  const sharedShelfBooks = [...sharedSourceBooks]
    .filter((b) => {
      if (!isSharedShelfStatusVisible(b.status || "Unstarted", sharedConfig, sharedDnfLabel)) {
        return false;
      }
      if (sharedStatusFilter !== "All" && b.status !== sharedStatusFilter) return false;
      if (
        sharedShelfGenre !== "All" &&
        ![...(b.genres || []), ...(b.vibes || [])].includes(sharedShelfGenre)
      ) {
        return false;
      }
      if (sharedShelfQuery.trim()) {
        const terms = sharedShelfQuery.toLowerCase().trim().split(/\s+/);
        const haystack = [
          b.title,
          b.authors,
          ...(b.genres || []),
          ...(b.vibes || []),
          ...(b.tags || []),
        ]
          .join(" ")
          .toLowerCase();
        if (!terms.every((term) => haystack.includes(term))) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const rankDiff = statusRankForSmart(a.status) - statusRankForSmart(b.status);
      if (rankDiff) return rankDiff;
      const aTs = createdAtOrderValue(a);
      const bTs = createdAtOrderValue(b);
      if (bTs !== aTs) return bTs - aTs;
      return (a.title || "").localeCompare(b.title || "");
    });
  const sharedShelfSections = [
    {
      key: "In Progress",
      title: "Currently Reading",
      books: sharedShelfBooks.filter((b) => b.status === "In Progress"),
    },
    {
      key: "Read",
      title: "Read",
      books: sharedShelfBooks.filter((b) => b.status === "Read"),
    },
    {
      key: "Unstarted",
      title: "Unread",
      books: sharedShelfBooks.filter((b) => b.status === "Unstarted"),
    },
    {
      key: "DNF",
      title: sharedDnfLabel,
      books: sharedShelfBooks.filter((b) => isDnfStatus(b.status, sharedDnfLabel)),
    },
  ].filter((section) => section.books.length > 0);
  const sharedShelfGridColumns = isTabletViewport
    ? "repeat(3,minmax(0,1fr))"
    : "repeat(2,minmax(0,1fr))";
  const renderSharedShelfGrid = (items) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: sharedShelfGridColumns,
        gap: isTabletViewport ? 14 : 12,
        alignContent: "start",
      }}
    >
      {items.map((book) => (
        <div
          key={book.uid}
          style={{
            background: T.surface,
            borderRadius: 14,
            border: "1px solid " + T.border,
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              aspectRatio: "2 / 3",
              background: T.s2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {book.cover ? (
              <img
                src={book.cover}
                alt={book.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: isTabletViewport ? "contain" : "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: T.accentBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 26,
                  color: T.accent,
                }}
              >
                {(book.title || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div style={{ padding: "10px 11px 12px", textAlign: "center" }}>
            {book.seriesName && (
              <div
                style={{
                  fontSize: 10,
                  color: T.accent,
                  fontWeight: 600,
                  marginBottom: 3,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {book.seriesName}
                {book.seriesNum ? " #" + book.seriesNum : ""}
              </div>
            )}
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 14,
                color: T.text,
                lineHeight: 1.3,
                marginBottom: 3,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {book.title}
            </div>
            <div
              style={{
                fontSize: 11,
                color: T.sub,
                fontStyle: "italic",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {book.authors}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  function resetMyBooksRefinements() {
    setSortBy("smart");
    setFGenre("All");
    setFFmt("All");
    setFFav(false);
    setFStatus("All");
    setCollectionSq("");
  }

  const inp = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: "1px solid " + T.border,
    background: T.s2,
    color: T.text,
    fontFamily: SANS,
    fontSize: 14,
    outline: "none",
    lineHeight: 1.5,
  };

  function Chip({ on, onClick, children }) {
    return (
      <button
        onClick={onClick}
        style={{
          padding: "7px 14px",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 13,
          fontFamily: SANS,
          whiteSpace: "nowrap",
          transition: "all 0.15s",
          border: "1px solid " + (on ? T.accent : T.border),
          background: on ? T.accentBg : T.s2,
          color: on ? T.accent : T.sub,
          fontWeight: on ? 600 : 400,
        }}
      >
        {children}
      </button>
    );
  }

  const Label = useCallback(
    function Label({ children }) {
      return (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: T.mute,
            marginBottom: 10,
          }}
        >
          {children}
        </div>
      );
    },
    [T.mute],
  );
  function SettingsLabel({ children }) {
    return (
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: T.sub,
          marginBottom: 10,
        }}
      >
        {children}
      </div>
    );
  }
  function SettingsNavGroup({ children }) {
    return (
      <div
        style={{
          background: T.surface,
          borderRadius: 18,
          overflow: "hidden",
          marginBottom: 16,
        }}
      >
        {children}
      </div>
    );
  }
  function SettingsNavRow({ title, onClick, danger, last = false }) {
    return (
      <button
        onClick={onClick}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "18px 16px",
          border: "none",
          borderBottom: last ? "none" : "1px solid rgba(0,0,0,0.06)",
          background: T.surface,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 16,
            fontWeight: 600,
            color: danger ? "#C4584A" : T.text,
          }}
        >
          {title}
        </div>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke={danger ? "#C4584A" : settingsHintColor}
          strokeWidth="2"
          strokeLinecap="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    );
  }
  const Sec = useCallback(
    function Sec({ label, children }) {
      return (
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: T.mute,
              marginBottom: 10,
            }}
          >
            {label}
          </div>
          {children}
        </div>
      );
    },
    [T.mute],
  );
  function BigBtn({ onClick, children, secondary }) {
    return (
      <button
        onClick={onClick}
        style={{
          width: "100%",
          padding: "14px",
          borderRadius: 12,
          border: secondary ? "1px solid " + T.border : "none",
          background: secondary ? T.s2 : T.accent,
          color: secondary ? T.sub : "#fff",
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: SANS,
        }}
      >
        {children}
      </button>
    );
  }
  function highlight(text, query) {
    if (!query.trim()) return text;
    const terms = query.trim().split(/\s+/);
    const regex = new RegExp(
      `(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\")).join("|")})`,
      "gi",
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark
          key={i}
          style={{
            background: "rgba(43,79,130,0.18)",
            color: T.accent,
            borderRadius: 3,
            padding: "0 2px",
            fontWeight: 700,
          }}
        >
          {part}
        </mark>
      ) : (
        part
      ),
    );
  }
  function BookCover({ book, w, h, r }) {
    return book.cover ? (
      <img
        src={book.cover}
        alt=""
        style={{
          width: w,
          height: h,
          objectFit: "cover",
          borderRadius: r,
          flexShrink: 0,
        }}
      />
    ) : (
      <div
        style={{
          width: w,
          height: h,
          borderRadius: r,
          background: T.accentBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: DISPLAY_SERIF,
          fontSize: Math.floor(w / 3),
          color: T.accent,
          flexShrink: 0,
        }}
      >
        {(book.title || "??").slice(0, 2).toUpperCase()}
      </div>
    );
  }

  function StreakBadge() {
    if (!streak || !showStreak) return null;
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: "rgba(196,136,60,0.12)",
          border: "1px solid rgba(196,136,60,0.25)",
          borderRadius: 20,
          padding: "4px 10px",
        }}
      >
        <span style={{ fontSize: 14 }}>
          {streak >= 7 ? "\uD83D\uDD25" : "\u2728"}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#C4883C" }}>
          {streak} day streak
        </span>
      </div>
    );
  }

  function BottomNav({ active: act }) {
    const tabs = [
      {
        id: "mybooks",
        label: "MY BOOKS",
        d: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
      },
      {
        id: "reading",
        label: "READING",
        d: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      },
      {
        id: "nextup",
        label: "NEXT UP",
        d: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z",
      },
      {
        id: "settings",
        label: "SETTINGS",
        d: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
      },
    ];
    return (
      <nav
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: shellMaxWidth,
          background: T.surface,
          borderTop: "1px solid " + T.border,
          display: "flex",
          zIndex: 50,
          paddingBottom: "calc(8px + env(safe-area-inset-bottom))",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === "settings") {
                openSettingsHome();
                return;
              }
              setView(tab.id);
            }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "10px 0 4px",
              color: act === tab.id ? T.accent : T.mute,
              fontFamily: SANS,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={tab.d} />
            </svg>
            <span
              style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em" }}
            >
              {tab.label}
            </span>
          </button>
        ))}
      </nav>
    );
  }

  function RecapModal() {
    const topBook = [...finishedYr].sort(
      (a, b) => (b.rating || 0) - (a.rating || 0),
    )[0];
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.8)",
          zIndex: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(135deg," + T.accent + " 0%," + T.bg + " 100%)",
            borderRadius: 24,
            padding: 28,
            maxWidth: 340,
            width: "100%",
            position: "relative",
            boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          }}
        >
          <button
            onClick={() => setShowRecap(false)}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: 30,
              height: 30,
              cursor: "pointer",
              color: "#fff",
              fontSize: 16,
            }}
          >
            &#x2715;
          </button>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.7)",
                marginBottom: 6,
              }}
            >
              {yr} Reading Wrapped
            </div>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 48,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {finishedYr.length}
            </div>
            <div
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.8)",
                marginTop: 4,
              }}
            >
              books finished
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {[
              ["📄", pagesStatLabel, pagesStatValue],
              ["⭐", "Avg Rating", avgStar],
              [
                "\uD83C\uDFC6",
                "Top Genre",
                top5Genres[0] ? top5Genres[0][0] : "--",
              ],
              ["\uD83D\uDD25", "Streak", streak + " days"],
            ].map(([ico, lbl, val]) => (
              <div
                key={lbl}
                style={{
                  background: "rgba(255,255,255,0.12)",
                  borderRadius: 12,
                  padding: "12px 10px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 4 }}>{ico}</div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 16,
                    color: "#fff",
                  }}
                >
                  {val}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.6)",
                    marginTop: 2,
                  }}
                >
                  {lbl}
                </div>
              </div>
            ))}
          </div>
          {topBook && (
            <div
              style={{
                background: "rgba(255,255,255,0.12)",
                borderRadius: 14,
                padding: 14,
                display: "flex",
                gap: 12,
                alignItems: "center",
              }}
            >
              <BookCover book={topBook} w={44} h={60} r={8} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.6)",
                    marginBottom: 3,
                  }}
                >
                  Top Rated
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 14,
                    color: "#fff",
                    lineHeight: 1.3,
                  }}
                >
                  {topBook.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.6)",
                    fontStyle: "italic",
                    marginTop: 2,
                  }}
                >
                  {topBook.authors}
                </div>
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              textAlign: "center",
            }}
          >
            Screenshot to share your reading recap!
          </div>
        </div>
      </div>
    );
  }

  if (loading)
    return (
      <div
        style={{
          background: T.bg,
          minHeight: "100dvh",
          maxWidth: shellMaxWidth,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: DISPLAY_SERIF,
            fontSize: 28,
            color: T.text,
          }}
        >
          Loading your library...
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "3px solid " + T.border,
            borderTopColor: T.accent,
            animation: "spin 0.7s linear infinite",
          }}
        ></div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );

  return (
    <div
      style={{
        fontFamily: SANS,
        fontVariantNumeric: "lining-nums",
        fontOpticalSizing: "auto",
        background: T.bg,
        minHeight: "100dvh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        maxWidth: shellMaxWidth,
        margin: "0 auto",
        color: T.text,
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bitter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap');
        html,body,#root{height:100%;min-height:100dvh;background:${T.bg}}
        body{margin:0;overscroll-behavior-y:none;-webkit-overflow-scrolling:touch;background:${T.bg}}
        *{box-sizing:border-box;margin:0;padding:0;font-optical-sizing:auto}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:#ccc;border-radius:3px}
        button,input,select,textarea{font-family:inherit}
        #root{padding-top:0;background:${T.bg}}
        .bcard{cursor:pointer;border-radius:16px;overflow:hidden;transition:transform 0.2s,box-shadow 0.2s}
        .bcard:hover{transform:translateY(-4px)}
        .bcard:active{transform:scale(0.98)}
        .lrow{display:flex;align-items:center;gap:14px;padding:12px 20px;cursor:pointer;transition:background 0.12s}
        .lrow:hover{background:rgba(0,0,0,0.03)}
        .sheet-bg{position:fixed;inset:0;background:rgba(0,0,0,0.44);z-index:300;display:flex;flex-direction:column;justify-content:flex-end;animation:fi 0.18s ease}
        @keyframes fi{from{opacity:0}to{opacity:1}}
        .sheet{border-radius:22px 22px 0 0;animation:su 0.28s cubic-bezier(0.34,1.1,0.64,1)}
        @keyframes su{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .toast{position:fixed;top:18px;left:50%;transform:translateX(-50%);z-index:9999;padding:10px 20px;border-radius:100px;font-size:13px;font-weight:600;white-space:nowrap;box-shadow:0 8px 24px rgba(0,0,0,0.18);animation:tdi 0.22s ease}
        @keyframes tdi{from{opacity:0;transform:translateX(-50%) translateY(-6px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
        .spin{animation:spin 0.7s linear infinite}
        @keyframes spin{to{transform:rotate(360deg)}}
        .scanline{animation:sl 1.8s ease-in-out infinite}
        @keyframes sl{0%,100%{top:15%}50%{top:78%}}
        .stagger>*{animation:fu 0.35s ease both}
        .stagger>*:nth-child(1){animation-delay:0.03s}.stagger>*:nth-child(2){animation-delay:0.06s}.stagger>*:nth-child(3){animation-delay:0.09s}.stagger>*:nth-child(4){animation-delay:0.12s}.stagger>*:nth-child(5){animation-delay:0.15s}.stagger>*:nth-child(6){animation-delay:0.18s}.stagger>*:nth-child(7){animation-delay:0.21s}.stagger>*:nth-child(8){animation-delay:0.24s}
        @keyframes fu{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        input::placeholder,textarea::placeholder{color:#A8A29E}
      `}</style>

      {toast && (
        <div
          className="toast"
          style={{ background: toast.warn ? "#C4584A" : T.text, color: T.bg }}
        >
          {toast.msg}
        </div>
      )}
      {updateReady && !updateDismissed && (
        <div
          style={{
            position: "fixed",
            top: toast ? 66 : 18,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9998,
            width: "calc(100% - 24px)",
            maxWidth: Math.min(shellMaxWidth - 24, 420),
            background: T.surface,
            border: "1px solid " + T.border,
            borderRadius: 16,
            boxShadow: "0 12px 28px rgba(0,0,0,0.16)",
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                color: T.mute,
                marginBottom: 3,
              }}
            >
              Update Ready
            </div>
            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.5 }}>
              A newer version of Lea&apos;s Library is available.
            </div>
          </div>
          <button
            type="button"
            onClick={refreshToLatestVersion}
            style={{
              border: "none",
              background: T.accent,
              color: "#fff",
              borderRadius: 11,
              padding: "10px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: SANS,
              flexShrink: 0,
            }}
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setUpdateDismissed(true)}
            style={{
              border: "none",
              background: "transparent",
              color: T.mute,
              fontSize: 16,
              lineHeight: 1,
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
            aria-label="Dismiss update notice"
          >
            ✕
          </button>
        </div>
      )}
      {showScrollTop &&
        ["mybooks", "settings-private", "sharedprofile", "sharedbookshelf"].includes(
          view,
        ) && (
          <div
            style={{
              position: "fixed",
              left: "50%",
              bottom: "calc(92px + env(safe-area-inset-bottom))",
              transform: "translateX(-50%)",
              width: "100%",
              maxWidth: shellMaxWidth,
              padding: "0 16px",
              display: "flex",
              justifyContent: "flex-end",
              pointerEvents: "none",
              zIndex: 56,
            }}
          >
            <button
              type="button"
              onClick={scrollActivePanelToTop}
              style={{
                pointerEvents: "auto",
                border: "1px solid " + T.border,
                background: T.surface,
                color: T.text,
                borderRadius: 999,
                padding: "9px 12px",
                boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                fontFamily: SANS,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
              aria-label="Scroll back to top"
            >
              <span style={{ fontSize: 12, lineHeight: 1 }}>↑</span>
              <span>Top</span>
            </button>
          </div>
        )}
      {showRecap && <RecapModal />}
      {monthPeek && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 420,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setMonthPeek(null);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              maxHeight: "78dvh",
              overflow: "hidden",
              background: T.surface,
              borderRadius: 16,
              border: "1px solid " + T.border,
              boxShadow: "0 20px 44px rgba(0,0,0,0.28)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderBottom: "1px solid " + T.border,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.text,
                  }}
                >
                  {MONTHS_FULL[monthPeek.monthIndex]} {yr}
                </div>
                <div style={{ fontSize: 12, color: T.mute, marginTop: 2 }}>
                  {monthPeek.books.length} book
                  {monthPeek.books.length === 1 ? "" : "s"} finished
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMonthPeek(null)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div style={{ overflowY: "auto", padding: "2px 0" }}>
              {monthPeek.books.map((book) => (
                <div
                  key={book.uid || `${book.title}-${book.authors}-${book.dateRead || ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    borderBottom: "1px solid " + T.border,
                  }}
                >
                  <BookCover book={book} w={34} h={50} r={6} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: T.text,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {book.title}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: T.sub,
                        fontStyle: "italic",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {book.authors}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.mute, flexShrink: 0 }}>
                    {formatBookDate(book.dateRead)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {smartNextPick && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.58)",
            zIndex: 310,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSmartNextPick(null);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              borderRadius: 24,
              overflow: "hidden",
              background: T.surface,
              border: "1px solid " + T.border,
              boxShadow: "0 24px 70px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                position: "relative",
                padding: "22px 22px 18px",
                minHeight: 210,
                background: T.s2,
              }}
            >
              {smartNextPick.book.cover && (
                <>
                  <img
                    src={smartNextPick.book.cover}
                    alt=""
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "blur(26px) brightness(0.34) saturate(1.15)",
                      transform: "scale(1.08)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.62), rgba(0,0,0,0.78))",
                    }}
                  />
                </>
              )}
              <div style={{ position: "relative", zIndex: 2 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.72)",
                    }}
                  >
                    {smartNextPick.headline}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.6)",
                      flexShrink: 0,
                    }}
                  >
                    Pick {smartNextPick.offerNumber} of {smartNextPick.totalOffers}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-end" }}>
                  <BookCover book={smartNextPick.book} w={82} h={118} r={10} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontFamily: DISPLAY_SERIF,
                        fontSize: 28,
                        lineHeight: 1.02,
                        color: "#fff",
                        textShadow: "0 2px 10px rgba(0,0,0,0.55)",
                        marginBottom: 6,
                      }}
                    >
                      {smartNextPick.book.title}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.82)",
                        fontStyle: "italic",
                        marginBottom: 10,
                      }}
                    >
                      {smartNextPick.book.authors}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      {(smartNextPick.book.genres || [])
                        .slice(0, 2)
                        .map((genre) => (
                          <span
                            key={genre}
                            style={{
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: "rgba(255,255,255,0.1)",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 600,
                              border: "1px solid rgba(255,255,255,0.12)",
                            }}
                          >
                            {genre}
                          </span>
                        ))}
                      {smartNextPick.inQueue && (
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.1)",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 600,
                            border: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          In Next Up
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: "18px 20px 20px" }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: T.mute,
                  marginBottom: 8,
                }}
              >
                Why this pick
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: T.sub,
                  lineHeight: 1.7,
                  marginBottom: 14,
                }}
              >
                {smartNextPick.detailLine}
              </div>

              {(smartNextPick.matchedVibes.length > 0 ||
                smartNextPick.matchedGenres.length > 0) && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 16,
                  }}
                >
                  {smartNextPick.matchedGenres.slice(0, 2).map((genre) => (
                    <span
                      key={genre}
                      style={{
                        background: T.accentBg,
                        color: T.accent,
                        borderRadius: 999,
                        padding: "6px 11px",
                        fontSize: 11,
                        fontWeight: 600,
                        border: "1px solid " + T.border,
                      }}
                    >
                      {genre}
                    </span>
                  ))}
                  {smartNextPick.matchedVibes.slice(0, 2).map((vibe) => (
                    <span
                      key={vibe}
                      style={{
                        background: "rgba(196,96,122,0.08)",
                        color: "#C4607A",
                        borderRadius: 999,
                        padding: "6px 11px",
                        fontSize: 11,
                        fontWeight: 600,
                        border: "1px solid rgba(196,96,122,0.15)",
                      }}
                    >
                      {vibe}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => startSmartPickedBook(smartNextPick.book)}
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: 12,
                    border: "none",
                    background: T.accent,
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: SANS,
                  }}
                >
                  Start Reading
                </button>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={rejectSmartNextPick}
                    disabled={!smartNextPick.canPickAgain}
                    style={{
                      flex: 1,
                      padding: "13px",
                      borderRadius: 12,
                      border: "1px solid " + T.border,
                      background: smartNextPick.canPickAgain ? T.s2 : T.border,
                      color: smartNextPick.canPickAgain ? T.sub : T.mute,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: smartNextPick.canPickAgain ? "pointer" : "default",
                      fontFamily: SANS,
                      opacity: smartNextPick.canPickAgain ? 1 : 0.8,
                    }}
                  >
                    {smartNextPick.canPickAgain ? "Pick Again" : "End of picks"}
                  </button>
                  <button
                    onClick={() => setSmartNextPick(null)}
                    style={{
                      flex: 1,
                      padding: "13px",
                      borderRadius: 12,
                      border: "1px solid " + T.border,
                      background: T.surface,
                      color: T.text,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: SANS,
                    }}
                  >
                    Not Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSignOut && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.44)",
            zIndex: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSignOut(false);
          }}
        >
          <div
            style={{
              background: T.surface,
              borderRadius: "22px 22px 0 0",
              padding: "20px 24px 40px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              ></div>
            </div>
            <div
              style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}
            >
              🚪
            </div>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 22,
                color: T.text,
                marginBottom: 6,
                textAlign: "center",
              }}
            >
              Sign Out?
            </div>
            <div
              style={{
                fontSize: 14,
                color: T.mute,
                textAlign: "center",
                marginBottom: 24,
                lineHeight: 1.6,
              }}
            >
              Your library will be waiting for you when you come back!
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => {
                  import("firebase/auth").then(({ signOut }) => signOut(auth));
                  setShowSignOut(false);
                }}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  background: "#C4584A",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Yes, Sign Out
              </button>
              <button
                onClick={() => setShowSignOut(false)}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}

      {resetScope && resetInfo && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.44)",
            zIndex: 320,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeResetSheet();
          }}
        >
          <div
            style={{
              background: T.surface,
              borderRadius: "22px 22px 0 0",
              padding: "20px 24px 40px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              ></div>
            </div>
            <div
              style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}
            >
              {resetInfo.icon}
            </div>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 22,
                color: T.text,
                marginBottom: 6,
                textAlign: "center",
              }}
            >
              {resetInfo.title}
            </div>
            <div
              style={{
                fontSize: 14,
                color: T.mute,
                textAlign: "center",
                marginBottom: resetNeedsPhrase ? 14 : 24,
                lineHeight: 1.6,
              }}
            >
              {resetInfo.body}
            </div>

            {resetNeedsPhrase && (
              <div style={{ marginBottom: 18 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: T.mute,
                    marginBottom: 8,
                  }}
                >
                  Type RESET to confirm
                </div>
                <input
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  placeholder="RESET"
                  style={{
                    ...inp,
                    textTransform: "uppercase",
                  }}
                />
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={confirmResetCollection}
                disabled={!resetPhraseValid || resetBusy}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  background: !resetPhraseValid || resetBusy ? T.border : "#C4584A",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: !resetPhraseValid || resetBusy ? "default" : "pointer",
                  fontFamily: SANS,
                  opacity: !resetPhraseValid || resetBusy ? 0.7 : 1,
                }}
              >
                {resetBusy ? "Working..." : resetInfo.cta}
              </button>
              <button
                onClick={closeResetSheet}
                disabled={resetBusy}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: resetBusy ? "default" : "pointer",
                  fontFamily: SANS,
                  opacity: resetBusy ? 0.7 : 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ADD SHEET */}
      {addSheet && (
        <div
          className="sheet-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAddSheet(false);
          }}
          onTouchEnd={(e) => {
            if (e.target === e.currentTarget) setAddSheet(false);
          }}
        >
          <div className="sheet" style={{ background: T.surface }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0 4px",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              ></div>
            </div>
            <div
              style={{
                padding: "10px 22px 16px",
                borderBottom: "1px solid " + T.border,
              }}
            >
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 22,
                  color: T.text,
                }}
              >
                Add a Book
              </div>
            </div>
            <div
              style={{
                padding: "14px 20px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {[
                {
                  icon: "🔍",
                  title: "Search",
                  sub: "By title, author, or ISBN",
                  go: () => {
                    setAddSheet(false);
                    setSq("");
                    setResults([]);
                    setSearchMode("auto");
                    setView("search");
                  },
                },
                {
                  icon: "📷",
                  title: "Scan Barcode",
                  sub: "Use your camera",
                  go: () => {
                    setAddSheet(false);
                    setView("scan");
                    setTimeout(startScan, 80);
                  },
                },
                {
                  icon: "⬆️",
                  title: "Import CSV",
                  sub: "Goodreads or other CSV exports",
                  go: () => {
                    setAddSheet(false);
                    openImportView(view);
                  },
                },
                ...(addTarget === "nextup"
                  ? [
                      {
                        icon: "📚",
                        title: "From My Books",
                        sub: "Add books you already own",
                        go: () => {
                          setAddSheet(false);
                          setNextUpFromLibrarySq("");
                          setView("nextupfromlibrary");
                        },
                      },
                    ]
                  : []),
              ].map((opt) => (
                <button
                  key={opt.title}
                  onClick={opt.go}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    borderRadius: 14,
                    border: "1px solid " + T.border,
                    background: T.s2,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 11,
                      background: T.accentBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    {opt.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ fontSize: 15, fontWeight: 600, color: T.text }}
                    >
                      {opt.title}
                    </div>
                    <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
                      {opt.sub}
                    </div>
                  </div>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.mute}
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FILTER SHEET */}
      {filterSheet && (
        <div
          className="sheet-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setFilterSheet(false);
          }}
        >
          <div className="sheet" style={{ background: T.surface }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0 4px",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              ></div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 22px 14px",
                borderBottom: "1px solid " + T.border,
              }}
            >
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 20,
                  color: T.text,
                }}
              >
                Filter & Sort
              </div>
              {hasActiveRefinements && (
                <button
                  onClick={resetMyBooksRefinements}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.accent,
                    fontFamily: SANS,
                  }}
                >
                  Reset all
                </button>
              )}
            </div>
            <div
              style={{
                padding: "16px 22px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <div>
                <Label>Sort by</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {[
                    ["smart", "Smart"],
                    ["added", "Date Added"],
                    ["az", "A to Z"],
                    ["za", "Z to A"],
                    ["rating", "Top Rated"],
                    ["author", "Author"],
                  ].map(([v, l]) => (
                    <Chip
                      key={v}
                      on={sortBy === v}
                      onClick={() => setSortBy(v)}
                    >
                      {l}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <Label>Format</Label>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {[
                    ["All", "All"],
                    ["Physical", "📖 Physical"],
                    ["E-Book", "📱 E-Book"],
                    ["Audiobook", "🎧 Audiobook"],
                  ].map(([v, l]) => (
                    <Chip key={v} on={fFmt === v} onClick={() => setFFmt(v)}>
                      {l}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <Label>Genre / Vibe</Label>
                <select
                  value={fGenre}
                  onChange={(e) => setFGenre(e.target.value)}
                  style={{ ...inp }}
                >
                  {allTagOptions.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Favorites only</Label>
                <Chip on={fFav} onClick={() => setFFav((f) => !f)}>
                  {fFav ? "❤️ On" : "Show Favorites"}
                </Chip>
              </div>
              <BigBtn onClick={() => setFilterSheet(false)}>Done</BigBtn>
            </div>
          </div>
        </div>
      )}

      {metadataReportOpen && metadataReportBook && (
        <div
          className="sheet-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeMetadataReport();
          }}
        >
          <div className="sheet" style={{ background: T.surface }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0 4px",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 22px 14px",
                borderBottom: "1px solid " + T.border,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.text,
                  }}
                >
                  Fix Book Info
                </div>
                <div style={{ fontSize: 12, color: T.sub, marginTop: 4 }}>
                  Select everything that looks off and we’ll review it.
                </div>
              </div>
              <button
                onClick={closeMetadataReport}
                disabled={metadataReportBusy}
                style={{
                  background: "none",
                  border: "none",
                  cursor: metadataReportBusy ? "default" : "pointer",
                  color: T.mute,
                  fontSize: 18,
                  padding: 0,
                }}
                aria-label="Close report sheet"
              >
                ✕
              </button>
            </div>
            <div
              style={{
                padding: "16px 22px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid " + T.border,
                  background: T.s2,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: T.text,
                    lineHeight: 1.35,
                  }}
                >
                  {metadataReportBook.title || "Unknown Title"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: T.sub,
                    marginTop: 3,
                    fontStyle: "italic",
                  }}
                >
                  {metadataReportBook.authors || "Unknown Author"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: T.mute,
                    marginBottom: 8,
                  }}
                >
                  What needs fixing?
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: T.sub,
                    marginBottom: 10,
                    lineHeight: 1.5,
                  }}
                >
                  You can choose more than one.
                </div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {METADATA_REPORT_TYPES.map(([value, label]) => (
                    <Chip
                      key={value}
                      on={metadataReportIssues.includes(value)}
                      onClick={() => toggleMetadataReportIssue(value)}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: T.mute,
                    marginBottom: 8,
                  }}
                >
                  Extra notes
                </div>
                <textarea
                  value={metadataReportNote}
                  onChange={(e) => setMetadataReportNote(e.target.value)}
                  rows={4}
                  placeholder="Anything else we should know?"
                  style={{ ...inp, resize: "vertical", lineHeight: 1.6 }}
                />
              </div>
              <button
                onClick={submitMetadataReport}
                disabled={metadataReportBusy}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  background: metadataReportBusy ? T.border : T.accent,
                  color: metadataReportBusy ? T.mute : "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: metadataReportBusy ? "default" : "pointer",
                  fontFamily: SANS,
                }}
              >
                {metadataReportBusy ? "Sending..." : "Send Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {metadataReportSentOpen && (
        <div
          className="sheet-bg"
          onClick={() => setMetadataReportSentOpen(false)}
          style={{
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: T.surface,
              border: "1px solid " + T.border,
              borderRadius: 18,
              boxShadow: "0 22px 70px rgba(0,0,0,0.22)",
              padding: "22px 20px 18px",
            }}
          >
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 22,
                color: T.text,
                marginBottom: 8,
              }}
            >
              Sent for Review
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.7,
                color: T.sub,
                marginBottom: 16,
              }}
            >
              Reviews usually take 24 to 48 hours. Once a fix is approved, it will be
              included in the next metadata refresh.
            </div>
            <button
              onClick={() => setMetadataReportSentOpen(false)}
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "none",
                background: T.accent,
                color: "#fff",
                fontSize: 14,
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: SANS,
              }}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* MY BOOKS */}
      {view === "mybooks" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "14px 16px 0",
              borderBottom: "1px solid " + T.border,
              position: "sticky",
              top: 0,
              zIndex: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontWeight: 400,
                    fontSize: 24,
                    color: T.text,
                    lineHeight: 1.05,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  Hi, {headerGreetingName}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    background: T.s2,
                    borderRadius: 9,
                    border: "1px solid " + T.border,
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setGridMode(true)}
                    style={{
                      width: 32,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: gridMode ? T.accent : "transparent",
                      color: gridMode ? "#fff" : T.mute,
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setGridMode(false)}
                    style={{
                      width: 32,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: !gridMode ? T.accent : "transparent",
                      color: !gridMode ? "#fff" : T.mute,
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={() => {
                    setAddTarget("library");
                    setAddSheet(true);
                  }}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 11,
                    border: "none",
                    background: T.accent,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
          >
              <svg
                width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Summary strip */}
            {books.length > 0 && (
              <div
                style={{
                  borderTop: "1px solid " + T.border,
                  padding: "10px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontWeight: 500,
                      fontSize: 19,
                      color: T.text,
                      lineHeight: 1,
                    }}
                  >
                    {collectionCountLabel}
                  </span>
                  <span
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 15,
                      fontWeight: 400,
                      color: T.text,
                    }}
                  >
                    book{ownedCount !== 1 ? "s" : ""} in your collection
                  </span>
                </div>
              </div>
            )}
            {/* Collection search */}
            <div style={{ padding: "8px 16px 0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: collectionFocused ? T.surface : T.s2,
                    borderRadius: 12,
                    padding: "0 14px",
                    border:
                      "1.5px solid " + (collectionFocused ? T.accent : T.border),
                    gap: 10,
                    transition: "all 0.2s",
                    flex: 1,
                    minWidth: 0,
                    position: "relative",
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={collectionFocused ? T.accent : T.mute}
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    value={collectionSq}
                    onChange={(e) => setCollectionSq(e.target.value)}
                    onFocus={() => setCollectionFocused(true)}
                    onBlur={() => setCollectionFocused(false)}
                    placeholder="Search titles, authors, tags, vibes..."
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: 13,
                      color: T.text,
                      padding: collectionSq ? "9px 30px 9px 0" : "9px 0",
                      fontFamily: SANS,
                    }}
                  />
                  {collectionSq && (
                    <button
                      onClick={() => setCollectionSq("")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: T.mute,
                        width: 20,
                        height: 20,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      aria-label="Clear search"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      >
                        <path d="M2 2l8 8" />
                        <path d="M10 2 2 10" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setFilterSheet(true)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border:
                      "1px solid " +
                      (hasActiveRefinements ? T.accent : T.border),
                    background: hasActiveRefinements ? T.accentBg : T.s2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={hasActiveRefinements ? T.accent : T.mute}
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                    <line x1="11" y1="18" x2="13" y2="18" />
                  </svg>
                </button>
                {hasActiveRefinements && (
                  <button
                    onClick={resetMyBooksRefinements}
                    style={{
                      height: 34,
                      borderRadius: 9,
                      border: "1px solid " + T.border,
                      background: T.s2,
                      color: T.accent,
                      padding: "0 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: SANS,
                      flexShrink: 0,
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>
              {matchedTags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    padding: "8px 0 4px",
                  }}
                >
                  {matchedTags.slice(0, 6).map((t) => (
                    <button
                      key={t}
                      onClick={() => setCollectionSq(t)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        border: "1px solid " + T.border,
                        background: T.s2,
                        color: T.sub,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: SANS,
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status tabs */}
            <div style={{ display: "flex", background: T.surface }}>
              {["All", "Unstarted", "In Progress", "Read", dnfLabel].map(
                (s) => (
                  <button
                    key={s}
                    onClick={() => setFStatus(s)}
                    style={{
                      flex: 1,
                      padding: "9px 2px 7px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      borderBottom:
                        "2.5px solid " +
                        (fStatus === s ? T.accent : "transparent"),
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: fStatus === s ? T.accent : T.mute,
                      }}
                    >
                      {s === "In Progress"
                        ? "READING"
                        : s === "Unstarted"
                          ? "UNREAD"
                          : s.toUpperCase()}
                    </div>
                  </button>
                ),
              )}
            </div>
          </header>

          {books.length === 0 && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 32px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontWeight: 700,
                  fontSize: 26,
                  color: T.text,
                  marginBottom: 10,
                }}
              >
                Your collection awaits
              </div>
              <div
                style={{
                  fontSize: 15,
                  color: T.sub,
                  lineHeight: 1.6,
                  marginBottom: 28,
                }}
              >
                Start adding books you own
              </div>
              <button
                onClick={() => {
                  setAddTarget("library");
                  setAddSheet(true);
                }}
                style={{
                  padding: "14px 28px",
                  borderRadius: 12,
                  border: "none",
                  background: T.accent,
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                + Add Your First Book
              </button>
              <button
                onClick={() => openImportView("mybooks")}
                style={{
                  marginTop: 10,
                  padding: "12px 24px",
                  borderRadius: 12,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Import from CSV
              </button>
            </div>
          )}

          {books.length > 0 && gridMode && (
            <div
              ref={myBooksScrollRef}
              onScroll={(e) => handleScrollTopVisibility(e.currentTarget.scrollTop)}
              className="stagger"
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: myBooksGridColumns,
                gap: isTabletViewport ? 14 : 12,
                padding: `14px 14px ${bottomNavPad}`,
                alignContent: "start",
              }}
            >
              {shown.length === 0 && (
                <div
                  style={{
                    gridColumn: "1/-1",
                    textAlign: "center",
                    padding: "60px 20px",
                  }}
                >
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>
                    🔍
                  </div>
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontWeight: 700,
                      fontSize: 18,
                      color: T.sub,
                    }}
                  >
                    No books match
                  </div>
                </div>
              )}
              {shown.map((book) => (
                <div
                  key={book.uid}
                  className="bcard"
                  onClick={() => openDetail(book)}
                  style={{
                    background: T.surface,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                  }}
                >
                  <div
                    style={{
                      aspectRatio: "2 / 3",
                      position: "relative",
                      background: T.s2,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {book.cover ? (
                      <img
                        src={book.cover}
                        alt={book.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: isTabletViewport ? "contain" : "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: T.accentBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: DISPLAY_SERIF,
                          fontSize: 28,
                          color: T.accent,
                        }}
                      >
                        {book.title.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    {book.fav && (
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          fontSize: 14,
                        }}
                      >
                        {"\u2764\uFE0F"}
                      </div>
                    )}
                    {book.status === dnfLabel && (
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          background: "rgba(184,76,76,0.9)",
                          color: "#fff",
                          fontSize: 9,
                          fontWeight: 700,
                          padding: "3px 7px",
                          borderRadius: 5,
                        }}
                      >
                        {dnfLabel}
                      </div>
                    )}
                    {book.status === "In Progress" && (
                      <button
                        onClick={(e) => logReadToday(book.uid, e)}
                        style={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          width: 30,
                          height: 30,
                          background: hasLoggedToday(book)
                            ? STATUS.Read.color
                            : T.accentBg,
                          border: hasLoggedToday(book)
                            ? "none"
                            : "1px solid " + T.border,
                          borderRadius: 9,
                          padding: 0,
                          color: hasLoggedToday(book) ? "#fff" : T.accent,
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        aria-label={
                          hasLoggedToday(book)
                            ? "Already logged today"
                            : "Log reading today"
                        }
                      >
                        &#10003;
                      </button>
                    )}
                  </div>
                  <div style={{ padding: "11px 12px 14px" }}>
                    {book.seriesName && (
                      <div
                        style={{
                          fontSize: 10,
                          color: T.accent,
                          fontWeight: 600,
                          marginBottom: 3,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {book.seriesName}
                        {book.seriesNum ? " #" + book.seriesNum : ""}
                      </div>
                    )}
                    <div
                      style={{
                        fontFamily: DISPLAY_SERIF,
                        fontWeight: 600,
                        fontSize: 12,
                        color: T.text,
                        lineHeight: 1.35,
                        marginBottom: 2,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {book.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: T.mute,
                        fontStyle: "italic",
                        marginBottom:
                          showBookRatings && (book.rating || 0) > 0
                            ? 9
                            : 0,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {book.authors}
                    </div>
                    {showBookRatings && (book.rating || 0) > 0 && (
                      <>
                        <div style={{ marginTop: 0 }}>
                          <HalfStars val={book.rating || 0} size={13} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {books.length > 0 && !gridMode && (
            <div
              ref={myBooksScrollRef}
              onScroll={(e) => handleScrollTopVisibility(e.currentTarget.scrollTop)}
              className="stagger"
              style={{ flex: 1, paddingBottom: bottomNavPad }}
            >
              {shown.length === 0 && (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>
                    🔍
                  </div>
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontWeight: 700,
                      fontSize: 18,
                      color: T.sub,
                    }}
                  >
                    No books match
                  </div>
                </div>
              )}
              {shown.map((book) => (
                <div
                  key={book.uid}
                  className="lrow"
                  style={{
                    borderBottom: "1px solid " + T.border,
                    background: T.surface,
                  }}
                  onClick={() => openDetail(book)}
                >
                  <BookCover book={book} w={46} h={64} r={8} />
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {book.seriesName && (
                      <div
                        style={{
                          fontSize: 10,
                          color: T.accent,
                          fontWeight: 600,
                          marginBottom: 2,
                        }}
                      >
                        {book.seriesName}
                        {book.seriesNum ? " #" + book.seriesNum : ""}
                      </div>
                    )}
                    <div
                      style={{
                        fontFamily: DISPLAY_SERIF,
                        fontSize: 15,
                        color: T.text,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {highlight(book.title, collectionSq)}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: T.sub,
                        fontStyle: "italic",
                        margin: "2px 0 4px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {highlight(book.authors, collectionSq)}
                    </div>
                    {showBookRatings && book.rating > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          flexWrap: "wrap",
                          marginTop: 2,
                        }}
                      >
                        <HalfStars val={book.rating} size={11} />
                      </div>
                    )}
                  </div>
                  {book.status === "In Progress" && (
                    <button
                      onClick={(e) => logReadToday(book.uid, e)}
                      style={{
                        alignSelf: "center",
                        width: 34,
                        height: 34,
                        background: hasLoggedToday(book)
                          ? STATUS.Read.color
                          : T.accentBg,
                        border: hasLoggedToday(book)
                          ? "none"
                          : "1px solid " + T.border,
                        borderRadius: 9,
                        padding: 0,
                        color: hasLoggedToday(book) ? "#fff" : T.accent,
                        fontSize: 16,
                        fontWeight: 700,
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                      aria-label={
                        hasLoggedToday(book)
                          ? "Already logged today"
                          : "Log reading today"
                      }
                    >
                      &#10003;
                    </button>
                  )}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.mute}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ alignSelf: "center", flexShrink: 0 }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              ))}
            </div>
          )}
          <BottomNav active="mybooks" />
        </div>
      )}

      {/* READING TAB */}
      {view === "reading" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "20px 20px 16px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              zIndex: 20,
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 22,
                  color: T.text,
                }}
              >
                Reading
              </div>
              {showStreak && streak > 0 && (
                <div style={{ marginTop: 6 }}>
                  <StreakBadge />
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                onClick={() => setShowRecap(true)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontFamily: SANS,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                ✨ Wrapped
              </button>
              <select
                value={yr}
                onChange={(e) => setYr(Number(e.target.value))}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.text,
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {displayYrs.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </header>

          <div
            style={{ flex: 1, overflowY: "auto", padding: `18px 16px ${bottomNavPad}` }}
          >
            {/* Currently Reading */}
            {currentReads.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    ...readingSectionHeadingStyle,
                    marginBottom: 12,
                  }}
                >
                  {currentReadsHeading}
                </div>
                {currentReads.map((b) => (
                  <div
                    key={b.uid}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      background: T.surface,
                      borderRadius: 14,
                      padding: "12px 14px",
                      marginBottom: 10,
                      border: "1px solid " + T.border,
                      cursor: "pointer",
                    }}
                    onClick={() => openDetail(b)}
                  >
                    <BookCover book={b} w={46} h={64} r={8} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {b.seriesName && (
                        <div
                          style={{
                            fontSize: 10,
                            color: T.accent,
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          {b.seriesName}
                          {b.seriesNum ? " #" + b.seriesNum : ""}
                        </div>
                      )}
                      <div
                        style={{
                          fontFamily: DISPLAY_SERIF,
                          fontSize: 15,
                          color: T.text,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {b.title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: T.sub,
                          fontStyle: "italic",
                          margin: "2px 0 4px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.authors}
                      </div>
                    </div>
                    <button
                      onClick={(e) => logReadToday(b.uid, e)}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 9,
                        border: hasLoggedToday(b)
                          ? "none"
                          : "1px solid " + T.border,
                        background: hasLoggedToday(b)
                          ? STATUS.Read.color
                          : T.accentBg,
                        color: hasLoggedToday(b) ? "#fff" : T.accent,
                        cursor: "pointer",
                        fontSize: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontWeight: 700,
                      }}
                      aria-label={
                        hasLoggedToday(b)
                          ? "Already logged today"
                          : "Log reading today"
                      }
                    >
                      &#10003;
                    </button>
                  </div>
                ))}
              </div>
            )}

            {currentReads.length === 0 && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  padding: "20px",
                  marginBottom: 20,
                  border: "1px solid " + T.border,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 16,
                    color: T.text,
                    marginBottom: 4,
                  }}
                >
                  {selectedYear === CUR_YR ? "Nothing in progress" : "No reading activity in " + selectedYear}
                </div>
                <div style={{ fontSize: 13, color: T.mute }}>
                  {selectedYear === CUR_YR
                    ? "Pick something from Next Up or add a book you're reading"
                    : "Try another year or add started dates for older reads."}
                </div>
              </div>
            )}

            {/* Reading Goal Progress */}
            {(
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    ...readingSectionHeadingStyle,
                    marginBottom: 12,
                  }}
                >
                  Year Snapshot
                </div>
                <div
                  style={{
                    background: T.accent,
                    borderRadius: 20,
                    padding: "22px",
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(43,79,130,0.2)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      right: -24,
                      top: -24,
                      width: 130,
                      height: 130,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.06)",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      opacity: 0.82,
                      marginBottom: 16,
                    }}
                  >
                    {selectedYear} Reading
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0,
                      marginBottom: 18,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        borderRight: "1px solid rgba(255,255,255,0.18)",
                        paddingRight: 20,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: DISPLAY_SERIF,
                          fontSize: 58,
                          lineHeight: 1,
                          letterSpacing: "-0.04em",
                        }}
                      >
                        {finishedYr.length}
                      </div>
                      <div
                        style={{ fontSize: 13, opacity: 0.8, marginTop: 5 }}
                      >
                        book{finishedYr.length !== 1 ? "s" : ""} finished
                      </div>
                    </div>
                    <div style={{ flex: 1, paddingLeft: 20 }}>
                      <div style={{ fontSize: 32, marginBottom: 6 }}>
                        {goalHeroEmoji}
                      </div>
                      <div
                        style={{
                          fontFamily: DISPLAY_SERIF,
                          fontSize: 16,
                          lineHeight: 1.35,
                          marginBottom: 4,
                        }}
                      >
                        {goalHeroMsg}
                      </div>
                      <div style={{ fontSize: 13, opacity: 0.78 }}>
                        {goalDeltaMsg}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.18)",
                      borderRadius: 8,
                      height: 8,
                      overflow: "hidden",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        height: "100%",
                        borderRadius: 8,
                        width: hasGoalForYear ? goalPct + "%" : "0%",
                        transition: "width 0.8s ease",
                        boxShadow: "0 0 8px rgba(255,255,255,0.4)",
                      }}
                    />
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div style={{ fontSize: 12, opacity: 0.76 }}>
                      {hasGoalForYear
                        ? finishedYr.length + " of " + goalTarget + " books"
                        : finishedYr.length + " books finished"}
                    </div>
                    <div
                      style={{ fontSize: 13, fontWeight: 700, opacity: 0.92 }}
                    >
                      {hasGoalForYear ? goalPct + "%" : "No goal"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recently Finished */}
            {recentlyFinished.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    ...readingSectionHeadingStyle,
                    marginBottom: 12,
                  }}
                >
                  Recently Finished ({selectedYear})
                </div>
                {recentlyFinished.map((b) => (
                  <div
                    key={b.uid}
                    className="lrow"
                    style={{
                      borderBottom: "1px solid " + T.border,
                      background: T.surface,
                      borderRadius: 0,
                    }}
                    onClick={() => openDetail(b)}
                  >
                    <BookCover book={b} w={42} h={58} r={7} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          ...readingListTitleStyle,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.title}
                      </div>
                      <div
                        style={{
                          ...readingListAuthorStyle,
                          margin: "2px 0 4px",
                        }}
                      >
                        {b.authors}
                      </div>
                      <HalfStars val={b.rating || 0} size={11} />
                    </div>
                    {b.dateRead && (
                      <div
                        style={{ fontSize: 12, color: T.sub, flexShrink: 0 }}
                      >
                        {formatBookDate(b.dateRead)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Stats */}
            <div
              style={{
                ...analyticsHeadingStyle,
                marginBottom: 12,
              }}
            >
              {selectedYear} Stats
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 12,
              }}
            >
              {[
                [pagesStatLabel, pagesStatValue, "📄"],
                ["Avg Stars", avgStar, "⭐"],
                [streakCardLabel, streakCardValue, "🔥"],
              ].map(([lbl, val, ico]) => (
                <div
                  key={lbl}
                  style={{
                    background: T.surface,
                    borderRadius: 13,
                    padding: "13px 10px",
                    textAlign: "center",
                    border: "1px solid " + T.border,
                  }}
                >
                  <div style={{ fontSize: 18, marginBottom: 5 }}>{ico}</div>
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 16,
                      color: T.text,
                    }}
                  >
                    {val}
                  </div>
                  <div
                    style={{
                      ...analyticsHeadingStyle,
                      fontSize: isDarkTheme ? 9 : 10,
                      letterSpacing: isDarkTheme ? "0.1em" : "0.09em",
                      marginTop: 3,
                    }}
                  >
                    {lbl}
                  </div>
                </div>
              ))}
            </div>
            {(shortest || longest || fastest) && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  overflow: "hidden",
                  border: "1px solid " + T.border,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    padding: "13px 16px 10px",
                    borderBottom: "1px solid " + T.border,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      ...analyticsHeadingStyle,
                    }}
                  >
                    Highlights
                  </div>
                </div>
                {[
                  shortest && {
                    lbl: "Shortest Read",
                    book: shortest,
                    detail: shortest.pages + "p",
                  },
                  longest && {
                    lbl: "Longest Read",
                    book: longest,
                    detail: longest.pages + "p",
                  },
                  fastest && {
                    lbl: "Fastest Read",
                    book: fastest,
                    detail:
                      fastest.days + " day" + (fastest.days === 1 ? "" : "s"),
                  },
                ]
                  .filter(Boolean)
                  .map(({ lbl, book, detail }) => (
                    <div
                      key={lbl}
                      onClick={() => openDetail(book)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 16px",
                        borderBottom: "1px solid " + T.border,
                        cursor: "pointer",
                      }}
                    >
                      <BookCover book={book} w={32} h={44} r={5} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: T.mute,
                            marginBottom: 2,
                          }}
                        >
                          {lbl}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: T.text,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {book.title}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: T.accent,
                          flexShrink: 0,
                        }}
                      >
                        {detail}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {top5Genres.length > 0 && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  padding: "16px",
                  marginBottom: 12,
                  border: "1px solid " + T.border,
                }}
              >
                <div
                  style={{
                    ...analyticsHeadingStyle,
                    marginBottom: 14,
                    textAlign: "center",
                  }}
                >
                  Top Genres
                </div>
                {top5Genres.map(([g, n], i) => {
                  const pct = Math.round((n / finishedYr.length) * 100);
                  return (
                    <div
                      key={g}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: i < top5Genres.length - 1 ? 10 : 0,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: T.text,
                            }}
                          >
                            {g}
                          </span>
                          <span style={{ fontSize: 12, color: T.mute }}>
                            {n} book{n !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div
                          style={{
                            background: T.s2,
                            borderRadius: 4,
                            height: 6,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              background: T.accent,
                              height: "100%",
                              width: pct + "%",
                              borderRadius: 4,
                              opacity: 1 - i * 0.15,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {top5Vibes.length > 0 && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  padding: "16px",
                  marginBottom: 12,
                  border: "1px solid " + T.border,
                }}
              >
                <div
                  style={{
                    ...analyticsHeadingStyle,
                    marginBottom: 14,
                    textAlign: "center",
                  }}
                >
                  Top Vibes ✨
                </div>
                {top5Vibes.map(([g, n], i) => {
                  const pct = Math.round((n / finishedYr.length) * 100);
                  return (
                    <div
                      key={g}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: i < top5Vibes.length - 1 ? 10 : 0,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: T.text,
                            }}
                          >
                            {g}
                          </span>
                          <span style={{ fontSize: 12, color: T.mute }}>
                            {n} book{n !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div
                          style={{
                            background: T.s2,
                            borderRadius: 4,
                            height: 6,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              background: "#C4607A",
                              height: "100%",
                              width: pct + "%",
                              borderRadius: 4,
                              opacity: 1 - i * 0.15,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              style={{
                background: T.surface,
                borderRadius: 16,
                padding: "18px 16px",
                marginBottom: 12,
                border: "1px solid " + T.border,
              }}
            >
              <div
                style={{
                  ...analyticsHeadingStyle,
                  marginBottom: 12,
                }}
              >
                Monthly Activity
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {byMo.map(({ m, n, books, monthIndex }) => {
                  const pct = n > 0 ? Math.max(14, Math.round((n / maxMo) * 92)) : 0;
                  const monthBooks = books;
                  const cardW = 24;
                  const cardH = Math.round(cardW * 1.44);
                  const overlap = 16;
                  return (
                    <div
                      key={m}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "34px 1fr auto",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: T.sub,
                        }}
                      >
                        {m}
                      </div>
                      <div
                        style={{
                          position: "relative",
                          height: 54,
                          borderRadius: 12,
                          overflow: "hidden",
                          border: "1px solid " + T.border,
                          background: T.s2,
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
                          cursor: n > 0 ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (n > 0) setMonthPeek({ monthIndex, books });
                        }}
                      >
                        {n > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              inset: "0 auto 0 0",
                              width: `${pct}%`,
                              borderRadius: 11,
                              background:
                                "linear-gradient(90deg," +
                                T.accent +
                                " 0%, " +
                                T.accent +
                                "CC 100%)",
                              opacity: isDarkTheme ? 0.92 : 0.88,
                            }}
                          />
                        )}
                        {n > 0 && monthBooks.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              inset: "0 auto 0 0",
                              width: `${pct}%`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              paddingRight: 7,
                              pointerEvents: "none",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {monthBooks.map((book, i) =>
                                book.cover ? (
                                  <img
                                    key={book.uid || `${m}-${i}`}
                                    src={book.cover}
                                    alt=""
                                    style={{
                                      width: cardW,
                                      height: cardH,
                                      objectFit: "cover",
                                      borderRadius: 7,
                                      marginLeft: i === 0 ? 0 : -overlap,
                                      border: "1px solid rgba(255,255,255,0.28)",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                                    }}
                                  />
                                ) : (
                                  <div
                                    key={book.uid || `${m}-${i}`}
                                    style={{
                                      width: cardW,
                                      height: cardH,
                                      borderRadius: 7,
                                      marginLeft: i === 0 ? 0 : -overlap,
                                      border: "1px solid rgba(255,255,255,0.2)",
                                      background: "rgba(255,255,255,0.18)",
                                      color: "#fff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: Math.max(8, Math.floor(cardW * 0.44)),
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {(book.title || "?").trim().charAt(0) || "?"}
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          minWidth: 58,
                          textAlign: "right",
                          fontSize: 12,
                          fontWeight: 700,
                          color: n > 0 ? T.text : T.mute,
                        }}
                      >
                        {n}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {finishedYr.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "36px 20px",
                  color: T.mute,
                }}
              >
                <div style={{ fontSize: 40, marginBottom: 10, opacity: 0.35 }}>
                  📅
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 18,
                    color: T.sub,
                  }}
                >
                  Nothing logged for {yr}
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  Mark books as Finished to see your stats
                </div>
              </div>
            )}
          </div>
          <BottomNav active="reading" />
        </div>
      )}

      {/* NEXT UP */}
      {view === "nextup" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "18px 20px 14px",
              borderBottom: "1px solid " + T.border,
              position: "sticky",
              top: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.13em",
                  color: T.mute,
                  textTransform: "uppercase",
                }}
              >
                Can't wait to read
              </div>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 22,
                  color: T.text,
                  lineHeight: 1,
                }}
              >
                Next Up
              </div>
              {nextUp.length > 1 && (
                <div style={{ fontSize: 11, color: T.mute, marginTop: 4 }}>
                  Drag to reorder
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setAddTarget("nextup");
                setAddSheet(true);
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                border: "none",
                background: T.accent,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </header>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={openSmartNextPick}
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid " + T.border,
                borderBottom: "1px solid " + T.border,
                background:
                  "linear-gradient(135deg," + T.surface + " 0%," + T.s2 + " 100%)",
                padding: "14px 18px",
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.11em",
                    textTransform: "uppercase",
                    color: T.mute,
                    marginBottom: 4,
                  }}
                >
                  Smart Pick
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 18,
                    color: T.text,
                    lineHeight: 1.15,
                    marginBottom: 3,
                  }}
                >
                  Pick My Next Read
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: T.sub,
                    lineHeight: 1.45,
                  }}
                >
                  Based on {unreadCount} unread book
                  {unreadCount === 1 ? "" : "s"} and your recent reading mood.
                </div>
              </div>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "1px solid " + T.border,
                  background: "rgba(255,255,255,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T.text}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </div>
            </button>
          )}
          {nextUp.length === 0 && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 32px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 64, marginBottom: 20 }}>🔖</div>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 24,
                  color: T.text,
                  marginBottom: 10,
                }}
              >
                Your queue is empty
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: T.sub,
                  lineHeight: 1.6,
                  marginBottom: 28,
                }}
              >
                Add books you're excited to read next — including ones you
                already own!
              </div>
              <button
                onClick={() => {
                  setAddTarget("nextup");
                  setAddSheet(true);
                }}
                style={{
                  padding: "14px 28px",
                  borderRadius: 12,
                  border: "none",
                  background: T.accent,
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                + Add to Queue
              </button>
            </div>
          )}
          <div ref={nextUpScrollRef} style={{ flex: 1, paddingBottom: bottomNavPad }}>
            {nextUp.map((book, i) => (
              <div
                key={book.uid}
                data-nextup-uid={book.uid}
                className="lrow"
                style={{
                  borderBottom: "1px solid " + T.border,
                  background:
                    nextUpDragUid && nextUpDropUid === book.uid
                      ? T.accentBg
                      : T.surface,
                }}
                onDragOver={(e) => {
                  if (!nextUpDragUid) return;
                  e.preventDefault();
                  setNextUpDropUid(book.uid);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceUid =
                    nextUpDragUid || e.dataTransfer?.getData("text/plain");
                  reorderNextUpByUid(sourceUid, book.uid);
                  clearNextUpDragState();
                }}
                onClick={() => {
                  if (nextUpDragUid) return;
                  openDetail(book, true);
                }}
              >
                <div style={{ minWidth: 36, display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    draggable
                    onClick={(e) => e.stopPropagation()}
                    onDragStart={(e) => {
                      setNextUpDragUid(book.uid);
                      setNextUpDropUid(book.uid);
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", book.uid);
                      }
                    }}
                    onDragEnd={() => clearNextUpDragState()}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setNextUpDragUid(book.uid);
                      setNextUpDropUid(book.uid);
                    }}
                    onTouchMove={(e) => {
                      const touch = e.touches?.[0];
                      if (!touch) return;
                      const uid = nextUpUidFromPoint(touch.clientX, touch.clientY);
                      if (uid) setNextUpDropUid(uid);
                    }}
                    onTouchEnd={() => {
                      const targetUid = nextUpDropUid || book.uid;
                      reorderNextUpByUid(book.uid, targetUid);
                      clearNextUpDragState();
                    }}
                    onTouchCancel={() => clearNextUpDragState()}
                    style={{
                      width: 24,
                      height: 30,
                      border: "none",
                      background: "transparent",
                      color: nextUpDragUid === book.uid ? T.accent : T.mute,
                      cursor: "grab",
                      fontSize: 16,
                      lineHeight: 1,
                      padding: 0,
                      touchAction: "none",
                    }}
                    aria-label="Reorder queue"
                    title="Drag to reorder"
                  >
                    ⋮⋮
                  </button>
                  <div
                    style={{
                      fontSize: 18,
                      color: T.mute,
                      fontWeight: 700,
                      minWidth: 20,
                      textAlign: "center",
                    }}
                  >
                    {i + 1}
                  </div>
                </div>
                <BookCover book={book} w={46} h={64} r={8} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {book.seriesName && (
                    <div
                      style={{
                        fontSize: 10,
                        color: T.accent,
                        fontWeight: 600,
                        marginBottom: 2,
                      }}
                    >
                      {book.seriesName}
                      {book.seriesNum ? " #" + book.seriesNum : ""}
                    </div>
                  )}
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 15,
                      color: T.text,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {book.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.sub,
                      fontStyle: "italic",
                      margin: "2px 0 4px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {book.authors}
                  </div>
                  {(book.genres || []).length > 0 && (
                    <div style={{ fontSize: 10, color: T.mute }}>
                      {(book.genres || []).join(" · ")}
                    </div>
                  )}
                </div>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T.mute}
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            ))}
          </div>
          <BottomNav active="nextup" />
        </div>
      )}

      {/* NEXT UP FROM MY BOOKS */}
      {view === "nextupfromlibrary" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <button
                onClick={() => setView("nextup")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.sub,
                  fontSize: 22,
                  padding: 0,
                }}
              >
                &#8592;
              </button>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 20,
                  color: T.text,
                }}
              >
                Add from My Books
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: T.s2,
                borderRadius: 12,
                padding: "0 14px",
                border: "1px solid " + T.border,
                gap: 10,
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke={T.mute}
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                value={nextUpFromLibrarySq}
                onChange={(e) => setNextUpFromLibrarySq(e.target.value)}
                placeholder="Search your library..."
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 14,
                  color: T.text,
                  padding: "12px 0",
                  fontFamily: SANS,
                }}
                autoFocus
              />
              {nextUpFromLibrarySq && (
                <button
                  onClick={() => setNextUpFromLibrarySq("")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: T.mute,
                    fontSize: 16,
                    padding: 0,
                  }}
                >
                  &#x2715;
                </button>
              )}
            </div>
          </header>
          <div style={{ flex: 1, overflowY: "auto", paddingBottom: bottomNavPad }}>
            {books.length === 0 && (
              <div style={{ textAlign: "center", padding: "64px 24px" }}>
                <div style={{ fontSize: 46, marginBottom: 12, opacity: 0.35 }}>
                  📚
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.sub,
                  }}
                >
                  No books in My Books yet
                </div>
              </div>
            )}
            {books.length > 0 && nextUpFromLibraryList.length === 0 && (
              <div style={{ textAlign: "center", padding: "64px 24px" }}>
                <div style={{ fontSize: 46, marginBottom: 12, opacity: 0.35 }}>
                  ✅
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.sub,
                    marginBottom: 8,
                  }}
                >
                  Nothing to add
                </div>
                <div style={{ fontSize: 13, color: T.mute }}>
                  {nextUpFromLibrarySq.trim()
                    ? "No books match your search."
                    : "All matching books are already in Next Up."}
                </div>
              </div>
            )}
            {nextUpFromLibraryList.map((book) => (
              <div
                key={book.uid}
                className="lrow"
                style={{
                  borderBottom: "1px solid " + T.border,
                  background: T.surface,
                }}
              >
                <BookCover book={book} w={42} h={60} r={7} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {book.seriesName && (
                    <div
                      style={{
                        fontSize: 10,
                        color: T.accent,
                        fontWeight: 600,
                        marginBottom: 2,
                      }}
                    >
                      {book.seriesName}
                      {book.seriesNum ? " #" + book.seriesNum : ""}
                    </div>
                  )}
                  <div
                    style={{
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 14,
                      color: T.text,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {book.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.sub,
                      fontStyle: "italic",
                      margin: "2px 0 4px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {book.authors}
                  </div>
                  <div style={{ fontSize: 10, color: T.mute }}>
                    {STATUS[book.status]?.label || book.status || "Unread"}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addFromLibraryToNextUp(book);
                  }}
                  style={{
                    border: "1px solid " + T.border,
                    background: T.s2,
                    color: T.accent,
                    borderRadius: 10,
                    padding: "8px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: SANS,
                    flexShrink: 0,
                  }}
                >
                  + Queue
                </button>
              </div>
            ))}
          </div>
          <BottomNav active="nextup" />
        </div>
      )}

      {/* IMPORT */}
      {view === "import" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <button
                onClick={closeImportView}
                style={{
                  background: "none",
                  border: "none",
                  cursor: importBusy || importEnrichBusy ? "default" : "pointer",
                  color: T.sub,
                  fontSize: 22,
                  padding: 0,
                  opacity: importBusy || importEnrichBusy ? 0.5 : 1,
                }}
              >
                &#8592;
              </button>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 20,
                  color: T.text,
                }}
              >
                Import Your Books
              </div>
            </div>
          </header>
          <div style={{ flex: 1, overflowY: "auto", padding: `16px 16px ${bottomNavPad}` }}>
            <input
              ref={importFileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFilePicked}
              style={{ display: "none" }}
            />

            <div
              style={{
                background: T.surface,
                borderRadius: 14,
                border: "1px solid " + T.border,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <Label>How It Works</Label>
              <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
                1. Export a CSV from Goodreads or another tracker.
                <br />
                2. Upload the CSV here.
                <br />
                3. Review the preview and import.
              </div>
            </div>

            <div
              style={{
                background: T.surface,
                borderRadius: 14,
                border: "1px solid " + T.border,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <Label>Import Options</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  {
                    key: "queue",
                    label: "Send Want-to-Read books to Next Up",
                    value: importRouteQueueToNextUp,
                    toggle: () =>
                      setImportRouteQueueToNextUp((prev) => !prev),
                  },
                  {
                    key: "dupes",
                    label: "Skip duplicates already in your library",
                    value: importSkipDuplicates,
                    toggle: () => setImportSkipDuplicates((prev) => !prev),
                  },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={opt.toggle}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "12px 13px",
                      borderRadius: 10,
                      border: "1px solid " + T.border,
                      background: T.s2,
                      color: T.text,
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: SANS,
                      textAlign: "left",
                      gap: 10,
                    }}
                  >
                    <span>{opt.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: opt.value ? T.accent : T.mute,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        flexShrink: 0,
                      }}
                    >
                      {opt.value ? "On" : "Off"}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                background: T.surface,
                borderRadius: 14,
                border: "1px solid " + T.border,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <Label>CSV File</Label>
              <button
                onClick={triggerImportFilePicker}
                disabled={importBusy || importEnrichBusy}
                style={{
                  width: "100%",
                  padding: "13px",
                  borderRadius: 10,
                  border: "1px solid " + T.border,
                  background: importBusy || importEnrichBusy ? T.border : T.s2,
                  color: T.sub,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: importBusy || importEnrichBusy ? "default" : "pointer",
                  fontFamily: SANS,
                }}
              >
                {importSource ? "Choose Another CSV" : "Choose CSV File"}
              </button>

              {importSource?.fileName && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: T.mute,
                    wordBreak: "break-word",
                  }}
                >
                  Selected: {importSource.fileName}
                </div>
              )}
            </div>

            {importErr && (
              <div
                style={{
                  background: "rgba(196,88,74,0.08)",
                  border: "1px solid rgba(196,88,74,0.25)",
                  color: "#C4584A",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 13,
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}
              >
                {importErr}
              </div>
            )}

            {importDraft && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  border: "1px solid " + T.border,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <Label>Import Preview</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    ["Rows found", importDraft.stats.parsedRows],
                    ["Ready to import", importDraft.stats.preparedRows],
                    ["Into My Books", importDraft.stats.preparedBooks],
                    ["Into Next Up", importDraft.stats.preparedNextUp],
                    ["Missing title", importDraft.stats.skippedMissingTitle],
                    [
                      "Duplicate rows in file",
                      importDraft.stats.skippedDuplicateRows,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: T.sub,
                      }}
                    >
                      <span>{label}</span>
                      <span style={{ fontWeight: 700, color: T.text }}>{value}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={applyImportDraft}
                  disabled={
                    importBusy || importEnrichBusy || importDraft.stats.preparedRows === 0
                  }
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: 12,
                    border: "none",
                    background:
                      importBusy || importEnrichBusy || importDraft.stats.preparedRows === 0
                        ? T.border
                        : T.accent,
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor:
                      importBusy || importEnrichBusy || importDraft.stats.preparedRows === 0
                        ? "default"
                        : "pointer",
                    fontFamily: SANS,
                    marginTop: 14,
                    opacity:
                      importBusy || importEnrichBusy || importDraft.stats.preparedRows === 0
                        ? 0.8
                        : 1,
                  }}
                >
                  {importBusy
                    ? "Importing..."
                    : `Import ${importDraft.stats.preparedRows} Book${
                        importDraft.stats.preparedRows === 1 ? "" : "s"
                      }`}
                </button>
              </div>
            )}

            {importReport && (
              <div
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  border: "1px solid " + T.border,
                  padding: 16,
                }}
              >
                <Label>Last Import</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    ["Imported total", importReport.importedTotal],
                    ["Added to My Books", importReport.importedBooks],
                    ["Added to Next Up", importReport.importedNextUp],
                    ["Skipped duplicates", importReport.skippedDuplicates],
                    ["Failed writes", importReport.failedCount],
                    [
                      "Metadata updates",
                      importReport.enrichUpdated !== undefined
                        ? importReport.enrichUpdated
                        : "--",
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: T.sub,
                      }}
                    >
                      <span>{label}</span>
                      <span style={{ fontWeight: 700, color: T.text }}>{value}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={enrichLastImportMetadata}
                  disabled={
                    importBusy ||
                    importEnrichBusy ||
                    !(importReport.importedEntries || []).length
                  }
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 10,
                    border: "1px solid " + T.border,
                    background:
                      importBusy ||
                      importEnrichBusy ||
                      !(importReport.importedEntries || []).length
                        ? T.border
                        : T.s2,
                    color: T.sub,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor:
                      importBusy ||
                      importEnrichBusy ||
                      !(importReport.importedEntries || []).length
                        ? "default"
                        : "pointer",
                    fontFamily: SANS,
                    marginTop: 12,
                    opacity:
                      importBusy ||
                      importEnrichBusy ||
                      !(importReport.importedEntries || []).length
                        ? 0.8
                        : 1,
                  }}
                >
                  {importEnrichBusy
                    ? `Refreshing metadata... (${importEnrichProgress.done}/${importEnrichProgress.total})`
                    : "Refresh Covers & Book Info"}
                </button>
                {importEnrichBusy && (
                  <div style={{ fontSize: 12, color: T.mute, marginTop: 8 }}>
                    Updated {importEnrichProgress.updated} so far.
                  </div>
                )}
              </div>
            )}
          </div>
          <BottomNav active={importNavTab} />
        </div>
      )}

      {/* SEARCH */}
      {view === "search" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <button
                onClick={() => setView("mybooks")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.sub,
                  fontSize: 22,
                  padding: 0,
                }}
              >
                &#8592;
              </button>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 20,
                  color: T.text,
                }}
              >
                Find a Book
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: T.s2,
                borderRadius: 12,
                padding: "0 14px",
                border: "1px solid " + T.border,
                gap: 10,
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke={T.mute}
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                value={sq}
                onChange={(e) => {
                  setSq(e.target.value);
                  setSearchHasRun(false);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  runSearchNow();
                }}
                placeholder={
                  searchMode === "isbn"
                    ? "ISBN-10 or ISBN-13..."
                    : "Title, author, or ISBN..."
                }
                type="search"
                enterKeyHint="search"
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 14,
                  color: T.text,
                  padding: "12px 0",
                  fontFamily: SANS,
                }}
                autoFocus
              />
              {sq && (
                <button
                  onClick={() => {
                    searchReqRef.current += 1;
                    setSq("");
                    setResults([]);
                    setSrching(false);
                    setSearchHasRun(false);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: T.mute,
                    fontSize: 16,
                    padding: 0,
                  }}
                >
                  &#x2715;
                </button>
              )}
              <button
                type="button"
                onClick={() => runSearchNow()}
                disabled={
                  srching ||
                  !sq.trim() ||
                  (searchMode === "isbn" && !extractIsbnFromText(sq))
                }
                style={{
                  height: 30,
                  borderRadius: 9,
                  border: "1px solid " + (srching ? T.border : T.accent),
                  background: srching ? T.s2 : T.accentBg,
                  color: srching ? T.mute : T.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  cursor:
                    srching ||
                    !sq.trim() ||
                    (searchMode === "isbn" && !extractIsbnFromText(sq))
                      ? "default"
                      : "pointer",
                  opacity:
                    srching ||
                    !sq.trim() ||
                    (searchMode === "isbn" && !extractIsbnFromText(sq))
                      ? 0.55
                      : 1,
                }}
                aria-label="Search"
              >
                Search
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {[
                ["auto", "Auto"],
                ["isbn", "ISBN"],
              ].map(([mode, label]) => {
                const on = searchMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      searchReqRef.current += 1;
                      setSearchMode(mode);
                      setResults([]);
                      setSrching(false);
                      setSearchHasRun(false);
                    }}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid " + (on ? T.accent : T.border),
                      background: on ? T.accentBg : T.s2,
                      color: on ? T.accent : T.sub,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: SANS,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </header>
          <div
            ref={searchScrollRef}
            style={{ flex: 1, overflowY: "auto", paddingBottom: bottomNavPad }}
          >
            {srching && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 40,
                }}
              >
                <div
                  className="spin"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "2px solid " + T.border,
                    borderTopColor: T.accent,
                  }}
                ></div>
              </div>
            )}
            {!srching && !sq && (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 48, marginBottom: 14, opacity: 0.35 }}>
                  🔍
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.sub,
                  }}
                >
                  Search millions of books
                </div>
              </div>
            )}
            {!srching && sq && searchHasRun && results.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 48, marginBottom: 14, opacity: 0.35 }}>
                  😔
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.sub,
                  }}
                >
                  No results
                </div>
              </div>
            )}
            {results.map((r) => (
              <div
                key={r.id}
                className="lrow"
                style={{ borderBottom: "1px solid " + T.border }}
                onClick={() => openAdd(r)}
              >
                {r.cover ? (
                  <img
                    src={r.cover}
                    alt=""
                    style={{
                      width: 42,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 7,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 42,
                      height: 60,
                      borderRadius: 7,
                      background: T.accentBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 13,
                      color: T.accent,
                      flexShrink: 0,
                    }}
                  >
                    {r.title.slice(0, 2)}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: T.text,
                      lineHeight: 1.3,
                    }}
                  >
                    {r.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.sub,
                      fontStyle: "italic",
                      margin: "2px 0 3px",
                    }}
                  >
                    {r.authors}
                  </div>
                  <div style={{ fontSize: 11, color: T.mute }}>
                    {r.year}
                    {r.pages !== "--" ? " · " + r.pages + "p" : ""}
                  </div>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T.accent}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </div>
            ))}
          </div>
          <BottomNav active="mybooks" />
        </div>
      )}

      {/* SCAN */}
      {view === "scan" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
            background: "#0A0A0A",
          }}
        >
          <header
            style={{
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <button
              onClick={() => {
                stopScan();
                setView("mybooks");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fff",
                fontSize: 22,
                padding: 0,
              }}
            >
              &#8592;
            </button>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 20,
                color: "#fff",
              }}
            >
              Scan Barcode
            </div>
            <div style={{ width: 24 }}></div>
          </header>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 24px 40px",
              gap: 20,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 280,
                aspectRatio: "3/4",
                borderRadius: 20,
                overflow: "hidden",
                position: "relative",
                background: "#111",
              }}
            >
              <video
                ref={vidRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {!scanOn && !scanErr && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    color: "#555",
                  }}
                >
                  <div style={{ fontSize: 44 }}>📷</div>
                  <div style={{ fontSize: 13 }}>Starting camera...</div>
                </div>
              )}
              {scanErr && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: 36 }}>⚠️</div>
                  <div
                    style={{
                      color: "#EF4444",
                      fontSize: 13,
                      padding: "0 20px",
                      textAlign: "center",
                    }}
                  >
                    {scanErr}
                  </div>
                </div>
              )}
              {scanOn && !scanErr && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "10%",
                      right: "10%",
                      height: 2,
                      background:
                        "linear-gradient(90deg,transparent,#7BA7D4,transparent)",
                      borderRadius: 1,
                    }}
                    className="scanline"
                  ></div>
                </div>
              )}
            </div>
            <p style={{ color: "#555", fontSize: 13, textAlign: "center" }}>
              Hold the barcode steady · tap screen to focus
            </p>
            {srching && (
              <div
                className="spin"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "2px solid #333",
                  borderTopColor: "#7BA7D4",
                }}
              ></div>
            )}
            {scanOn && !scanErr && !srching && (
              <button
                onClick={simScan}
                style={{
                  padding: "12px 28px",
                  borderRadius: 12,
                  border: "1px solid #333",
                  background: "transparent",
                  color: "#fff",
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Simulate Scan (Demo)
              </button>
            )}
          </div>
        </div>
      )}

      {/* ADD */}
      {view === "add" && pending && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <button
              onClick={() => {
                setView("search");
                setPending(null);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.sub,
                fontSize: 22,
                padding: 0,
              }}
            >
              &#8592;
            </button>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 20,
                color: T.text,
              }}
            >
              Add to {addTarget === "nextup" ? "Next Up" : "My Books"}
            </div>
          </header>
          <div
            style={{ flex: 1, overflowY: "auto", padding: "20px 20px 48px" }}
          >
            <div
              style={{
                display: "flex",
                gap: 14,
                marginBottom: 24,
                padding: 14,
                background: T.surface,
                borderRadius: 14,
                border: "1px solid " + T.border,
              }}
            >
              <BookCover
                book={{ ...pending, title: aTitle || pending.title }}
                w={68}
                h={96}
                r={9}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      fontFamily: DISPLAY_SERIF,
                      fontSize: 16,
                      color: T.text,
                      lineHeight: 1.3,
                    }}
                  >
                    {aTitle || pending.title}
                  </div>
                  <button
                    type="button"
                    onClick={() => setAFav((f) => !f)}
                    aria-label={aFav ? "Remove favorite" : "Mark as favorite"}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      border:
                        "1px solid " +
                        (aFav ? "rgba(196,96,122,0.25)" : T.border),
                      background: aFav ? "rgba(196,96,122,0.1)" : T.s2,
                      cursor: "pointer",
                      fontSize: 16,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {aFav ? "❤️" : "🤍"}
                  </button>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: T.sub,
                    fontStyle: "italic",
                    marginBottom: 5,
                  }}
                >
                  {pending.authors}
                </div>
                <div style={{ fontSize: 11, color: T.mute }}>
                  {[pending.year, pending.pages !== "--" && pending.pages + "p"]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>
            <Sec label="Book Title">
              <input
                value={aTitle}
                onChange={(e) => setATitle(e.target.value)}
                placeholder="Book title"
                style={{ ...inp }}
              />
            </Sec>
            <Sec label="Series (optional)">
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  key="asname"
                  defaultValue={aSeriesName}
                  onBlur={(e) => setASeriesName(e.target.value)}
                  placeholder="Series name..."
                  style={{ ...inp, flex: 2 }}
                />
                <input
                  key="asnum"
                  defaultValue={aSeriesNum}
                  onBlur={(e) => setASeriesNum(e.target.value)}
                  placeholder="#"
                  style={{ ...inp, flex: 1 }}
                />
              </div>
            </Sec>
            <Sec label="Edition (optional)">
              <input
                key="aedition"
                defaultValue={aEdition}
                onBlur={(e) => setAEdition(e.target.value)}
                placeholder="Special, deluxe, anniversary..."
                style={{ ...inp }}
              />
            </Sec>
            <Sec label="Format">
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {Object.entries(FMTS).map(([f, icon]) => (
                  <Chip
                    key={f}
                    on={aFmts.includes(f)}
                    onClick={() =>
                      setAFmts((p) =>
                        p.includes(f) ? p.filter((x) => x !== f) : [...p, f],
                      )
                    }
                  >
                    {icon} {f}
                  </Chip>
                ))}
              </div>
            </Sec>
            {addTarget !== "nextup" && (
              <Sec label="Status">
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {Object.keys(STATUS).map((s) => (
                    <Chip key={s} on={aStat === s} onClick={() => setAStat(s)}>
                      {STATUS[s].label}
                    </Chip>
                  ))}
                </div>
              </Sec>
            )}
            <Sec label="Genre, Vibes & Fun Stuff">
              <GenrePicker
                selectedGenres={aGenres}
                onGenres={setAGenres}
                lane={aGenreLane}
                onLaneChange={(nextLane) => {
                  const normalized = normalizeGenreLane(nextLane);
                  setAGenreLane(normalized);
                  setAGenres((prev) => restrictGenresToLane(prev, normalized));
                }}
                selectedVibes={aVibes}
                onVibes={setAVibes}
                customTags={aTags}
                onTags={setATags}
                T={T}
                openState={addGenreOpen}
                setOpenState={setAddGenreOpen}
              />
            </Sec>
            {addTarget !== "nextup" && (
              <Sec label="Dates">
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <MonthYearPicker
                    value={aStartDate}
                    onChange={setAStartDate}
                    label="Started"
                    T={T}
                  />
                  <MonthYearPicker
                    value={aEndDate}
                    onChange={setAEndDate}
                    label="Finished"
                    T={T}
                  />
                </div>
              </Sec>
            )}
            <Sec label="Rating">
              <HalfStars val={aRating} onChange={setARating} />
            </Sec>
            <div style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: T.mute,
                  marginBottom: 10,
                }}
              >
                My Notes... (optional)
              </div>
              <textarea
                value={aNotes}
                onChange={(e) => setANotes(e.target.value)}
                placeholder="Add thoughts, context, or why this one matters..."
                rows={4}
                style={{ ...inp, resize: "vertical", lineHeight: 1.7 }}
              />
            </div>
            <BigBtn onClick={confirmAdd}>
              Add to {addTarget === "nextup" ? "Next Up" : "My Books"}
            </BigBtn>
          </div>
        </div>
      )}

      {/* DETAIL - VIEW */}
      {view === "detail" && active && detailTab === "view" && (
        <BookDetailView
          active={active}
          fromNextUp={fromNextUp}
          dFav={dFav}
          dStatus={dStatus}
          dFmts={dFmts}
          dGenres={dGenres}
          dVibes={dVibes}
          dTags={dTags}
          dNotes={dNotes}
          dReads={dReads}
          dRereading={dRereading}
          dRereadStartDate={dRereadStartDate}
          dRating={dRating}
          dSeriesName={dSeriesName}
          dSeriesNum={dSeriesNum}
          dEdition={dEdition}
          dStartDate={dStartDate}
          dEndDate={dEndDate}
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          STATUS={STATUS}
          BookCover={BookCover}
          HalfStars={HalfStars}
          detailViewScrollRef={detailViewScrollRef}
          formatBookDate={formatBookDate}
          readingDays={readingDays}
          cleanBookDescription={cleanBookDescription}
          activePublishedDisplay={activePublishedDisplay}
          activeHasPages={activeHasPages}
          showStartReadingButton={showStartReadingButton}
          startReadingButtonLabel={startReadingButtonLabel}
          onBack={() => setView(fromNextUp ? "nextup" : "mybooks")}
          onToggleFavorite={toggleDetailFavorite}
          onEdit={() => {
            setDetailEditSection("");
            setDetailTab("edit");
          }}
          onStartReading={() =>
            fromNextUp
              ? moveToLibrary(active)
              : dStatus === "Read" && !dRereading
                ? startRereadInLibrary(active)
                : dStatus !== "In Progress"
                  ? startReadingInLibrary(active)
                  : null
          }
          onFinishReading={() => finishReadingInLibrary(active)}
          onDelete={deleteBook}
          onReportMetadata={() => openMetadataReport(active)}
        />
      )}

      {/* DETAIL - EDIT */}
      {view === "detail" && active && detailTab === "edit" && (
        <BookDetailEdit
          active={active}
          fromNextUp={fromNextUp}
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          STATUS={STATUS}
          FMTS={FMTS}
          BookCover={BookCover}
          Chip={Chip}
          Sec={Sec}
          GenrePicker={GenrePicker}
          MonthYearPicker={MonthYearPicker}
          HalfStars={HalfStars}
          BigBtn={BigBtn}
          inp={inp}
          dStatus={dStatus}
          setDStatus={handleDetailStatusChange}
          dTitle={dTitle}
          setDTitle={setDTitle}
          dFmts={dFmts}
          setDFmts={setDFmts}
          dSeriesName={dSeriesName}
          setDSeriesName={setDSeriesName}
          dSeriesNum={dSeriesNum}
          setDSeriesNum={setDSeriesNum}
          dEdition={dEdition}
          setDEdition={setDEdition}
          dCover={dCover}
          setDCover={setDCover}
          dIsbn={dIsbn}
          setDIsbn={setDIsbn}
          dPublishedDate={dPublishedDate}
          setDPublishedDate={setDPublishedDate}
          dPublisher={dPublisher}
          setDPublisher={setDPublisher}
          dPages={dPages}
          setDPages={setDPages}
          dGenres={dGenres}
          setDGenres={setDGenres}
          dGenreLane={dGenreLane}
          setDGenreLane={setDGenreLane}
          normalizeGenreLane={normalizeGenreLane}
          restrictGenresToLane={restrictGenresToLane}
          dVibes={dVibes}
          setDVibes={setDVibes}
          dTags={dTags}
          setDTags={setDTags}
          detailGenreOpen={detailGenreOpen}
          setDetailGenreOpen={setDetaiGenreOpen}
          dStartDate={dStartDate}
          setDStartDate={setDStartDate}
          dEndDate={dEndDate}
          setDEndDate={setDEndDate}
          dRating={dRating}
          setDRating={setDRating}
          dReads={dReads}
          setDReads={setDReads}
          dRereading={dRereading}
          dRereadStartDate={dRereadStartDate}
          dNotes={dNotes}
          setDNotes={setDNotes}
          dDesc={dDesc}
          setDDesc={setDDesc}
          dFav={dFav}
          setDFav={setDFav}
          editSection={detailEditSection}
          detailEditScrollRef={detailEditScrollRef}
          onBack={() => {
            setDetailEditSection("");
            setDetailTab("view");
          }}
          onDelete={deleteBook}
          onLogReread={startRereadDraft}
          onFindCoverCandidates={findCoverCandidates}
          onSave={saveDetail}
        />
      )}
      {/* SHARED PROFILE */}
      {view === "sharedprofile" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
            background: T.bg,
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <button
              onClick={() =>
                setView(
                  sharedRoute.source === "preview"
                    ? sharedRoute.returnView || "settings"
                    : "mybooks",
                )
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.sub,
                fontSize: 20,
                padding: 0,
              }}
            >
              ←
            </button>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 20,
                color: T.text,
              }}
            >
              Shared Shelf
            </div>
            <div style={{ width: 20 }} />
          </header>

          {sharedRemoteLoading && !sharedOwnerIsCurrentUser ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.sub,
                fontSize: 14,
              }}
            >
              Loading shared bookshelf...
            </div>
          ) : !sharedAccessAuthorized ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "40px 24px",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 44 }}>🔒</div>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 24,
                  color: T.text,
                }}
              >
                Invite-Only Bookshelf
              </div>
              <div style={{ fontSize: 14, color: T.sub, lineHeight: 1.7 }}>
                {sharedAccessMessage}
              </div>
              {sharedRoute.source === "link" && !sharedOwnerIsCurrentUser && (
                <div style={{ width: "100%", maxWidth: 360, marginTop: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: T.mute,
                      marginBottom: 6,
                    }}
                  >
                    Invite Code
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={sharedUnlockCode}
                      onChange={(e) => {
                        setSharedUnlockCode(formatInviteCode(e.target.value));
                        if (sharedUnlockErr) setSharedUnlockErr("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void unlockSharedShelfWithCode(sharedUnlockCode);
                        }
                      }}
                      placeholder="Enter code"
                      autoCapitalize="characters"
                      style={{ ...inp, flex: 1, textTransform: "uppercase" }}
                    />
                    <button
                      onClick={() => unlockSharedShelfWithCode(sharedUnlockCode)}
                      disabled={sharedUnlockBusy}
                      style={{
                        padding: "0 14px",
                        borderRadius: 10,
                        border: "none",
                        background: sharedUnlockBusy ? T.border : T.accent,
                        color: sharedUnlockBusy ? T.mute : "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: sharedUnlockBusy ? "default" : "pointer",
                        fontFamily: SANS,
                        flexShrink: 0,
                      }}
                    >
                      {sharedUnlockBusy ? "Unlocking..." : "Unlock"}
                    </button>
                  </div>
                  {sharedUnlockErr && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#C4584A" }}>
                      {sharedUnlockErr}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => setView("mybooks")}
                style={{
                  marginTop: 10,
                  padding: "11px 18px",
                  borderRadius: 10,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Back to Library
              </button>
            </div>
          ) : (
            <div
              ref={sharedProfileScrollRef}
              onScroll={(e) => handleScrollTopVisibility(e.currentTarget.scrollTop)}
              style={{ flex: 1, overflowY: "auto", padding: "18px 16px 44px" }}
            >
              <div
                style={{
                  background: sharedHeroBackground,
                  borderRadius: 16,
                  border: "1px solid " + T.border,
                  boxShadow: sharedHeroShadow,
                  padding: "18px 18px 16px",
                  marginBottom: sharedSectionGap,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: T.mute,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Private Shelf
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 34,
                    color: T.text,
                    lineHeight: 1.05,
                    marginBottom: 6,
                  }}
                >
                  {sharedDisplayName}
                </div>
                <div style={{ color: T.sub, fontSize: 14, marginBottom: 10 }}>
                  {sharedMetaLine}
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    borderRadius: 20,
                    padding: "4px 10px",
                    border: "1px solid " + T.border,
                    background: T.s2,
                    color: T.sub,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  <span>🔒</span>{" "}
                  {sharedConfig.mode === "invite-only" ? "Invite Only" : "Private"}
                </span>
              </div>

              {sharedBio && (
                <div
                  style={{
                    background: T.surface,
                    borderRadius: 14,
                    padding: "14px 14px 12px",
                    border: "1px solid " + T.border,
                    marginBottom: sharedSectionGap,
                  }}
                >
                  <div
                    style={{
                      ...sharedSectionTitleStyle,
                    }}
                  >
                    Bio
                  </div>
                  <div
                    style={{
                      color: T.sub,
                      fontSize: 14,
                      lineHeight: 1.7,
                      textAlign: "center",
                    }}
                  >
                    {sharedBio}
                  </div>
                </div>
              )}

              {sharedConfig.showReading !== false && (
                <div
                  style={{
                    padding: "4px 2px 8px",
                    marginBottom: sharedSectionGap,
                  }}
                >
                  <div
                    style={{
                      ...sharedSectionTitleStyle,
                    }}
                  >
                    Currently Reading
                  </div>
                  {sharedCurrentReads.length > 0 ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                        gap: 12,
                        justifyItems: "center",
                      }}
                    >
                      {sharedCurrentReads.map((book) => (
                        <div key={book.uid} style={{ minWidth: 0, textAlign: "center" }}>
                          <BookCover book={book} w={94} h={130} r={8} />
                          <div
                            style={{
                              fontSize: 12,
                              color: T.text,
                              fontWeight: 600,
                              marginTop: 6,
                              lineHeight: 1.25,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              textAlign: "center",
                            }}
                          >
                            {book.title}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: T.mute,
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 94,
                              textAlign: "center",
                            }}
                          >
                            {book.authors}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: T.mute, fontSize: 13, textAlign: "center" }}>
                      No current reads selected yet.
                    </div>
                  )}
                </div>
              )}

              {sharedTopGenres.length > 0 && (
                <div
                  style={{
                    padding: "4px 2px 8px",
                    marginBottom: sharedSectionGap,
                  }}
                >
                  <div
                    style={{
                      ...sharedSectionTitleStyle,
                    }}
                  >
                    Top Genres
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 7,
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    {sharedTopGenres.map((label) => (
                      <span
                        key={label}
                        style={{
                          padding: "6px 11px",
                          borderRadius: 20,
                          border: "1px solid " + T.border,
                          background: T.s2,
                          color: T.sub,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  padding: "4px 2px 8px",
                  marginBottom: sharedSectionGap,
                }}
              >
                <div
                  style={{
                    ...sharedSectionTitleStyle,
                  }}
                >
                  Books I Always Recommend
                </div>
                {sharedRecommendedBooks.length > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                      gap: 14,
                      justifyItems: "center",
                    }}
                  >
                    {sharedRecommendedBooks.map((book, i) => (
                      <div
                        key={book.uid || book.title + i}
                        style={{
                          minWidth: 0,
                          textAlign: "center",
                        }}
                      >
                        <BookCover book={book} w={94} h={130} r={8} />
                        <div
                          style={{
                            fontSize: 12,
                            color: T.text,
                            fontWeight: 600,
                            marginTop: 6,
                            lineHeight: 1.25,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            textAlign: "center",
                          }}
                        >
                          {book.title}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: T.mute,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 94,
                            textAlign: "center",
                          }}
                        >
                          {book.authors}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: T.mute, fontSize: 13, textAlign: "center" }}>
                    Recommended picks have not been selected yet.
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: "4px 2px 8px",
                  marginBottom: sharedSectionGap,
                }}
              >
                <div
                  style={{
                      ...sharedSectionTitleStyle,
                    }}
                  >
                    Get to Know Me
                  </div>
                {sharedPromptAnswers.length > 0 ? (
                  sharedPromptAnswers.map((entry, i) => (
                    <div
                      key={entry.prompt + i}
                      style={{
                        padding: i === 0 ? "0 0 10px" : "10px 0",
                        borderTop: i === 0 ? "none" : "1px solid " + T.border,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: sharedPromptQuestionColor,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: 6,
                          textAlign: "center",
                        }}
                      >
                        {entry.prompt}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: sharedPromptAnswerColor,
                          lineHeight: 1.7,
                          textAlign: "center",
                          fontStyle: "italic",
                        }}
                      >
                        {entry.answer}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: T.mute, fontSize: 13, textAlign: "center" }}>
                    Icebreaker answers have not been added yet.
                  </div>
                )}
              </div>

              {sharedCanOpenFullShelf ? (
                <>
                  <button
                    onClick={() => {
                      setSharedShelfGenre("All");
                      setSharedShelfQuery("");
                      setView("sharedbookshelf");
                    }}
                    style={{
                      width: "100%",
                      padding: "14px",
                      borderRadius: 12,
                      border: "none",
                      background: T.accent,
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: SANS,
                    }}
                  >
                    View Full Bookshelf
                  </button>
                  {sharedOwnerIsCurrentUser && sharedConfig.showFullShelf === false && (
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: T.mute,
                        textAlign: "center",
                      }}
                    >
                      Full bookshelf is hidden from invited viewers.
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: 12,
                    border: "1px solid " + T.border,
                    background: T.surface,
                    color: T.mute,
                    fontSize: 13,
                    textAlign: "center",
                  }}
                >
                  This reader is only sharing a curated shelf, not the full bookshelf.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SHARED BOOKSHELF */}
      {view === "sharedbookshelf" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
            background: T.bg,
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "16px 20px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={() => setView("sharedprofile")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.sub,
                fontSize: 20,
                padding: 0,
              }}
            >
              ←
            </button>
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 20,
                color: T.text,
              }}
            >
              Full Bookshelf
            </div>
          </header>
          {sharedRemoteLoading && !sharedOwnerIsCurrentUser ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.sub,
                fontSize: 14,
              }}
            >
              Loading shared bookshelf...
            </div>
          ) : !sharedAccessAuthorized ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: T.sub,
                gap: 8,
                padding: 24,
              }}
            >
              <div style={{ fontSize: 32 }}>🔒</div>
              <div>{sharedAccessMessage}</div>
            </div>
          ) : !sharedCanOpenFullShelf ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: T.sub,
                gap: 8,
                padding: 24,
              }}
            >
              <div style={{ fontSize: 32 }}>📚</div>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 22,
                  color: T.text,
                }}
              >
                Full shelf is hidden
              </div>
              <div style={{ maxWidth: 320, lineHeight: 1.6 }}>
                This reader is sharing a curated private shelf only.
              </div>
            </div>
          ) : (
            <div
              ref={sharedBookshelfScrollRef}
              onScroll={(e) => handleScrollTopVisibility(e.currentTarget.scrollTop)}
              style={{ flex: 1, overflowY: "auto", padding: "14px 16px 44px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 12, color: T.mute }}>
                  {sharedShelfBooks.length} book{sharedShelfBooks.length === 1 ? "" : "s"}
                </div>
                <div
                  style={{
                    display: "flex",
                    background: T.s2,
                    borderRadius: 9,
                    border: "1px solid " + T.border,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <button
                    onClick={() => setSharedShelfGridMode(true)}
                    style={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: sharedShelfGridMode ? T.accent : "transparent",
                      color: sharedShelfGridMode ? "#fff" : T.mute,
                      border: "none",
                      cursor: "pointer",
                    }}
                    aria-label="Cards view"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setSharedShelfGridMode(false)}
                    style={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: !sharedShelfGridMode ? T.accent : "transparent",
                      color: !sharedShelfGridMode ? "#fff" : T.mute,
                      border: "none",
                      cursor: "pointer",
                    }}
                    aria-label="List view"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                    </svg>
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: T.s2,
                  borderRadius: 10,
                  padding: "0 12px",
                  border: "1px solid " + T.border,
                  marginBottom: 10,
                }}
              >
                <input
                  value={sharedShelfQuery}
                  onChange={(e) => setSharedShelfQuery(e.target.value)}
                  placeholder="Search titles, authors, genres..."
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: 13,
                    color: T.text,
                    padding: "11px 0",
                    fontFamily: SANS,
                  }}
                />
                {sharedShelfQuery && (
                  <button
                    onClick={() => setSharedShelfQuery("")}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: T.mute,
                      fontSize: 16,
                      padding: 0,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <select
                value={sharedShelfGenre}
                onChange={(e) => setSharedShelfGenre(e.target.value)}
                style={{ ...inp, marginBottom: 10 }}
              >
                {sharedShelfGenreOptions.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre === "All" ? "All Genres" : genre}
                  </option>
                ))}
              </select>

              {sharedUsesContinuousShelf ? (
                sharedShelfBooks.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: T.mute,
                      padding: "38px 16px",
                    }}
                  >
                    No books match these filters.
                  </div>
                ) : sharedShelfGridMode ? (
                  renderSharedShelfGrid(sharedShelfBooks)
                ) : (
                  sharedShelfBooks.map((book) => (
                    <div
                      key={book.uid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 2px",
                        borderBottom: "1px solid " + T.border,
                      }}
                    >
                      <BookCover book={book} w={42} h={60} r={7} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 15,
                            color: T.text,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {book.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: T.sub,
                            fontStyle: "italic",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {book.authors}
                        </div>
                        {[...(book.genres || []), ...(book.vibes || [])].length > 0 && (
                          <div
                            style={{
                              fontSize: 11,
                              color: T.mute,
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {[...(book.genres || []), ...(book.vibes || [])].join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )
              ) : sharedShelfSections.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: T.mute,
                    padding: "38px 16px",
                  }}
                >
                  No books match these filters.
                </div>
              ) : (
                sharedShelfSections.map((section) => (
                  <div key={section.key} style={{ marginBottom: 18 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: DISPLAY_SERIF,
                          fontSize: 22,
                          color: T.text,
                        }}
                      >
                        {section.title}
                      </div>
                      <div style={{ fontSize: 12, color: T.mute }}>
                        {section.books.length}
                      </div>
                    </div>
                    {sharedShelfGridMode
                      ? renderSharedShelfGrid(section.books)
                      : section.books.map((book) => (
                          <div
                            key={book.uid}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 2px",
                              borderBottom: "1px solid " + T.border,
                            }}
                          >
                            <BookCover book={book} w={42} h={60} r={7} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 15,
                                  color: T.text,
                                  fontWeight: 600,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {book.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: T.sub,
                                  fontStyle: "italic",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {book.authors}
                              </div>
                              {[...(book.genres || []), ...(book.vibes || [])].length > 0 && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: T.mute,
                                    marginTop: 2,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {[...(book.genres || []), ...(book.vibes || [])].join(" · ")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
      {/* SETTINGS */}
      {view === "settings" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "20px 20px 16px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
          <div
            style={{
              fontFamily: DISPLAY_SERIF,
              fontSize: 24,
                color: T.text,
              }}
            >
              Settings
            </div>
          </header>
          <div
            style={{ flex: 1, overflowY: "auto", padding: `18px 16px ${bottomNavPad}` }}
          >
            <SettingsLabel>App Settings</SettingsLabel>
              <SettingsNavGroup>
                <SettingsNavRow
                  title="Profile"
                  onClick={() => setView("settings-profile")}
                />
              <SettingsNavRow
                title="Appearance"
                onClick={() => setView("settings-appearance")}
              />
              <SettingsNavRow
                title="Private Shelf"
                onClick={() => setView("settings-private")}
              />
              <SettingsNavRow
                title="Library & Data"
                onClick={() => setView("settings-library")}
                last
              />
            </SettingsNavGroup>

            <SettingsLabel>Account</SettingsLabel>
            <SettingsNavGroup>
              <SettingsNavRow
                title="Account"
                onClick={() => setView("settings-account")}
                last={!isAdminUser}
              />
              {isAdminUser && (
                <SettingsNavRow
                  title="Admin Tools"
                  onClick={() => setView("settings-admin")}
                  last
                />
              )}
            </SettingsNavGroup>

            <SettingsLabel>Support</SettingsLabel>
            <SettingsNavGroup>
              <SettingsNavRow
                title="Privacy"
                onClick={() => setView("settings-privacy")}
              />
              <SettingsNavRow
                title="Help & Support"
                onClick={() => setView("settings-help")}
                last
              />
            </SettingsNavGroup>
          </div>
          <BottomNav active="settings" />
        </div>
      )}

      {view === "settings-profile" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav} title="Profile">
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Display Name</SettingsLabel>
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <input
                value={tmpName}
                onChange={(e) => setTmpName(e.target.value)}
                style={{ ...inp, flex: 1 }}
              />
              <button
                onClick={() => {
                  if (tmpName.trim()) {
                    setName(tmpName.trim());
                    saveSettings({ name: tmpName.trim() });
                    showToast("Name updated");
                  }
                }}
                style={{
                  padding: "0 18px",
                  borderRadius: 9,
                  border: "none",
                  background: T.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Save
              </button>
            </div>

            <SettingsLabel>Yearly Reading Goal</SettingsLabel>
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <input
                type="number"
                min="0"
                max="365"
                value={tmpGoal}
                onChange={(e) => setTmpGoal(Number(e.target.value))}
                style={{ ...inp, flex: 1 }}
                placeholder="e.g. 24"
              />
              <button
                onClick={() => {
                  const updated = { ...yearlyGoals, [yr]: tmpGoal };
                  setYearlyGoals(updated);
                  saveSettings({ yearlyGoals: updated });
                  showToast("Goal updated");
                }}
                style={{
                  padding: "0 18px",
                  borderRadius: 9,
                  border: "none",
                  background: T.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Save
              </button>
            </div>

            <SettingsLabel>Rename DNF Label</SettingsLabel>
            <div style={{ fontSize: 13, color: settingsHintColor, marginBottom: 10 }}>
              Call it whatever feels right.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <input
                value={tmpDnf}
                onChange={(e) => setTmpDnf(e.target.value)}
                placeholder="DNF"
                style={{ ...inp, flex: 1 }}
              />
              <button
                onClick={() => {
                  if (tmpDnf.trim()) {
                    const newLabel = tmpDnf.trim();
                    setDnfLabel(newLabel);
                    saveSettings({ dnfLabel: newLabel });
                    books
                      .filter((b) => b.status === "DNF" || b.status === dnfLabel)
                      .forEach((b) => {
                        const ref = doc(db, "users", userId, "books", b.uid);
                        updateDoc(ref, { status: newLabel });
                      });
                    showToast("Label updated");
                  }
                }}
                style={{
                  padding: "0 18px",
                  borderRadius: 9,
                  border: "none",
                  background: T.accent,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </SettingsSubpage>
      )}

      {view === "settings-appearance" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav} title="Appearance">
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Color Theme</SettingsLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Object.entries(THEMES).map(([themeKey, themeMeta]) => {
                const on = theme === themeKey;
                return (
                  <button
                    key={themeKey}
                    onClick={() => {
                      setTheme(themeKey);
                      saveSettings({ theme: themeKey });
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 11,
                      border: "1px solid " + (on ? themeMeta.accent : T.border),
                      background: on ? themeMeta.accentBg : T.s2,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background:
                          "linear-gradient(135deg," +
                          themeMeta.bg +
                          " 0%," +
                          themeMeta.surface +
                          " 55%," +
                          themeMeta.accent +
                          " 100%)",
                        border: "1px solid " + themeMeta.border,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                        {themeMeta.name}
                      </div>
                    </div>
                    {on && (
                      <div style={{ fontSize: 12, color: themeMeta.accent, fontWeight: 700 }}>
                        Active
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>My Books</SettingsLabel>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                  Show reading streak
                </div>
                <div style={{ fontSize: 13, color: settingsHintColor, marginTop: 2 }}>
                  Show your streak badge in the Reading tab
                </div>
              </div>
              <button
                onClick={() => {
                  setShowStreak((s) => {
                    const next = !s;
                    saveSettings({ showStreak: next });
                    return next;
                  });
                }}
                style={{
                  width: 48,
                  height: 28,
                  borderRadius: 14,
                  border: "none",
                  background: showStreak ? T.accent : T.border,
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: showStreak ? 22 : 3,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                  Show star ratings on My Books
                </div>
                <div style={{ fontSize: 13, color: settingsHintColor, marginTop: 2 }}>
                  Keep ratings visible in both grid and list view
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBookRatings((s) => {
                    const next = !s;
                    saveSettings({ showBookRatings: next });
                    return next;
                  });
                }}
                style={{
                  width: 48,
                  height: 28,
                  borderRadius: 14,
                  border: "none",
                  background: showBookRatings ? T.accent : T.border,
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: showBookRatings ? 22 : 3,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                />
              </button>
            </div>
          </div>
        </SettingsSubpage>
      )}

      {view === "settings-private" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
          }}
        >
          <header
            style={{
              background: T.surface,
              padding: "18px 20px 14px",
              borderBottom: "1px solid " + T.border,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <button
              onClick={openSettingsHome}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.sub,
                fontSize: 18,
                padding: 0,
                flexShrink: 0,
                fontFamily: SANS,
              }}
            >
              ←
            </button>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: DISPLAY_SERIF,
                  fontSize: 22,
                  color: T.text,
                }}
              >
                Private Shelf
              </div>
              <div style={{ fontSize: 13, color: settingsHintColor, marginTop: 2 }}>
                Curate and manage your private bookshelf profile.
              </div>
            </div>
          </header>
          <div
            ref={privateShelfScrollRef}
            onScroll={(e) => handleScrollTopVisibility(e.currentTarget.scrollTop)}
            style={{ flex: 1, overflowY: "auto", padding: `18px 16px ${bottomNavPad}` }}
          >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 12, color: settingsHintColor }}>
                Changes save automatically.
              </div>
              <div style={{ fontSize: 12, color: settingsHintColor }}>
                {shelfSharing.mode === "invite-only" ? "Invite-only" : "Private"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => openSharedProfilePreview("settings-private")}
                style={{
                  flex: 1,
                  padding: "11px",
                  borderRadius: 10,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Preview Profile
              </button>
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: settingsHintColor,
                marginBottom: 8,
              }}
            >
              Shelf visibility
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {[
                ["private", "Private"],
                ["invite-only", "Invite only"],
              ].map(([value, label]) => (
                <Chip
                  key={value}
                  on={shelfSharing.mode === value}
                  onClick={() => {
                    let nextCfg = normalizeShelfSharingConfig({
                      ...shelfSharing,
                      mode: value,
                      enabled: value === "invite-only",
                    });
                    if (
                      value === "invite-only" &&
                      !formatInviteCode(nextCfg.inviteCode)
                    ) {
                      nextCfg = normalizeShelfSharingConfig({
                        ...nextCfg,
                        inviteCode: makeInviteCode(),
                      });
                    }
                    updateShelfSharing(nextCfg);
                    if (value === "invite-only") {
                      void syncShelfInviteCodeSecret(nextCfg.inviteCode);
                      void syncSharedShelfMirror(nextCfg);
                    }
                  }}
                >
                  {label}
                </Chip>
              ))}
            </div>
            <div style={{ fontSize: 13, color: settingsHintColor, marginBottom: 14 }}>
              {shelfSharing.mode === "invite-only"
                ? "Invited viewers can unlock your shared shelf with your private code or link."
                : "Only you can view your shelf right now."}
            </div>
            {shelfSharing.mode === "invite-only" && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 6 }}>
                  Invite Code
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <input
                    readOnly
                    value={shareInviteCode || "Generating code..."}
                    style={{ ...inp, fontSize: 12, padding: "10px 12px", flex: 1 }}
                  />
                  <button onClick={copyShelfInviteCode} style={{ padding: "0 12px", borderRadius: 10, border: "1px solid " + T.border, background: T.s2, color: T.sub, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: SANS, flexShrink: 0 }}>
                    Copy
                  </button>
                  <button onClick={regenerateShelfInviteCode} disabled={shareCodeBusy} style={{ padding: "0 12px", borderRadius: 10, border: "none", background: shareCodeBusy ? T.border : T.accent, color: shareCodeBusy ? T.mute : "#fff", fontSize: 12, fontWeight: 700, cursor: shareCodeBusy ? "default" : "pointer", fontFamily: SANS, flexShrink: 0 }}>
                    {shareCodeBusy ? "Refreshing..." : "Regenerate"}
                  </button>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 6 }}>
                  Invite Link
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input
                    readOnly
                    value={shareInviteUrl || "Generating link..."}
                    style={{ ...inp, fontSize: 12, padding: "10px 12px", flex: 1 }}
                  />
                  <button onClick={shareShelfInviteLink} style={{ padding: "0 12px", borderRadius: 10, border: "1px solid " + T.border, background: T.s2, color: T.sub, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: SANS, flexShrink: 0 }}>
                    Share
                  </button>
                  <button onClick={copyShelfInviteLink} style={{ padding: "0 12px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: SANS, flexShrink: 0 }}>
                    Copy
                  </button>
                </div>
                <div style={{ fontSize: 13, color: settingsHintColor, marginBottom: 10 }}>
                  Anyone with this link and code can unlock your shelf. Regenerating creates a new code for future invites.
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button onClick={revokeAllSharedShelfAccess} disabled={shareRevokeBusy} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid " + T.border, background: shareRevokeBusy ? T.border : T.s2, color: shareRevokeBusy ? T.mute : T.sub, fontSize: 12, fontWeight: 700, cursor: shareRevokeBusy ? "default" : "pointer", fontFamily: SANS }}>
                    {shareRevokeBusy ? "Revoking..." : "Revoke All Access"}
                  </button>
                  <button onClick={() => syncSharedShelfMirror()} disabled={shareSyncBusy} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: shareSyncBusy ? T.border : T.accent, color: shareSyncBusy ? T.mute : "#fff", fontSize: 12, fontWeight: 700, cursor: shareSyncBusy ? "default" : "pointer", fontFamily: SANS }}>
                    {shareSyncBusy ? "Refreshing..." : "Refresh Shared Shelf"}
                  </button>
                </div>
              </>
            )}
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8 }}>
                  Shelf bio
                </div>
                <textarea
                  value={shelfSharing.bio || ""}
                  onChange={(e) =>
                    updateShelfSharing(
                      (prev) => ({
                        ...prev,
                        bio: String(e.target.value || "").slice(0, 200),
                      }),
                      false,
                    )
                  }
                  onBlur={() => updateShelfSharing((prev) => ({ ...prev }))}
                  rows={5}
                  maxLength={200}
                  placeholder="A short note about the kind of reader you are..."
                  style={{ ...inp, padding: "10px 12px", fontSize: 13, lineHeight: 1.6, resize: "vertical", minHeight: 112, marginBottom: 8 }}
                />
                <div style={{ fontSize: 12, color: settingsHintColor, marginBottom: 20, textAlign: "right" }}>
                  {String(shelfSharing.bio || "").length}/200
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8 }}>
                  Top genres (up to 5)
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
                  {shareGenreOptions.length === 0 && (
                    <span style={{ fontSize: 13, color: settingsHintColor }}>
                      Add books with genres first.
                    </span>
                  )}
                  {shareGenreOptions.map((genre) => {
                    const on = shelfSharing.topGenres.includes(genre);
                    const disabled = !on && shelfSharing.topGenres.length >= 5;
                    return (
                      <button
                        key={genre}
                        onClick={() =>
                          !disabled &&
                          updateShelfSharing((prev) => ({
                            ...prev,
                            topGenres: on
                              ? prev.topGenres.filter((g) => g !== genre)
                              : [...prev.topGenres, genre],
                          }))
                        }
                        style={{
                          padding: "6px 11px",
                          borderRadius: 20,
                          border: "1px solid " + (on ? T.accent : T.border),
                          background: on ? T.accentBg : T.s2,
                          color: on ? T.accent : disabled ? T.mute : T.sub,
                          fontSize: 12,
                          cursor: disabled ? "default" : "pointer",
                          opacity: disabled ? 0.45 : 1,
                          fontFamily: SANS,
                          fontWeight: on ? 600 : 500,
                        }}
                      >
                        {genre}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8, marginTop: 4 }}>
                  Currently reading highlights (up to 3)
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                  {shareCurrentReadsPickerBooks.length === 0 ? (
                    <div style={{ fontSize: 13, color: settingsHintColor }}>
                      No active reads right now.
                    </div>
                  ) : (
                    shareCurrentReadsPickerBooks.map((book) => {
                      const on = shareCurrentReadSelections.includes(book.uid);
                      const disabled = !on && shareCurrentReadSelections.length >= 3;
                      return (
                        <button
                          key={book.uid}
                          onClick={() =>
                            !disabled &&
                            updateShelfSharing((prev) => ({
                              ...prev,
                              featuredCurrentReads: on
                                ? shareCurrentReadSelections.filter((id) => id !== book.uid)
                                : [...shareCurrentReadSelections, book.uid],
                            }))
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "8px 10px",
                            borderRadius: 10,
                            border: "1px solid " + (on ? T.accent : T.border),
                            background: on ? T.accentBg : T.s2,
                            color: T.text,
                            cursor: disabled ? "default" : "pointer",
                            opacity: disabled ? 0.45 : 1,
                            textAlign: "left",
                          }}
                        >
                          <BookCover book={book} w={28} h={40} r={5} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {book.title}
                            </div>
                            <div style={{ fontSize: 11, color: settingsHintColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {book.authors}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8, marginTop: 4 }}>
                  Icebreaker prompts (up to 5)
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {shelfPromptDrafts.slice(0, 5).map((entry, idx) => (
                    <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <select
                        value={entry.prompt}
                        onChange={(e) =>
                          {
                            const nextPrompt = e.target.value;
                            setShelfPromptDrafts((prev) => {
                              const nextPrompts = normalizeShelfPromptAnswers(prev);
                              nextPrompts[idx] = {
                                ...nextPrompts[idx],
                                prompt: nextPrompt,
                              };
                              return nextPrompts;
                            });
                            updateShelfSharing(
                              (prev) => {
                                const nextPrompts = normalizeShelfPromptAnswers(prev.promptAnswers);
                                nextPrompts[idx] = {
                                  ...nextPrompts[idx],
                                  prompt: nextPrompt,
                                  answer:
                                    shelfPromptDrafts[idx]?.answer ??
                                    nextPrompts[idx]?.answer ??
                                    "",
                                };
                                return { ...prev, promptAnswers: nextPrompts };
                              },
                              false,
                            );
                          }
                        }
                        onBlur={() => updateShelfSharing((prev) => ({ ...prev }))}
                        style={{ ...inp, padding: "9px 10px", fontSize: 12 }}
                      >
                        {SHELF_ICEBREAKER_PROMPTS.map((prompt) => (
                          <option key={prompt} value={prompt}>
                            {prompt}
                          </option>
                        ))}
                      </select>
                      <textarea
                        value={entry.answer}
                        onChange={(e) =>
                          setShelfPromptDrafts((prev) => {
                            const nextPrompts = normalizeShelfPromptAnswers(prev);
                            nextPrompts[idx] = {
                              ...nextPrompts[idx],
                              answer: e.target.value,
                            };
                            return nextPrompts;
                          })
                        }
                        onBlur={() =>
                          updateShelfSharing((prev) => {
                            const nextPrompts = normalizeShelfPromptAnswers(shelfPromptDrafts);
                            return { ...prev, promptAnswers: nextPrompts };
                          })
                        }
                        rows={2}
                        placeholder="Add your answer..."
                        style={{ ...inp, padding: "10px 12px", fontSize: 13, resize: "vertical" }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8, marginTop: 4 }}>
                  Books I always recommend (3 to 6)
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.s2, borderRadius: 10, border: "1px solid " + T.border, padding: "0 12px", marginBottom: 10 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={settingsHintColor} strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    value={shareRecommendedQuery}
                    onChange={(e) => setShareRecommendedQuery(e.target.value)}
                    type="search"
                    placeholder="Search My Books..."
                    style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: T.text, fontSize: 13, fontFamily: SANS, padding: "10px 0" }}
                  />
                  {shareRecommendedQuery && (
                    <button
                      type="button"
                      onClick={() => setShareRecommendedQuery("")}
                      style={{ background: "none", border: "none", color: settingsHintColor, cursor: "pointer", fontSize: 14, padding: 0 }}
                      aria-label="Clear recommendation search"
                    >
                      &#x2715;
                    </button>
                  )}
                </div>
                {!shareRecommendedQuery.trim() && shareRecommendedPickerBooks.length > shareRecommendedVisibleBooks.length && (
                  <div style={{ fontSize: 12, color: settingsHintColor, marginBottom: 10 }}>
                    Showing the first {shareRecommendedVisibleBooks.length}. Search to find more.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                  {shareRecommendedPool.length === 0 ? (
                    <div style={{ fontSize: 13, color: settingsHintColor }}>
                      Mark some books as Read to curate recommendations.
                    </div>
                  ) : shareRecommendedVisibleBooks.length === 0 ? (
                    <div style={{ fontSize: 13, color: settingsHintColor }}>
                      No books match that search yet.
                    </div>
                  ) : (
                    shareRecommendedVisibleBooks.map((book) => {
                      const on = shelfSharing.recommendedBookIds.includes(book.uid);
                      const disabled = !on && shelfSharing.recommendedBookIds.length >= 6;
                      return (
                        <button
                          key={book.uid}
                          onClick={() =>
                            !disabled &&
                            updateShelfSharing((prev) => ({
                              ...prev,
                              recommendedBookIds: on
                                ? prev.recommendedBookIds.filter((id) => id !== book.uid)
                                : [...prev.recommendedBookIds, book.uid],
                            }))
                          }
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, border: "1px solid " + (on ? T.accent : T.border), background: on ? T.accentBg : T.s2, color: T.text, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1, textAlign: "left" }}
                        >
                          <BookCover book={book} w={26} h={38} r={5} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {book.title}
                            </div>
                            <div style={{ fontSize: 11, color: settingsHintColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {book.authors}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: settingsHintColor, marginBottom: 8, marginTop: 4 }}>
                  Viewer options
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: T.s2, borderRadius: 10, border: "1px solid " + T.border, padding: "11px 12px", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                      Allow full shelf browsing
                    </div>
                    <div style={{ fontSize: 12, color: settingsHintColor, marginTop: 2 }}>
                      If off, invited viewers only see the curated private shelf.
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      updateShelfSharing((prev) => ({
                        ...prev,
                        showFullShelf: prev.showFullShelf === false,
                      }))
                    }
                    style={{ width: 48, height: 28, borderRadius: 14, border: "none", background: shelfSharing.showFullShelf === false ? T.border : T.accent, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
                  >
                    <div style={{ position: "absolute", top: 3, left: shelfSharing.showFullShelf === false ? 3 : 22, width: 22, height: 22, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
                  </button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    ["showReadCount", "Show books read count"],
                  ].map(([key, label]) => (
                    <Chip
                      key={key}
                      on={!!shelfSharing[key]}
                      onClick={() =>
                        updateShelfSharing((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: settingsHintColor,
                    marginTop: 14,
                    marginBottom: 8,
                  }}
                >
                  Shelf layout
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  <Chip
                    on={shelfSharing.fullShelfLayout !== "continuous"}
                    onClick={() =>
                      updateShelfSharing((prev) => ({
                        ...prev,
                        fullShelfLayout: "sectioned",
                      }))
                    }
                  >
                    Sectioned by status
                  </Chip>
                  <Chip
                    on={shelfSharing.fullShelfLayout === "continuous"}
                    onClick={() =>
                      updateShelfSharing((prev) => ({
                        ...prev,
                        fullShelfLayout: "continuous",
                      }))
                    }
                  >
                    One continuous shelf
                  </Chip>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: settingsHintColor,
                    marginTop: -2,
                    marginBottom: 12,
                    lineHeight: 1.55,
                  }}
                >
                  {shelfSharing.fullShelfLayout === "continuous"
                    ? "Viewers will see one combined bookshelf with no Reading, Unread, Read, or DNF tabs and no per-book status labels."
                    : "Viewers will see the full shelf grouped into separate Currently Reading, Read, Unread, and DNF sections."}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    ["showReading", "Show currently reading"],
                    ["showRead", "Show read"],
                    ["showUnread", "Show unread"],
                    ["showDnf", `Show ${dnfLabel}`],
                  ].map(([key, label]) => (
                    <Chip
                      key={key}
                      on={!!shelfSharing[key]}
                      onClick={() =>
                        updateShelfSharing((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        }))
                      }
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
          </div>
          </div>
          <BottomNav active="settings" />
        </div>
      )}

      {view === "settings-library" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav}
          title="Library & Data"
          subtitle="Import books and manage your collection."
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Data Import</SettingsLabel>
            <div style={{ fontSize: 13, color: settingsHintColor, marginBottom: 10 }}>
              Import books from Goodreads or other CSV exports, including status,
              ratings, genres, vibes, and tags.
            </div>
            <button
              onClick={() => openImportView("settings-library")}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 10,
                border: "1px solid " + T.border,
                background: T.s2,
                color: T.sub,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: SANS,
              }}
            >
              Import from CSV
            </button>
          </div>
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Book Covers</SettingsLabel>
            <div
              style={{
                fontSize: 13,
                color: settingsHintColor,
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              Replace missing or Google-hosted covers with ISBN-matched cover art.
              Books without a saved ISBN will be matched by title and author first.
            </div>
            <button
              onClick={repairLibraryCovers}
              disabled={coverRepairBusy}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 10,
                border: "none",
                background: coverRepairBusy ? T.border : T.accent,
                color: coverRepairBusy ? T.mute : "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: coverRepairBusy ? "default" : "pointer",
                fontFamily: SANS,
              }}
            >
              {coverRepairBusy
                ? `Repairing covers… (${coverRepairProgress.done}/${coverRepairProgress.total})`
                : "Repair Book Covers"}
            </button>
            {coverRepairReport && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: settingsHintColor,
                  lineHeight: 1.6,
                }}
              >
                Checked {coverRepairReport.checked}; restored {coverRepairReport.coversUpdated}
                {coverRepairReport.isbnsRecovered > 0
                  ? `; recovered ${coverRepairReport.isbnsRecovered} missing ISBN${
                      coverRepairReport.isbnsRecovered === 1 ? "" : "s"
                    }`
                  : ""}
                {coverRepairReport.unresolved > 0
                  ? `; ${coverRepairReport.unresolved} still need a cover`
                  : ""}
                .
              </div>
            )}
          </div>
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Collection</SettingsLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => openResetSheet("nextup")}
                style={{
                  width: "100%",
                  padding: "13px",
                  borderRadius: 10,
                  border: "1px solid " + T.border,
                  background: T.s2,
                  color: T.sub,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Clear Next Up Queue
              </button>
              <button
                onClick={() => openResetSheet("books")}
                style={{
                  width: "100%",
                  padding: "13px",
                  borderRadius: 10,
                  border: "1px solid rgba(196,88,74,0.25)",
                  background: "rgba(196,88,74,0.06)",
                  color: "#C4584A",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Clear My Books
              </button>
              <button
                onClick={() => openResetSheet("all")}
                style={{
                  width: "100%",
                  padding: "13px",
                  borderRadius: 10,
                  border: "1px solid rgba(196,88,74,0.35)",
                  background: "rgba(196,88,74,0.10)",
                  color: "#C4584A",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: SANS,
                }}
              >
                Reset Entire Library
              </button>
            </div>
          </div>
        </SettingsSubpage>
      )}

      {finishPromptOpen && active && (
        <div
          className="sheet-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeFinishPrompt();
          }}
        >
          <div className="sheet" style={{ background: T.surface }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0 4px",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: T.border,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 22px 14px",
                borderBottom: "1px solid " + T.border,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: DISPLAY_SERIF,
                    fontSize: 20,
                    color: T.text,
                  }}
                >
                  <>
                    Yay! 🥳 One more book down.
                    <br />
                    How was it?
                  </>
                </div>
              </div>
              <button
                onClick={() => closeFinishPrompt()}
                disabled={finishPromptBusy}
                style={{
                  background: "none",
                  border: "none",
                  cursor: finishPromptBusy ? "default" : "pointer",
                  color: T.mute,
                  fontSize: 18,
                  padding: 0,
                }}
                aria-label="Close finish sheet"
              >
                ✕
              </button>
            </div>
            <div
              style={{
                padding: "16px 22px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.mute, marginBottom: 10 }}>
                  Rating
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <HalfStars val={finishPromptRating} onChange={setFinishPromptRating} size={18} />
                  <Chip
                    on={finishPromptFav}
                    onClick={() => !finishPromptBusy && setFinishPromptFav((f) => !f)}
                  >
                    {finishPromptFav ? "❤️ Favorited" : "Add to Favorites"}
                  </Chip>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.mute, marginBottom: 10 }}>
                  My Notes...
                </div>
                <textarea
                  key={(active?.uid || "") + "-finish-notes"}
                  defaultValue={finishPromptNotes}
                  onBlur={(e) => setFinishPromptNotes(e.target.value)}
                  placeholder="Add a quick thought while it’s fresh..."
                  rows={4}
                  style={{ ...inp, resize: "vertical", lineHeight: 1.7 }}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => closeFinishPrompt()}
                  disabled={finishPromptBusy}
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid " + T.border,
                    background: "transparent",
                    color: T.sub,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: finishPromptBusy ? "default" : "pointer",
                    fontFamily: SANS,
                  }}
                >
                  Not now
                </button>
                <button
                  onClick={saveFinishPromptDetails}
                  disabled={finishPromptBusy}
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "none",
                    background: finishPromptBusy ? T.border : T.accent,
                    color: finishPromptBusy ? T.mute : "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: finishPromptBusy ? "default" : "pointer",
                    fontFamily: SANS,
                  }}
                >
                  {finishPromptBusy ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {view === "settings-account" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav}
          title="Account"
          subtitle="Security and sign-in details."
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Signed In As</SettingsLabel>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 600, marginBottom: 6 }}>
              {auth.currentUser?.email || "No email available"}
            </div>
            <div style={{ fontSize: 13, color: settingsHintColor }}>
              Password reset is available from the sign-in screen right now.
            </div>
          </div>
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Session</SettingsLabel>
            <button
              onClick={() => setShowSignOut(true)}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 10,
                border: "1px solid rgba(196,88,74,0.3)",
                background: "rgba(196,88,74,0.06)",
                color: "#C4584A",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: SANS,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <span>🚪</span> Sign Out
            </button>
          </div>
        </SettingsSubpage>
      )}

      {view === "settings-admin" && isAdminUser && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav}
          title="Admin Tools"
          subtitle="Apply approved metadata fixes to existing reader libraries."
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Metadata Fixes</SettingsLabel>
            <div style={{ fontSize: 13, color: settingsHintColor, lineHeight: 1.65, marginBottom: 12 }}>
              Future searches already use your approved catalog overrides. This tool
              backfills those same fixes into books people have already saved.
            </div>
            <button
              onClick={applyAdminMetadataFixes}
              disabled={adminFixBusy}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 10,
                border: "none",
                background: adminFixBusy ? T.border : T.accent,
                color: adminFixBusy ? T.mute : "#fff",
                fontSize: 14,
                fontWeight: 800,
                cursor: adminFixBusy ? "default" : "pointer",
                fontFamily: SANS,
              }}
            >
              {adminFixBusy ? "Applying metadata fixes..." : "Apply Metadata Fixes"}
            </button>
            <div style={{ fontSize: 12, color: settingsHintColor, lineHeight: 1.6, marginTop: 10 }}>
              ISBN-based overrides are the most reliable right now. Report-linked fixes
              are also included when the reported book can be found.
            </div>
          </div>

          {adminFixReport && (
            <div
              style={{
                background: T.surface,
                borderRadius: 14,
                padding: 16,
                marginBottom: 12,
                border: "1px solid " + T.border,
              }}
            >
              <SettingsLabel>Last Run</SettingsLabel>
              {"error" in adminFixReport && adminFixReport.error ? (
                <div style={{ fontSize: 13, color: "#C4584A", lineHeight: 1.6 }}>
                  {adminFixReport.message || "Could not apply metadata fixes right now."}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 13, color: T.text }}>
                    Overrides processed: {adminFixReport.overridesProcessed}
                  </div>
                  <div style={{ fontSize: 13, color: T.text }}>
                    Title-only mirrors refreshed: {adminFixReport.mirroredOverrides}
                  </div>
                  <div style={{ fontSize: 13, color: T.text }}>
                    Matching saved books found: {adminFixReport.matchedDocs}
                  </div>
                  <div style={{ fontSize: 13, color: T.text }}>
                    Books updated: {adminFixReport.updatedDocs}
                  </div>
                  <div style={{ fontSize: 13, color: T.text }}>
                    Report-linked targets checked: {adminFixReport.reportTargets}
                  </div>
                  <div style={{ fontSize: 12, color: settingsHintColor }}>
                    Ran at {new Date(adminFixReport.ranAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          )}
        </SettingsSubpage>
      )}

      {view === "settings-privacy" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav}
          title="Privacy"
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontSize: 24,
                color: T.text,
                marginBottom: 6,
              }}
            >
              Lea&apos;s Library Privacy Policy
            </div>
            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
              Last updated: March 2025
            </div>
            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7, marginTop: 12 }}>
              Hey, welcome to Lea&apos;s Library. This is the plain-English version of how your
              information is handled: what we collect, why we collect it, and what we do
              and don&apos;t do with it. If you have questions, you can reach us at{" "}
              <span style={{ color: T.accent, fontWeight: 600 }}>
                hello@leaslibrary.com
              </span>
              .
            </div>
          </div>

          {[
            {
              title: "1. Who We Are",
              body: [
                "Lea's Library is a personal reading tracker app available as a Progressive Web App (PWA), meaning you can use it in your browser or save it to your home screen like a regular app. It's built for readers who want to track what they've read, what they're reading, and what's next on their list.",
                "The app is operated independently in the United States.",
              ],
            },
            {
              title: "2. What Information We Collect",
              body: [
                "When you create an account, we currently collect your email address, password, and display name. We may also save your invite code.",
                "We also store the library content and reading data you add inside the app, including things like books, queue items, notes, ratings, dates, tags, and settings.",
                "We do not collect location data, advertising profiles, date of birth, payment information, or browsing behavior across other websites.",
              ],
              bullets: [
                "Email address",
                "Password",
                "Display name",
                "Invite code",
                "Library and reading data you choose to add",
              ],
            },
            {
              title: "3. Why We Collect It",
              body: [
                "We collect the minimum information needed to make the app work. Your account details let us authenticate you, protect access to your library, and connect your reading data to the correct account.",
                "Your book and reading data is saved so your library, stats, queue, and private shelf settings are there when you return.",
                "We do not sell your data, and we do not share your reading habits with advertisers.",
              ],
            },
            {
              title: "4. Third-Party Services",
              body: [
                "Lea's Library uses trusted service providers to support authentication, secure data storage, hosting, and book metadata lookup.",
                "These providers help the app function, but they are not used for advertising or selling your data. If this changes in a meaningful way, this policy will be updated.",
              ],
            },
            {
              title: "5. How We Keep Your Data Safe",
              body: [
                "Your password is not stored in plain text in the app. Account and library data are stored behind authenticated access controls.",
                "We take reasonable steps to protect your information, but no internet service can guarantee 100% security. If you suspect unauthorized access, contact hello@leaslibrary.com right away.",
              ],
            },
            {
              title: "6. Your Rights & Choices",
              body: [
                "You can update parts of your profile and library inside the app. Password reset is available from the sign-in screen.",
                "If you want your account or data removed, or if you'd like a copy of the data we hold about you, email hello@leaslibrary.com and we'll help promptly.",
              ],
              bullets: [
                "Update your display name and library preferences in the app",
                "Reset your password from the sign-in screen",
                "Request deletion of your account and library data",
                "Request a copy of your data",
              ],
            },
            {
              title: "7. Content You Add to Your Library",
              body: [
                "Lea's Library is a personal reading tracker. The books and notes you add reflect your own choices. The app does not judge, endorse, or curate what you read.",
                "You remain responsible for the content you add to your library.",
              ],
            },
            {
              title: "8. Children's Privacy",
              body: [
                "Lea's Library does not knowingly collect information from children under 13. If you're a parent or guardian and believe a child has created an account, contact hello@leaslibrary.com and we will remove it.",
              ],
            },
            {
              title: "9. Changes to This Policy",
              body: [
                "If this policy changes in a meaningful way, we'll update the text here and adjust the last updated date. If something important changes, we won't try to hide it in fine print.",
              ],
            },
            {
              title: "10. Contact Us",
              body: [
                "Questions, concerns, or just want to say hi? Reach us at hello@leaslibrary.com. We're a small independent app and we genuinely appreciate you taking the time to read this.",
              ],
            },
          ].map((section) => (
            <div
              key={section.title}
              style={{
                background: T.surface,
                borderRadius: 14,
                padding: 16,
                marginBottom: 12,
                border: "1px solid " + T.border,
              }}
            >
              <SettingsLabel>{section.title}</SettingsLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(section.body || []).map((paragraph, idx) => (
                  <div
                    key={idx}
                    style={{ fontSize: 13, color: T.sub, lineHeight: 1.75 }}
                  >
                    {paragraph}
                  </div>
                ))}
                {section.bullets && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {section.bullets.map((bullet) => (
                      <div
                        key={bullet}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          fontSize: 13,
                          color: T.sub,
                          lineHeight: 1.65,
                        }}
                      >
                        <span style={{ color: T.accent, lineHeight: 1.4 }}>•</span>
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </SettingsSubpage>
      )}

      {view === "settings-help" && (
        <SettingsSubpage
          T={T}
          SANS={SANS}
          DISPLAY_SERIF={DISPLAY_SERIF}
          settingsHintColor={settingsHintColor}
          bottomNavPad={bottomNavPad}
          onBack={openSettingsHome}
          BottomNavComponent={BottomNav}
          title="Help & Support"
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 16,
              marginBottom: 12,
              border: "1px solid " + T.border,
            }}
          >
            <SettingsLabel>Support</SettingsLabel>
            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7, marginBottom: 10 }}>
              If you run into a bug, need help with your account, or have a question about the app, reach out anytime.
            </div>
            <div
              style={{
                fontSize: 14,
                color: T.accent,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              hello@leaslibrary.com
            </div>
            <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
              Using the home-screen app on iPhone? If an update doesn&apos;t appear right
              away, fully close and reopen the app. In some cases, you may need to
              re-add it from Safari.
            </div>
          </div>
        </SettingsSubpage>
      )}
    </div>
  );
}

export {
  bookNeedsCoverRepair,
  createdAtOrderValue,
  mergeSelectedBookMetadata,
  normalizeEditableBookTitle,
  normalizeUserCoverUrl,
  preferredBookIsbn,
};

export default function App() {
  return (
    <AuthGate>
      <AppInner />
    </AuthGate>
  );
}
